import { Component, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { takeUntil, debounceTime, switchMap, catchError } from 'rxjs/operators';
import { SegurosDataService, DashboardData, AnalyticsData } from './services/seguros-data.service';
import { SegurosApiService } from './services/seguros-api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserService, InsuranceReportFilters } from 'src/app/services/user/user.service';
import {
  PAYMENT_METHOD_ORDER,
  getPaymentMethodMeta,
  getPaymentMethodLabel,
  requiresManualReport
} from './utils/payment-method-metadata';
import { DateFilter, StatusDistribution, InsurerWidgetSummary, BreakdownDimension } from './models/seguros.interface';
import { DateUtils } from './utils/date-utils';
import { ChartConfig } from './utils/chart-config';
import {
  getPolicyStatusMetadata,
  sortByFunnelStep,
  getBreakdownDimensionTitle,
  getBreakdownLabel,
  getBreakdownKeyTone,
  getTudocFlowStepMetadata,
  TudocFlowStepMetadata
} from './utils/policy-status-metadata';
import { groupPoliciesByInsuredProduct, PolicyGroup } from './utils/group-policies';
import {
  deriveGlobalCobranza,
  deriveCobranzaByInsurer,
  deriveCobranzaFromSummary,
  getCobranzaTier,
  CobranzaStats,
  InsurerCobranzaRow
} from './utils/cobranza-stats';
import { deriveCobranzaHierarchy, CobranzaHierarchy } from './utils/cobranza-hierarchy';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';

// ──────────────────────────────────────────────────────────────────────────
// Visibilidad de TuDoc / Venemergencia en la consola de seguros
// ──────────────────────────────────────────────────────────────────────────
//
// `false` en esta rama: los seeds de TuDoc NO se corren en producción, así que la
// integración no debe dejar rastro en la interfaz — como si nunca se hubiera
// desarrollado. El código queda intacto detrás de este interruptor; el desarrollo
// continúa en la rama `feature/tudoc-integration`, donde la constante vale `true`.
//
// Para reactivar: poner `true`, recompilar y desplegar. Antes hay que correr los
// seeds de TuDoc en ese ambiente (Fase 1 del plan maestro) o la consola mostrará
// una aseguradora vacía.
//
// NO confundir con:
//   · el toggle de negocio D-34 (`Insurance_product.status`), que apaga la venta
//   · el kill switch de infra `VENEMERGENCIAS_TUDOC_ENABLED` del backend
//
// Plan: work-plans/integracion-venemergencias-tudoc/35-OCULTAR-TUDOC-EN-KREDIT-USER.md
export const TUDOC_UI_ENABLED = false;

/** Código de la aseguradora aliada (`Insurance.code`). D-1 del plan maestro. */
export const TUDOC_INSURER_CODE = 'VE';

// Identidades de las secciones colapsables del modal de detalle de póliza.
// Datos Básicos NO está acá porque siempre se muestra (es el resumen al tope).
export type PolicyModalSectionId =
  | 'insured'
  | 'insurance'
  | 'product'
  | 'proposal'
  | 'invoice'
  | 'payments'
  | 'vehicle'
  | 'integration'
  | 'additional'
  | 'tudoc_flow'
  | 'json';

export const POLICY_MODAL_SECTIONS: PolicyModalSectionId[] = [
  'insured', 'payments', 'insurance', 'product', 'proposal', 'invoice',
  'vehicle', 'integration', 'additional', 'tudoc_flow', 'json'
];

@Component({
  selector: 'app-insurance-console',
  templateUrl: './insurance-console.component.html',
  styleUrls: ['./insurance-console.component.css']
})
export class InsuranceConsoleComponent implements OnInit, OnDestroy {
  
  private destroy$ = new Subject<void>();
  
  // Data observables
  dashboardData$ = this.segurosDataService.dashboardData$;
  analyticsData$ = this.segurosDataService.analyticsData$;
  policiesData$ = this.segurosDataService.policiesData$;
  errorPoliciesData$ = this.segurosDataService.errorPoliciesData$;
  paymentErrorPoliciesData$ = this.segurosDataService.paymentErrorPoliciesData$;
  verificationErrorPoliciesData$ = this.segurosDataService.verificationErrorPoliciesData$;
  vehicleUseErrorPoliciesData$ = this.segurosDataService.vehicleUseErrorPoliciesData$;
  defaultAddressPoliciesData$ = this.segurosDataService.defaultAddressPoliciesData$;
  widgetSummaries$ = this.segurosDataService.widgetSummaries$;
  dateFilter$ = this.segurosDataService.dateFilter$;
  loading$ = this.segurosDataService.loading$;

  // Espejo de TUDOC_UI_ENABLED para el template: los templates de Angular no
  // pueden leer constantes de módulo, solo miembros de la clase.
  readonly tudocUiEnabled = TUDOC_UI_ENABLED;

  // Insurer filter
  insurers: any[] = [];
  selectedInsurerCode: string | null = null;
  showInsurerDropdown = false;
  isLoadingInsurers = false;

  // Dashboard rediseñado por aseguradora: estado del widget seleccionado
  // y caché local del Map<code, summary> proveniente del data service.
  selectedDashboardInsurerCode: string | null = null;
  widgetSummaries: Map<string, InsurerWidgetSummary> = new Map();

  // Tab Analítica Avanzada — sección colapsable de las 6 cards demográficas
  // y banner informativo sobre la futura comisión en $. Ambos persisten en
  // localStorage para respetar la preferencia del usuario entre sesiones.
  isDemoSectionCollapsed = true;
  showCommissionInfoBanner = true;
  private readonly DEMO_SECTION_LS_KEY = 'insurance_demographics_expanded_v1';
  private readonly COMMISSION_BANNER_LS_KEY = 'insurance_commission_banner_dismissed_v1';

  // Paleta para las 6 cards demográficas (un color por dimensión). Preserva
  // la identidad visual previa (verde geo, azul edad, púrpura prof, etc.).
  readonly DEMO_COLORS = {
    geography:    ChartConfig.COLORS.success,    // verde
    age:          ChartConfig.COLORS.primary,    // azul
    profession:   '#8B5CF6',                     // violeta
    product:      '#F97316',                     // naranja
    vehicleBrand: ChartConfig.COLORS.danger,     // rojo
    vehicleAge:   '#6366F1'                      // índigo
  };

  // Formatter de label para la card "Por Edad" — agrega sufijo "años" para
  // que el eje X muestre "20-25 años" en vez del rango crudo.
  readonly ageLabelFormatter = (raw: any): string => `${raw} años`;

  // UI State
  activeTab: 'dashboard' | 'analytics' | 'policies' | 'emission_errors' | 'payment_errors' | 'verification_errors' | 'vehicle_use_errors' | 'default_address' | 'config' | 'products' | 'sync_mercantil' | 'sync_caracas' | 'annulled_policies' | 'payment_reports' | 'installments_queue' | 'contacts' | 'tudoc_tracking' | 'tudoc_failed_affiliations' = 'dashboard';
  showDatePicker = false;
  showPoliciesDropdown = false;
  showConfigDropdown = false;
  showErrorsDropdown = false;
  showSyncDropdown = false;
  
  // Current data
  currentDashboardData: DashboardData | null = null;
  currentAnalyticsData: AnalyticsData | null = null;
  currentErrorPoliciesData: any = null;
  currentPaymentErrorsData: any = null;
  currentVerificationErrorsData: any = {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      limit: 20
    }
  };
  currentVehicleUseErrorsData: any = {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      limit: 20
    }
  };
  currentDefaultAddressData: any = {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      limit: 20
    }
  };
  currentPoliciesData: any = null;
  currentAnnulledPoliciesData: any = {
    policies: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 20
    }
  };
  currentDateFilter: DateFilter = {};
  selectedPreset: string = 'thisMonth'; // Default to 'thisMonth'
  
  // Export state
  isExportingPolicies = false;
  isExportingErrors = false;
  isExportingPaymentErrors = false;
  isExportingVerificationErrors = false;
  isExportingVehicleUseErrors = false;
  isExportingDefaultAddress = false;
  isExportingAnnulledPolicies = false;

  // Annulled policies properties
  annulledPoliciesPage = 1;
  annulledPoliciesLimit = 20;
  isLoadingAnnulledPolicies = false;
  annulledPoliciesDateRange: {
    startDate: string | undefined;
    endDate: string | undefined;
  } = {
    startDate: '',
    endDate: ''
  };

  // Date presets
  datePresets = DateUtils.getDatePresets();
  
  // Current time for last update display
  get currentTime(): string {
    return new Date().toISOString();
  }

  // Modal properties
  showPolicyModal = false;
  selectedPolicyDetail: any = null;
  isLoadingPolicyDetail = false;

  // Set con los IDs de las secciones colapsables actualmente expandidas.
  // Default al abrir el modal: vacío (todas colapsadas excepto Datos Básicos
  // que siempre se muestra). Se resetea en viewPolicyDetail y closePolicyModal.
  expandedModalSections = new Set<PolicyModalSectionId>();

  // Search properties
  searchQuery = '';
  searchMode: 'general' | 'advanced' = 'general';
  showAdvancedSearch = false;
  advancedSearchParams = {
    policy_code: '',
    identity: '',
    policy_status: ''
  };
  isSearchActive = false;

  // Agrupación de pólizas duplicadas (mismo asegurado + producto + frecuencia).
  // El Set guarda las keys de grupos actualmente expandidos en el acordeón.
  // Se resetea cuando cambia el listado (paginación, búsqueda, limpieza).
  expandedPolicyGroups = new Set<string>();
  showGroupingCaveat = true;
  private readonly GROUPING_CAVEAT_LS_KEY = 'insurance_grouping_caveat_dismissed_v1';

  // Toggle global: cuando es false (default), los grupos "todo abandonado"
  // (ningún intento llegó a ISSUED+) muestran solo el primary con badge
  // "+N intentos previos" clickeable. Cuando es true, se renderizan como
  // cualquier otro grupo con acordeón normal. Persiste en localStorage.
  showAbandonedAttempts = false;
  private readonly SHOW_ABANDONED_LS_KEY = 'insurance_show_abandoned_attempts_v1';

  // Emission Errors Search properties
  emissionErrorsSearchQuery = '';
  emissionErrorsSearchMode: 'general' | 'advanced' = 'general';
  showEmissionErrorsAdvancedSearch = false;
  emissionErrorsAdvancedSearchParams = {
    policy_code: '',
    identity: '',
    policy_status: ''
  };
  isEmissionErrorsSearchActive = false;

  // Payment Errors Search properties
  paymentErrorsSearchQuery = '';
  paymentErrorsSearchMode: 'general' | 'advanced' = 'general';
  showPaymentErrorsAdvancedSearch = false;
  paymentErrorsAdvancedSearchParams = {
    policy_code: '',
    identity: '',
    policy_status: ''
  };
  isPaymentErrorsSearchActive = false;

  // Verification Errors Search properties
  verificationErrorsSearchQuery = '';
  isVerificationErrorsSearchActive = false;

  // Vehicle Use Errors Search properties
  vehicleUseErrorsSearchQuery = '';
  isVehicleUseErrorsSearchActive = false;
  selectedErrorCode: string = ''; // Para filtrar por VS20 o VS22

  // Default Address Search properties
  defaultAddressSearchQuery = '';
  isDefaultAddressSearchActive = false;

  // Insurers Configuration properties
  insurersConfigData: any = null;
  isLoadingInsurersConfig = false;
  insurersConfigPage = 1;
  insurersConfigLimit = 20;
  insurersConfigSearch = '';
  insurersConfigStatusFilter: boolean | null = null;
  showInsurerEditModal = false;
  selectedInsurerForEdit: any = null;
  isUpdatingInsurer = false;
  editInsurerForm: any = {
    name: '',
    description: '',
    logo: '',
    params: {}
  };
  showParamsJson = false;
  paramsJsonString = '';

  // Products Configuration properties
  productsConfigData: any = null;
  isLoadingProductsConfig = false;
  productsConfigPage = 1;
  productsConfigLimit = 5;
  productsConfigSearch = '';
  productsConfigStatusFilter: boolean | null = null;
  showProductEditModal = false;
  selectedProductForEdit: any = null;
  isUpdatingProduct = false;
  editProductForm: any = {
    name: '',
    description: '',
    icon: '',
    mapping: []
  };
  showMappingJson = false;
  mappingJsonString = '';
  availableInsurers: any[] = [];
  mappingVisualMode = true;
  originalMappingClaves: string[] = [];

  // Sync log properties
  syncLogData: any = null;
  isLoadingSyncLog = false;
  syncLogError: string | null = null;
  syncLogPage = 1;
  syncLogLimit = 10;
  syncLogPagination: any = null;
  syncLogStatusFilter: string = 'all'; // all, updated, error, warning, no_changes
  
  // SC Relations properties
  scRelationData: any = null;
  isLoadingSCRelation = false;
  isConsultingSC = false;
  scRelationError: string | null = null;
  scRelationDate: string = '';
  showAddDepositModal = false;
  showReportResultModal = false;
  reportResult: any = null;
  shouldRefreshOnModalClose = true;
  banks: any[] = [
    { bankCode: '001', name: 'Banesco' },
    { bankCode: '008', name: 'Mercantil' }
  ];
  
  // SC Relation Tabs management
  scActiveTab: 'summary' | 'sc-details' | 'deposits' | 'payments' = 'summary';
  
  // Registered payments data
  scPaymentsData: any = null;
  isLoadingSCPayments = false;
  scPaymentsError: string | null = null;
  
  newDeposit = {
    cdBanco: '',
    nuDeposito: '',
    feDeposito: '',
    mtDeposito: 0,
    cdMoneda: '01',
    tpOperacion: 'D'
  };
  isSavingDeposit = false;
  isReportingDeposits = false;
  showEditDepositModal = false;
  isUpdatingDeposit = false;
  editingDeposit: any = {
    _id: '',
    cdBanco: '',
    nuDeposito: '',
    feDeposito: '',
    mtDeposito: 0,
    cdMoneda: '01',
    tpOperacion: 'D'
  };

  // Error resolution modal properties
  showResolveErrorModal = false;
  resolveErrorType: 'emission' | 'payment' | null = null;
  resolvingPolicyId: string | null = null;
  resolvingPolicyCode: string | null = null;
  resolutionComment = '';
  isResolvingError = false;

  // Payment errors expansion state
  expandedPaymentErrors: { [key: number]: boolean } = {};

  // Utility methods for template
  Math = Math;

  constructor(
    private segurosDataService: SegurosDataService,
    private segurosApiService: SegurosApiService,
    public _auth: AuthService,
    private _user: UserService,
    private offcanvasService: NgbOffcanvas,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadInitialData();
    this.loadInsurers();
    this.restoreGroupingCaveatPreference();
    this.restoreShowAbandonedAttemptsPreference();
    this.restoreAnalyticsPreferences();
    this.restoreBreakdownExpansion();
    this.restoreDemoStatusFilter();
    this.restoreDemoFrequencyFilter();
  }

  // Persistencia del banner caveat de agrupación: si el usuario ya lo cerró
  // en una sesión previa, no lo volvemos a mostrar.
  private restoreGroupingCaveatPreference(): void {
    try {
      this.showGroupingCaveat = localStorage.getItem(this.GROUPING_CAVEAT_LS_KEY) !== '1';
    } catch {
      this.showGroupingCaveat = true;
    }
  }

  private restoreShowAbandonedAttemptsPreference(): void {
    try {
      this.showAbandonedAttempts = localStorage.getItem(this.SHOW_ABANDONED_LS_KEY) === '1';
    } catch {
      this.showAbandonedAttempts = false;
    }
  }

  // Preferencias del tab Analítica Avanzada: sección demográfica colapsada
  // por default; banner de comisión visible hasta que el usuario lo cierre.
  private restoreAnalyticsPreferences(): void {
    try {
      // '1' en LS = expandida; cualquier otro valor (o ausente) = colapsada.
      this.isDemoSectionCollapsed = localStorage.getItem(this.DEMO_SECTION_LS_KEY) !== '1';
      this.showCommissionInfoBanner = localStorage.getItem(this.COMMISSION_BANNER_LS_KEY) !== '1';
    } catch {
      this.isDemoSectionCollapsed = true;
      this.showCommissionInfoBanner = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Canal de recarga del tab de reportes (debounce + switchMap)
    this.prSub?.unsubscribe();
    this.prReload$.complete();
  }

  private initializeSubscriptions(): void {
    // Subscribe to dashboard data changes
    this.dashboardData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('🎯 Component received dashboard data:', data);
        this.currentDashboardData = data;
        console.log('🎯 currentDashboardData updated:', this.currentDashboardData);
        // Invalidar cache de getters costosos (hierarchy, paymentErrorsActive).
        this.invalidateCobranzaCaches();
        // Reconstruir el chart de tendencia cuando llegan nuevos datos.
        this.buildTrendChartConfig();
      });

    // Subscribe to analytics data changes
    this.analyticsData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.currentAnalyticsData = data;
      });

    // Subscribe to error policies data changes
    this.errorPoliciesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('🚨 Component received error policies data:', data);
        this.currentErrorPoliciesData = data;
        
        // Debug pagination data specifically
        if (data?.pagination) {
          console.log('🔍 Error pagination data:', {
            currentPage: data.pagination.currentPage,
            totalPages: data.pagination.totalPages,
            totalItems: data.pagination.totalItems,
            limit: data.pagination.limit
          });
        }
      });

    // Subscribe to payment error policies data changes
    this.paymentErrorPoliciesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('💳 Component received payment error policies data:', data);
        this.currentPaymentErrorsData = data;
        
        // Debug pagination data specifically
        if (data?.pagination) {
          console.log('🔍 Payment error pagination data:', {
            currentPage: data.pagination.currentPage,
            totalPages: data.pagination.totalPages,
            totalItems: data.pagination.totalItems,
            limit: data.pagination.limit
          });
        }
      });

    // Subscribe to verification error policies data changes
    this.verificationErrorPoliciesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('⚠️ Component received verification error policies data:', data);
        this.currentVerificationErrorsData = data;
        
        if (data?.pagination) {
          console.log('🔍 Verification error pagination data:', {
            currentPage: data.pagination.currentPage,
            totalPages: data.pagination.totalPages,
            totalItems: data.pagination.totalItems,
            limit: data.pagination.limit
          });
        }
      });

    // Subscribe to vehicle use error policies data changes
    this.vehicleUseErrorPoliciesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('🚗 Component received vehicle use error policies data:', data);
        this.currentVehicleUseErrorsData = data;
        
        if (data?.pagination) {
          console.log('🔍 Vehicle use error pagination data:', {
            currentPage: data.pagination.currentPage,
            totalPages: data.pagination.totalPages,
            totalItems: data.pagination.totalItems,
            limit: data.pagination.limit
          });
        }
      });

    // Subscribe to default address policies data changes
    this.defaultAddressPoliciesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('📍 Component received default address policies data:', data);
        this.currentDefaultAddressData = data;
        
        if (data?.pagination) {
          console.log('🔍 Default address pagination data:', {
            currentPage: data.pagination.currentPage,
            totalPages: data.pagination.totalPages,
            totalItems: data.pagination.totalItems,
            limit: data.pagination.limit
          });
        }
      });

    // Subscribe to policies data changes
    this.policiesData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('📋 Component received policies data:', data);
        this.currentPoliciesData = data;
      });

    // Subscribe to date filter changes
    this.dateFilter$
      .pipe(takeUntil(this.destroy$))
      .subscribe(filter => {
        this.currentDateFilter = filter;
        // Si estamos en el tab dashboard, refrescar widgets + panel detalle.
        // El servicio ya auto-recarga el dashboard global, pero necesitamos
        // refrescar los widget summaries y el panel scoped a la aseguradora
        // seleccionada.
        if (this.activeTab === 'dashboard' && this.insurers?.length > 0) {
          const codes = this.visibleInsurers.map(i => i.code);
          this.segurosDataService.loadAllWidgetSummaries(codes);
          if (this.selectedDashboardInsurerCode) {
            this.segurosDataService.loadDashboardData(filter, this.selectedDashboardInsurerCode);
          }
        }
      });

    // Subscribe to widget summaries (Map<code, InsurerWidgetSummary>).
    this.widgetSummaries$
      .pipe(takeUntil(this.destroy$))
      .subscribe(summaries => {
        this.widgetSummaries = summaries;
        // Invalidar cache de getters costosos (cobranza global, by insurer).
        this.invalidateCobranzaCaches();
      });
  }

  private loadInitialData(): void {
    console.log('🚀 Iniciando carga de datos...');
    
    // Set default filter to this month (this automatically triggers loadDashboardData)
    const thisMonthPreset = this.datePresets.thisMonth;
    this.applyDatePreset(thisMonthPreset, 'thisMonth');
    
    // Load initial policies and error data
    this.segurosDataService.loadPoliciesData({ page: 1, limit: 20 });
    this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });
    this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
    this.segurosDataService.loadVerificationErrorPoliciesData({ page: 1, limit: 20 });
    this.segurosDataService.loadVehicleUseErrorPoliciesData({ page: 1, limit: 20 });
    this.segurosDataService.loadDefaultAddressPoliciesData({ page: 1, limit: 20 });
    
    // Load analytics data so it's ready when user switches to that tab
    this.segurosDataService.loadAnalyticsData();
    
    // Pre-load annulled policies with global date filter
    this.loadAnnulledPoliciesWithGlobalFilter();
    
    console.log('✅ Comandos de carga enviados');
  }

  private loadInsurers(): void {
    this.isLoadingInsurers = true;
    this.segurosDataService.getInsurers().subscribe({
      next: (insurers) => {
        this.insurers = this.filterHiddenInsurers(insurers || []);
        this.sanitizeSelectedInsurerCodes();
        this.isLoadingInsurers = false;
        // Disparar carga del dashboard rediseñado: widgets en paralelo +
        // panel detalle inicial con la primera aseguradora.
        this.initializeDashboardForInsurers();
      },
      error: (error) => {
        console.error('❌ Error al cargar aseguradoras:', error);
        this.isLoadingInsurers = false;
      }
    });
  }

  /** Plan 35 — punto único donde Venemergencia/TuDoc entra a la consola.
   *  `this.insurers` se asigna sólo acá, y de esta lista cuelgan el selector
   *  lateral, el grid de widgets del dashboard, las llamadas de widget-summary,
   *  la tabla de cobranza por aseguradora y `getInsurerName()`. Filtrar acá las
   *  apaga todas de una vez. Con el flag encendido devuelve la lista intacta.
   *
   *  En producción es un no-op: los seeds de TuDoc no se corren, así que el
   *  backend nunca devuelve VE. Queda como defensa por si un ambiente
   *  restaurado o un seed accidental la trajera. */
  private filterHiddenInsurers(insurers: any[]): any[] {
    if (TUDOC_UI_ENABLED) return insurers;
    return insurers.filter(i => i?.code !== TUDOC_INSURER_CODE);
  }

  /** Plan 35 — si quedó una aseguradora oculta seleccionada (estado previo del
   *  componente), el getter `visibleInsurers` devolvería [] y el dashboard
   *  saldría en blanco. Se resetea a null para volver a "todas". */
  private sanitizeSelectedInsurerCodes(): void {
    const isVisible = (code: string | null) =>
      !code || this.insurers.some(i => i?.code === code);

    if (!isVisible(this.selectedInsurerCode)) {
      this.selectedInsurerCode = null;
    }
    if (!isVisible(this.selectedDashboardInsurerCode)) {
      this.selectedDashboardInsurerCode = null;
    }
  }

  // Bootstrap del dashboard: carga sólo los widget summaries en paralelo.
  // Ningún widget se selecciona por defecto — el usuario debe hacer click
  // sobre uno para que se cargue el panel de detalle. Decisión UX explícita
  // para evitar mostrar datos de una aseguradora arbitraria.
  private initializeDashboardForInsurers(): void {
    if (!this.insurers?.length) return;
    const visibleCodes = this.visibleInsurers.map(i => i.code);
    this.segurosDataService.loadAllWidgetSummaries(visibleCodes);
  }

  loadSyncLog(page?: number): void {
    if (page) {
      this.syncLogPage = page;
    }
    
    this.isLoadingSyncLog = true;
    this.syncLogError = null;
    
    // Cargar todos los datos sin paginación del backend
    this.segurosDataService.getLatestSyncLog({ 
      page: 1, 
      limit: 5000 // Cargar hasta 5000 registros para sincronizaciones grandes
    }).subscribe({
      next: (response) => {
        console.log('📊 Log de sincronización obtenido:', response);
        console.log('📊 Response.success:', response.success);
        console.log('📊 Response.data:', response.data);
        console.log('📊 Response.data.details length:', response.data?.details?.length);
        
        if (response.success) {
          this.syncLogData = response.data;
          console.log('✅ syncLogData asignado:', this.syncLogData);
          this.applySyncLogPagination();
          console.log('✅ Paginación aplicada. Pagination:', this.syncLogPagination);
        } else {
          console.warn('⚠️ Response.success es false');
          this.syncLogError = response.message || 'No se encontraron logs de sincronización';
        }
        this.isLoadingSyncLog = false;
      },
      error: (error) => {
        console.error('❌ Error al cargar log de sincronización:', error);
        this.syncLogError = error.error?.message || error.message || 'Error al cargar el log de sincronización';
        this.isLoadingSyncLog = false;
      }
    });
  }

  applySyncLogPagination(): void {
    if (!this.syncLogData) return;
    
    // Aplicar filtro primero
    const allDetails = this.syncLogData.details || [];
    let filteredDetails = allDetails;
    
    if (this.syncLogStatusFilter !== 'all') {
      filteredDetails = allDetails.filter((detail: any) => {
        const status = this.getDetailStatus(detail);
        return status === this.syncLogStatusFilter;
      });
    }
    
    // Calcular paginación sobre datos filtrados
    const totalItems = filteredDetails.length;
    const totalPages = Math.ceil(totalItems / this.syncLogLimit);
    const currentPage = Math.max(1, Math.min(this.syncLogPage, totalPages || 1));
    const startIndex = (currentPage - 1) * this.syncLogLimit;
    const endIndex = startIndex + this.syncLogLimit;
    
    // Guardar todos los detalles originales y crear paginación
    this.syncLogData.allDetails = allDetails;
    this.syncLogData.filteredDetails = filteredDetails;
    this.syncLogData.paginatedDetails = filteredDetails.slice(startIndex, endIndex);
    
    this.syncLogPagination = {
      currentPage: currentPage,
      totalPages: totalPages,
      totalItems: totalItems,
      itemsPerPage: this.syncLogLimit,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1
    };
  }

  // Sync log pagination methods
  goToSyncLogPage(page: number): void {
    if (page >= 1 && page <= (this.syncLogPagination?.totalPages || 1)) {
      this.syncLogPage = page;
      this.applySyncLogPagination();
    }
  }

  nextSyncLogPage(): void {
    if (this.syncLogPagination?.hasNextPage) {
      this.syncLogPage++;
      this.applySyncLogPagination();
    }
  }

  previousSyncLogPage(): void {
    if (this.syncLogPagination?.hasPreviousPage) {
      this.syncLogPage--;
      this.applySyncLogPagination();
    }
  }

  getSyncLogPageNumbers(): number[] {
    if (!this.syncLogPagination) return [];
    
    const totalPages = this.syncLogPagination.totalPages;
    const currentPage = this.syncLogPagination.currentPage;
    const pages: number[] = [];
    
    // Show max 5 page numbers
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    
    // Adjust if we're near the start or end
    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages);
    }
    if (currentPage >= totalPages - 2) {
      startPage = Math.max(1, totalPages - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // Sync log filter methods
  setSyncLogStatusFilter(status: string): void {
    this.syncLogStatusFilter = status;
    this.syncLogPage = 1; // Reset to first page when filtering
    this.applySyncLogPagination();
  }

  getDetailStatus(detail: any): string {
    // Replicar exactamente la misma lógica que el HTML para consistencia
    
    // 1. Actualizada: si tiene installments_updated > 0
    if (detail.installments_updated > 0) {
      return 'updated';
    }
    
    // 2. Error: si installments_updated === 0 Y tiene installments Y hay error
    if (detail.installments_updated === 0 && detail.installments?.length > 0) {
      if (this.hasErrorInDetail(detail)) {
        return 'error';
      }
    }
    
    // 3. Con Alertas: si installments_updated === 0 Y tiene installments Y NO hay error
    if (detail.installments_updated === 0 && detail.installments?.length > 0) {
      if (!this.hasErrorInDetail(detail)) {
        return 'warning';
      }
    }
    
    // 4. Sin Cambios: si installments_updated === 0 Y NO tiene installments (o está vacío)
    if (detail.installments_updated === 0 && (!detail.installments || detail.installments.length === 0)) {
      return 'no_changes';
    }
    
    // Default (no debería llegar aquí)
    return 'no_changes';
  }

  hasErrorInDetail(detail: any): boolean {
    if (!detail.installments || detail.installments.length === 0) {
      return false;
    }
    return detail.installments.some((inst: any) => inst.status === 'error');
  }

  getFilteredSyncLogDetails(): any[] {
    if (!this.syncLogData) return [];
    
    // Retornar los datos ya paginados y filtrados
    return this.syncLogData.paginatedDetails || [];
  }

  exportSyncLogToExcel(): void {
    if (!this.syncLogData) return;

    // Obtener todos los detalles filtrados (sin paginación)
    const allDetails = this.syncLogData.allDetails || [];
    let dataToExport = allDetails;

    // Aplicar filtro si está activo
    if (this.syncLogStatusFilter !== 'all') {
      dataToExport = allDetails.filter((detail: any) => {
        const status = this.getDetailStatus(detail);
        return status === this.syncLogStatusFilter;
      });
    }

    // Preparar datos para Excel (CSV con tabuladores)
    const headers = [
      'Compra',
      'Código Compra',
      'Póliza',
      'Código Póliza',
      'Estado Póliza',
      'Shopcart ID',
      'Cuotas Actualizadas',
      'Cuota #',
      'Código Cuota',
      'Recibo',
      'Estado Recibo',
      'Estado Cuota',
      'Pagada',
      'Fecha Programada',
      'Tipo',
      'Mensaje'
    ];

    let csvContent = headers.join('\t') + '\n';

    dataToExport.forEach((detail: any) => {
      const baseData = [
        detail.invoice_identifier || '',
        detail.invoice_code || '',
        detail.policy_number || '',
        detail.policy_code || '',
        detail.policy_status || '',
        detail.shopcart_id || '',
        detail.installments_updated || 0
      ];

      // Si tiene cuotas con detalles, agregar una fila por cada cuota
      if (detail.installments && detail.installments.length > 0) {
        detail.installments.forEach((inst: any) => {
          const row = [
            ...baseData,
            inst.installment_order || '',
            inst.installment_code || '',
            inst.receipt_number || '',
            inst.receipt_status || '',
            inst.quote_status || '',
            inst.is_paid ? 'Sí' : 'No',
            inst.scheduled_date ? new Date(inst.scheduled_date).toLocaleDateString() : '',
            inst.status || '',
            inst.message || ''
          ];
          csvContent += row.join('\t') + '\n';
        });
      } else {
        // Si no tiene cuotas, agregar solo la fila base
        const row = [
          ...baseData,
          '', '', '', '', '', '', '', '', 'Sin novedades'
        ];
        csvContent += row.join('\t') + '\n';
      }
    });

    // Crear y descargar el archivo
    const blob = new Blob([csvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filterSuffix = this.syncLogStatusFilter !== 'all' ? `_${this.syncLogStatusFilter}` : '';
    link.download = `sincronizacion_seguros${filterSuffix}_${timestamp}.xls`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // SC Relations methods
  loadSCRelationForYesterday(): void {
    // Establecer fecha del día anterior por defecto
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.scRelationDate = yesterday.toISOString().split('T')[0];
    this.loadSCRelation();
  }

  loadSCRelation(): void {
    if (!this.scRelationDate) {
      this.scRelationError = 'Debe seleccionar una fecha';
      return;
    }

    this.isLoadingSCRelation = true;
    this.scRelationError = null;

    this.segurosApiService.getSCRelationByDate(this.scRelationDate).subscribe({
      next: (response) => {
        console.log('📊 Relación SC obtenida:', response);
        if (response.success) {
          this.scRelationData = response.data;
          // Cargar también los pagos registrados de nuestro lado
          this.loadSCPayments();
        } else {
          this.scRelationError = response.message || 'No se encontró relación para la fecha indicada';
          this.scRelationData = null;
        }
        this.isLoadingSCRelation = false;
      },
      error: (error) => {
        console.error('❌ Error al cargar relación SC:', error);
        this.scRelationError = error.error?.message || 'Error al cargar la relación';
        this.scRelationData = null;
        this.isLoadingSCRelation = false;
      }
    });
  }

  fetchSCRelationFromAPI(): void {
    if (!this.scRelationDate) {
      alert('Debe seleccionar una fecha');
      return;
    }

    this.isConsultingSC = true;

    // Convertir fecha de YYYY-MM-DD a DD/MM/YYYY
    const [year, month, day] = this.scRelationDate.split('-');
    const fePagoFormatted = `${day}/${month}/${year}`;

    this.segurosApiService.fetchSCRelationFromAPI(fePagoFormatted).subscribe({
      next: (response) => {
        console.log('✅ Relación consultada desde API SC:', response);
        if (response.success) {
          alert(`Relación consultada y guardada exitosamente. ${response.data.total_records} registros encontrados.`);
          // Recargar la relación ahora que está guardada
          this.loadSCRelation();
        } else {
          alert(response.message || 'No se encontraron registros en Seguros Caracas para la fecha indicada');
        }
        this.isConsultingSC = false;
      },
      error: (error) => {
        console.error('❌ Error al consultar relación SC:', error);
        
        // Construir mensaje de error detallado
        let errorMessage = 'Error al consultar relación en Seguros Caracas\n\n';
        
        if (error.error) {
          if (error.error.message) {
            errorMessage += `Mensaje: ${error.error.message}\n`;
          }
          if (error.error.msg) {
            errorMessage += `Detalles: ${error.error.msg}\n`;
          }
          if (error.error.error) {
            errorMessage += `Error: ${JSON.stringify(error.error.error, null, 2)}\n`;
          }
        }
        
        if (error.status) {
          errorMessage += `\nCódigo HTTP: ${error.status}`;
        }
        
        if (error.statusText) {
          errorMessage += `\nStatus: ${error.statusText}`;
        }
        
        alert(errorMessage);
        this.isConsultingSC = false;
      }
    });
  }

  loadSCPayments(): void {
    if (!this.scRelationDate) {
      return;
    }

    this.isLoadingSCPayments = true;
    this.scPaymentsError = null;

    this.segurosApiService.getSCPaymentsByDate(this.scRelationDate).subscribe({
      next: (response: any) => {
        console.log('💰 Pagos registrados obtenidos:', response);
        if (response.success) {
          this.scPaymentsData = response.data;
        } else {
          this.scPaymentsError = response.error || 'No se encontraron pagos';
          this.scPaymentsData = null;
        }
        this.isLoadingSCPayments = false;
      },
      error: (error: any) => {
        console.error('❌ Error al cargar pagos registrados:', error);
        this.scPaymentsError = error.error?.message || 'Error al cargar los pagos';
        this.scPaymentsData = null;
        this.isLoadingSCPayments = false;
      }
    });
  }

  setSCActiveTab(tab: 'summary' | 'sc-details' | 'deposits' | 'payments'): void {
    this.scActiveTab = tab;
  }

  exportSCRelationToExcel(): void {
    if (!this.scRelationData || !this.scPaymentsData) {
      alert('No hay datos para exportar');
      return;
    }

    // Crear libro de Excel
    const workbook = XLSX.utils.book_new();

    // ===== HOJA 1: PAGOS SC =====
    const scPaymentsSheet: any[] = [];
    
    // Encabezados para Pagos SC (igual formato que Pagos Creditotal)
    scPaymentsSheet.push([
      'Póliza',
      'Recibo',
      'Monto Recibo (USD)',
      'Monto Pago (Bs)',
      'Tasa'
    ]);

    // Datos de Pagos SC
    this.scRelationData.details.forEach((detail: any) => {
      scPaymentsSheet.push([
        parseFloat(detail.nuPoliza) || detail.nuPoliza, // Convertir a número si es posible
        parseFloat(detail.nuRecibo) || detail.nuRecibo, // Convertir a número si es posible
        parseFloat((parseFloat(detail.mtRecibo) || 0).toFixed(2)),
        parseFloat((parseFloat(detail.mtPago) || 0).toFixed(2)),
        parseFloat((parseFloat(detail.taCambio) || 0).toFixed(2))
      ]);
    });

    // Fila vacía antes de totales
    scPaymentsSheet.push([]);

    // Totales de Pagos SC
    scPaymentsSheet.push([
      '',
      'Totales:',
      parseFloat((this.scRelationData.header?.total_mtRecibo_usd || 0).toFixed(2)),
      parseFloat((this.scRelationData.header?.total_mtPago_ves || 0).toFixed(2)),
      ''
    ]);

    // Crear hoja de Pagos SC
    const ws1 = XLSX.utils.aoa_to_sheet(scPaymentsSheet);
    
    // Ajustar anchos de columna
    ws1['!cols'] = [
      { wch: 15 }, // Póliza
      { wch: 15 }, // Recibo
      { wch: 20 }, // Monto Recibo (USD)
      { wch: 20 }, // Monto Pago (Bs)
      { wch: 10 }  // Tasa
    ];

    XLSX.utils.book_append_sheet(workbook, ws1, 'Pagos SC');

    // ===== HOJA 2: PAGOS CREDITOTAL =====
    const creditotalPaymentsSheet: any[] = [];
    
    // Encabezados para Pagos Creditotal
    creditotalPaymentsSheet.push([
      'Póliza',
      'Recibo',
      'Monto Recibo (USD)',
      'Monto Pago (Bs)',
      'Tasa'
    ]);

    // Datos de Pagos Creditotal
    if (this.scPaymentsData.payments && this.scPaymentsData.payments.length > 0) {
      this.scPaymentsData.payments.forEach((payment: any) => {
        creditotalPaymentsSheet.push([
          parseFloat(payment.policy) || payment.policy, // Convertir a número si es posible
          parseFloat(payment.receipt) || payment.receipt, // Convertir a número si es posible
          parseFloat((parseFloat(payment.receiptAmount) || 0).toFixed(2)),
          parseFloat((parseFloat(payment.paymentAmount) || 0).toFixed(2)),
          parseFloat((parseFloat(payment.exchangeRate) || 0).toFixed(2))
        ]);
      });
    }

    // Fila vacía antes de totales
    creditotalPaymentsSheet.push([]);

    // Totales de Pagos Creditotal
    creditotalPaymentsSheet.push([
      '',
      'Totales:',
      parseFloat((this.scPaymentsData.total_usd || 0).toFixed(2)),
      parseFloat((this.scPaymentsData.total_ves || 0).toFixed(2)),
      ''
    ]);

    // Crear hoja de Pagos Creditotal
    const ws2 = XLSX.utils.aoa_to_sheet(creditotalPaymentsSheet);
    
    // Ajustar anchos de columna
    ws2['!cols'] = [
      { wch: 15 }, // Póliza
      { wch: 15 }, // Recibo
      { wch: 20 }, // Monto Recibo (USD)
      { wch: 20 }, // Monto Pago (Bs)
      { wch: 10 }  // Tasa
    ];

    XLSX.utils.book_append_sheet(workbook, ws2, 'Pagos Creditotal');

    // Generar nombre del archivo con el número de relación (que ya contiene la fecha)
    const fileName = `Relacion_SC_${this.scRelationData.header?.nuRelacion}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(workbook, fileName);

    console.log('✅ Archivo Excel exportado:', fileName);
  }

  loadBanks(): void {
    console.log('🔄 Iniciando carga de bancos...');
    this.segurosApiService.getBanks().subscribe({
      next: (response) => {
        console.log('✅ Bancos cargados - Respuesta completa:', response);
        console.log('✅ Bancos array:', response.res);
        this.banks = response.res || [];
        console.log('✅ Total de bancos cargados:', this.banks.length);
      },
      error: (error) => {
        console.error('❌ Error al cargar bancos:', error);
        console.error('❌ Error details:', error.error);
      }
    });
  }

  openAddDepositModal(): void {
    this.showAddDepositModal = true;
    this.newDeposit = {
      cdBanco: '',
      nuDeposito: '',
      feDeposito: new Date().toISOString().split('T')[0],
      mtDeposito: 0,
      cdMoneda: '01',
      tpOperacion: 'D'
    };
  }

  closeAddDepositModal(): void {
    this.showAddDepositModal = false;
  }

  saveDeposit(): void {
    if (!this.scRelationData?.header?.nuRelacion) {
      alert('No hay relación cargada');
      return;
    }

    if (!this.newDeposit.cdBanco || !this.newDeposit.nuDeposito || !this.newDeposit.mtDeposito) {
      alert('Debe completar todos los campos obligatorios');
      return;
    }

    this.isSavingDeposit = true;

    const depositData = {
      nuRelacion: this.scRelationData.header.nuRelacion,
      deposits: [{
        cdBanco: this.newDeposit.cdBanco,
        nuDeposito: this.newDeposit.nuDeposito,
        feDeposito: this.newDeposit.feDeposito,
        mtDeposito: this.newDeposit.mtDeposito,
        cdMoneda: this.newDeposit.cdMoneda,
        tpOperacion: this.newDeposit.tpOperacion
      }]
    };

    this.segurosApiService.saveSCDeposits(depositData).subscribe({
      next: (response) => {
        console.log('✅ Depósito guardado:', response);
        alert('Depósito guardado exitosamente');
        this.closeAddDepositModal();
        this.loadSCRelation(); // Recargar para mostrar el nuevo depósito
        this.isSavingDeposit = false;
      },
      error: (error) => {
        console.error('❌ Error al guardar depósito:', error);
        alert(error.error?.msg || 'Error al guardar el depósito');
        this.isSavingDeposit = false;
      }
    });
  }


  // Policy actions
  // Acepta objeto de póliza (idealmente) o string por retro-compat con llamadas
  // viejas. Ramifica al endpoint correcto según insurance.code:
  //   SM → resume_subscribe_sm    (retry existente)
  //   SC → retry_emission_sc      (Plan 7 SC/RCV — endpoint HTTP viene del Plan 8)
  // Sin aseguradora reconocida cae por default a SM (comportamiento previo).
  retryPolicy(policyOrCode: any): void {
    // Extraer code + insuranceCode aceptando ambas formas de invocación.
    let policyCode: string;
    let insuranceCode: string | undefined;

    if (typeof policyOrCode === 'string') {
      policyCode = policyOrCode;
      insuranceCode = undefined;
    } else if (policyOrCode && typeof policyOrCode === 'object') {
      policyCode = policyOrCode.params?.code || policyOrCode.code || policyOrCode._id;
      insuranceCode = policyOrCode.params?.insurance?.code
        || policyOrCode.insurance?.code
        || policyOrCode.insurance_code;
    } else {
      console.error('❌ retryPolicy: argumento inválido', policyOrCode);
      alert('Error: No se encontró el código de la póliza');
      return;
    }

    console.log('🔄 Reintentando póliza:', { policyCode, insuranceCode });

    if (!policyCode) {
      console.error('❌ No se encontró código de póliza para reintentar');
      alert('Error: No se encontró el código de la póliza');
      return;
    }

    this.segurosDataService.retryPolicy(policyCode, insuranceCode).subscribe({
      next: (response: any) => {
        console.log('✅ Póliza reintentada exitosamente:', response);
        alert('✅ Póliza reintentada exitosamente');

        // Recargar los datos de errores para reflejar el cambio
        this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });

        // También recargar el dashboard (respetando la aseguradora seleccionada)
        this.refreshDashboard();
      },
      error: (error: any) => {
        console.error('❌ Error al reintentar póliza:', error);
        const errorMessage = error?.error?.message || error?.message || 'Error desconocido';
        alert(`❌ Error al reintentar póliza: ${errorMessage}`);
      }
    });
  }

  viewPolicyDetail(policyId: string): void {
    console.log('👁️ Viendo detalle de póliza:', policyId);

    this.isLoadingPolicyDetail = true;
    this.showPolicyModal = true;
    this.selectedPolicyDetail = null;
    // Cada apertura del modal arranca con todas las secciones colapsadas.
    this.expandedModalSections.clear();
    
    this.segurosDataService.getPolicyDetail(policyId).subscribe({
      next: (policyDetail: any) => {
        console.log('📄 Detalle de póliza obtenido:', policyDetail);
        
        // Verificar específicamente el array de pagos
        console.log('💳 Verificando pagos en la póliza:');
        console.log('  - params.payments:', policyDetail?.params?.payments);
        console.log('  - payments (directo):', policyDetail?.payments);
        console.log('  - Cantidad de pagos:', this.getPayments(policyDetail).length);
        
        this.selectedPolicyDetail = policyDetail;
        this.isLoadingPolicyDetail = false;
      },
      error: (error: any) => {
        console.error('❌ Error al obtener detalle de póliza:', error);
        this.isLoadingPolicyDetail = false;
        this.showPolicyModal = false;
        alert('Error al obtener el detalle de la póliza');
      }
    });
  }

  closePolicyModal(): void {
    this.showPolicyModal = false;
    this.selectedPolicyDetail = null;
    this.isLoadingPolicyDetail = false;
    // Vaciar el Set para que la próxima apertura del modal arranque fresca.
    this.expandedModalSections.clear();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Secciones colapsables del modal de detalle de póliza
  // ─────────────────────────────────────────────────────────────────────────

  togglePolicySection(id: PolicyModalSectionId): void {
    if (this.expandedModalSections.has(id)) {
      this.expandedModalSections.delete(id);
    } else {
      this.expandedModalSections.add(id);
    }
  }

  isPolicySectionExpanded(id: PolicyModalSectionId): boolean {
    return this.expandedModalSections.has(id);
  }

  expandAllPolicySections(): void {
    POLICY_MODAL_SECTIONS.forEach(id => this.expandedModalSections.add(id));
  }

  collapseAllPolicySections(): void {
    this.expandedModalSections.clear();
  }

  getExpandedPolicySectionsCount(): number {
    return this.expandedModalSections.size;
  }

  // Cuenta de secciones que están efectivamente disponibles para la póliza
  // actual (descuenta las que tienen *ngIf que la oculta porque no hay data).
  getAvailablePolicySectionsCount(): number {
    const p = this.selectedPolicyDetail;
    if (!p) return 0;
    let count = 0;
    // Asegurado, Aseguradora, Producto/Plan: rara vez vacíos cuando hay póliza,
    // pero igual respetamos las condiciones del template.
    if (p.params?.insured || p.insured) count++;
    if (p.params?.insurance || p.insurance) count++;
    if (p.params?.plan || p.plan) count++;
    if (this.hasProposal(p)) count++;
    if (this.hasInvoice(p)) count++;
    count++; // payments (siempre se renderiza, aunque la lista esté vacía)
    if (this.isRCVProduct(p) && (p.params?.vehicle || p.vehicle)) count++;
    if (p.params?.integration_data) count++;
    count++; // additional (siempre se renderiza)
    count++; // json (siempre se renderiza)
    return count;
  }

  copyJsonToClipboard(): void {
    if (!this.selectedPolicyDetail) return;
    
    const jsonString = JSON.stringify(this.selectedPolicyDetail, null, 2);
    
    if (navigator.clipboard && window.isSecureContext) {
      // Use modern clipboard API
      navigator.clipboard.writeText(jsonString).then(() => {
        this.showCopySuccessMessage();
      }).catch(err => {
        console.error('Error copying to clipboard:', err);
        this.fallbackCopyToClipboard(jsonString);
      });
    } else {
      // Fallback for older browsers or non-secure contexts
      this.fallbackCopyToClipboard(jsonString);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.showCopySuccessMessage();
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      alert('Error al copiar al portapapeles. Por favor, selecciona y copia manualmente.');
    }
    
    document.body.removeChild(textArea);
  }

  private showCopySuccessMessage(): void {
    // You could implement a toast notification here
    // For now, we'll use a simple alert
    const alertDiv = document.createElement('div');
    alertDiv.innerHTML = `
      <div class="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300">
        <i class="fas fa-check mr-2"></i>
        JSON copiado al portapapeles
      </div>
    `;
    document.body.appendChild(alertDiv);
    
    setTimeout(() => {
      if (alertDiv && alertDiv.parentNode) {
        alertDiv.style.opacity = '0';
        setTimeout(() => {
          if (alertDiv.parentNode) {
            document.body.removeChild(alertDiv);
          }
        }, 300);
      }
    }, 2000);
  }

  // Tab navigation
  /** Handler para los clicks de los buckets del componente
   *  <app-sales-collection-summary>. Abre el sub-tab Contactos con los
   *  filtros pre-poblados que correspondan al bucket. El bucket "Cobré con
   *  error notif." sigue yendo al tab payment_errors porque ahí hay la acción
   *  específica de reintentar la notificación a la aseguradora. */
  onSalesBucketClick(bucket: 'sold' | 'collected' | 'collected_error' | 'pending'): void {
    console.log('💳 Sales bucket click:', bucket);
    switch (bucket) {
      case 'collected_error':
        // No abrimos contactos — el tab payment_errors ya tiene la lista y
        // los botones "Reintentar notificación" / "Marcar resuelto".
        this.setActiveTab('payment_errors');
        return;
      case 'sold':
        this.openContactsForStatus(
          ['ISSUED', 'ISSUED_PAID', 'PAYMENT_RECORDED', 'COMPLETED'],
          { title: 'Vendí · pólizas emitidas' }
        );
        return;
      case 'collected':
        // ISSUED_PAID incluido: el flujo nuevo (Plan 3 SC/RCV) emite con pago.
        this.openContactsForStatus(
          ['PAYMENT_RECORDED', 'ISSUED_PAID', 'COMPLETED'],
          { title: 'Cobré · con pago saneado', notification_status: 'notified_ok' }
        );
        return;
      case 'pending':
        this.openContactsForStatus(
          'ISSUED',
          { title: 'Falta cobrar · sin pago aún' }
        );
        return;
    }
  }

  /** Handler para los clicks de los buckets principales de la card "Distribución
   *  por Estado". Cada estado abre el sub-tab Contactos con su filtro. */
  onStatusBucketClick(status: string): void {
    const meta = getPolicyStatusMetadata(status);
    this.openContactsForStatus(status, { title: meta.label || status });
  }

  /** Handler para los sub-buckets del breakdown (notification_error,
   *  fully_paid_stuck, by_frequency.MENSUAL, etc.). */
  onBreakdownItemClick(
    parentStatus: string,
    dimension: BreakdownDimension,
    key: string,
    label?: string
  ): void {
    const parentMeta = getPolicyStatusMetadata(parentStatus);
    const parentLabel = parentMeta.label || parentStatus;
    const opts: any = { title: `${parentLabel} · ${label || key}` };
    if (dimension === 'by_plan_kind') {
      // partial_credit = CREDITO+SUSCRIPCION; fully_paid_stuck = CONTADO (en general).
      if (key === 'fully_paid_stuck') opts.plan_type = 'CONTADO';
      else if (key === 'partial_credit') opts.plan_type = 'SUSCRIPCION';
    } else if (dimension === 'by_notification') {
      opts.notification_status = key as 'notified_ok' | 'notification_error';
    } else if (dimension === 'by_frequency') {
      opts.frequency = key;
    }
    // Los breakdowns TuDoc (tudoc_by_*) no propagan filtro al drill-down por ahora
    // (fuera de scope del plan 22 — se pueden agregar en una fase futura).
    this.openContactsForStatus(parentStatus, opts);
  }

  setActiveTab(tab: 'dashboard' | 'analytics' | 'policies' | 'emission_errors' | 'payment_errors' | 'verification_errors' | 'vehicle_use_errors' | 'default_address' | 'config' | 'products' | 'sync_mercantil' | 'sync_caracas' | 'annulled_policies' | 'payment_reports' | 'installments_queue' | 'contacts' | 'tudoc_tracking' | 'tudoc_failed_affiliations'): void {
    // Plan 35: con TuDoc apagado el tab es inalcanzable desde la UI, pero este
    // guard cubre un activeTab persistido o una llamada programática.
    if (tab === 'tudoc_tracking' && !this.tudocUiEnabled) { return; }

    console.log('🔄 Changing tab to:', tab);
    this.activeTab = tab;
    this.showSyncDropdown = false;
    
    // Close dropdowns when changing tabs
    this.showPoliciesDropdown = false;
    this.showConfigDropdown = false;
    this.showErrorsDropdown = false;
    
    // Al volver al dashboard, asegurar que los widgets estén al día con los
    // filtros actuales (puede haber cambiado el rango de fechas desde otro tab).
    if (tab === 'dashboard' && this.insurers?.length > 0) {
      this.refreshDashboardWidgetGrid();
      if (this.selectedDashboardInsurerCode) {
        this.segurosDataService.loadDashboardData(undefined, this.selectedDashboardInsurerCode);
      }
    }

    // Load data for the specific tab if needed
    if (tab === 'config') {
      this.loadInsurersConfig();
    }
    if (tab === 'products') {
      this.loadProductsConfig();
    }
    if (tab === 'analytics' && !this.currentAnalyticsData?.geography) {
      this.segurosDataService.loadAnalyticsData();
    }
    // El bloque "Cobranza y Comisión" del tab analytics deriva de
    // widgetSummaries. Si el usuario entra directo a analytics sin pasar por
    // el dashboard, el Map puede estar vacío — forzamos su carga.
    if (tab === 'analytics' && this.widgetSummaries.size === 0 && this.insurers?.length > 0) {
      this.refreshDashboardWidgetGrid();
    }
    if (tab === 'sync_mercantil') {
      this.loadSyncLog();
    }
    if (tab === 'sync_caracas') {
      this.loadSCRelationForYesterday();
    }
    if (tab === 'verification_errors') {
      console.log('📋 Loading verification errors tab');
      this.segurosDataService.loadVerificationErrorPoliciesData({ page: 1, limit: 20 });
    }
    if (tab === 'vehicle_use_errors') {
      console.log('📋 Loading vehicle use errors tab');
      this.segurosDataService.loadVehicleUseErrorPoliciesData({ page: 1, limit: 20 });
    }
    if (tab === 'default_address') {
      console.log('📋 Loading default address tab');
      this.segurosDataService.loadDefaultAddressPoliciesData({ page: 1, limit: 20 });
    }
    if (tab === 'annulled_policies') {
      console.log('📋 Loading annulled policies tab');
      console.log('Current data:', this.currentAnnulledPoliciesData);
      console.log('Is loading:', this.isLoadingAnnulledPolicies);
      console.log('Date range:', this.annulledPoliciesDateRange);
      console.log('Policies array:', this.currentAnnulledPoliciesData?.policies);
      console.log('Policies length:', this.currentAnnulledPoliciesData?.policies?.length);
      // Siempre inicializar para asegurar que se cargue
      this.initializeAnnulledPoliciesDateRange();
    }
    if (tab === 'installments_queue') {
      // Misma puerta que la pestaña vieja: el motor de summary es de Mercantil.
      // Seguros Caracas no tiene sync de summary, así que su cola no existe.
      if (this.selectedInsurerCode === 'SC') return;
      this.iqLoadBreakdown();
      this.iqLoad(1);
    }
    if (tab === 'payment_reports') {
      // Gating: la funcionalidad está implementada únicamente para Mercantil.
      // Cuando el filtro general está en SC, la UI muestra empty state — no
      // hace falta golpear los endpoints SM-only.
      if (this.selectedInsurerCode === 'SC') return;
      // Un solo disparo: listado y KPIs viajan juntos por el mismo canal.
      this.loadPaymentReports(1);
    }
  }

  // ===========================================================================
  // TAB: Reporte de Cuotas a Seguros — la cola por CUOTA (pestaña nueva)
  // ===========================================================================
  // Convive con "Reporte de Pagos", que no se toca. Ojo con confundirlas: la
  // vieja también muestra "Cuota 1, Cuota 2", pero cada fila suya es un PAGO y
  // el número de cuota es una etiqueta derivada. Aquí cada fila ES una cuota,
  // y por eso aparecen las que ningún flujo se acordó de sembrar.
  iqCola: 'REPORTABLE' | 'SIN_DATO' | 'ESPERANDO_SM' | 'SIN_RECIBO' | 'RECONOCIDAS' = 'REPORTABLE';
  iqRows: any[] = [];
  iqGroups: any[] = [];
  iqBreakdown: any = null;
  iqLoading = false;
  iqSearch = '';
  iqPage = 1;
  iqBusyPolicy: { [code: string]: boolean } = {};

  /** Las cinco colas del ticket 03, con el orden en que se trabajan. */
  readonly IQ_COLAS: { key: any; label: string; hint: string }[] = [
    { key: 'REPORTABLE',   label: 'Reportables',    hint: 'SM abrió el recibo y no lo reconoce: hay trabajo' },
    { key: 'SIN_DATO',     label: 'Sin dato',       hint: 'Nunca le preguntamos a SM por estas cuotas' },
    { key: 'ESPERANDO_SM', label: 'Esperando a SM', hint: 'Cobradas, pero SM aún no ha abierto el recibo' },
    { key: 'SIN_RECIBO',   label: 'Sin recibo',     hint: 'Cobradas sin recibo generado: no reportables' },
    { key: 'RECONOCIDAS',  label: 'Al día',         hint: 'SM las da por pagadas' },
    { key: 'NO_APLICA',    label: 'No aplica',      hint: 'Inicial de póliza al contado: su pago ya viajó a SM al emitirla' }
  ];

  /**
   * Nombre del cliente presentable. En `people` los nombres están como los
   * tecleó quien registró —"stward caseres", "yanetzy marcano"— y a veces en
   * mayúsculas enteras, así que no basta con `text-transform: capitalize`:
   * hay que bajar todo y subir la inicial de cada palabra.
   *
   * Es sólo presentación: el dato no se toca.
   */
  iqNombre(valor: string | null | undefined): string {
    if (!valor) return 'Sin cliente';
    return String(valor)
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /** ¿Hay algo que resetear? Decide si se ofrece el botón de limpiar. */
  get iqFiltroActivo(): boolean {
    return !!(this.iqSearch && this.iqSearch.trim()) || this.iqCola !== 'REPORTABLE' || this.iqPage !== 1;
  }

  /** Deja la vista como al entrar: sin búsqueda, en la cola de trabajo y en la primera página. */
  iqLimpiar(): void {
    this.iqSearch = '';
    this.iqCola = 'REPORTABLE';
    this.iqLoad(1);
    this.iqLoadBreakdown();
  }

  /** Cuántas cuotas hay en una cola, o null si la cabecera aún no ha llegado. */
  iqCuentaCola(key: string): number | null {
    const r = this.iqBreakdown?.reparto?.[key];
    return r ? r.cuotas : null;
  }

  iqSetCola(cola: any): void {
    this.iqCola = cola;
    this.iqLoad(1);
  }

  iqLoadBreakdown(): void {
    this._user.getInsuranceInstallmentsQueueBreakdown().subscribe({
      next: (r: any) => { this.iqBreakdown = r; },
      error: (e: any) => console.error('[cola cuotas] breakdown', e)
    });
  }

  iqLoad(page = 1): void {
    this.iqLoading = true;
    this.iqPage = page;
    this._user.listInsuranceInstallmentsQueue({
      cola: this.iqCola, page, limit: 25, search: this.iqSearch
    }).subscribe({
      next: (r: any) => {
        this.iqRows = r?.rows || [];
        this.iqGroups = this.iqAgrupar(this.iqRows);
        // Página nueva, acordeón cerrado: el estado abierto es de las filas que
        // ya no están, y arrastrarlo abriría clientes que nadie desplegó.
        this.iqExpandido = {};
        this.iqLoading = false;
      },
      error: (e: any) => { console.error('[cola cuotas] listado', e); this.iqLoading = false; }
    });
  }

  /**
   * Agrupa cliente → póliza dentro de la página. A propósito dentro de la
   * página y no del universo: paginar por cliente obligaría a traer todas sus
   * cuotas de golpe, y hay clientes con decenas.
   */
  private iqAgrupar(rows: any[]): any[] {
    const porCliente = new Map<string, any>();
    for (const r of rows) {
      const cid = String(r?.person?.id || 'sin-cliente');
      if (!porCliente.has(cid)) {
        porCliente.set(cid, { person: r.person, polizas: new Map<string, any>() });
      }
      const cli = porCliente.get(cid);
      const pcode = String(r?.poliza?.code || 'sin-poliza');
      if (!cli.polizas.has(pcode)) {
        cli.polizas.set(pcode, { poliza: r.poliza, credito: r.credito, cuotas: [] });
      }
      cli.polizas.get(pcode).cuotas.push(r);
    }
    return [...porCliente.values()].map(c => ({
      person: c.person,
      polizas: [...c.polizas.values()]
    }));
  }

  /**
   * Pregunta a SM por toda la póliza. Es lo que convierte "sin verificar" en un
   * dato real: sin esto, lo que se ve en pantalla es el último corte del sync,
   * que dejó de mirar la cuota justo cuando la cobramos.
   */
  iqRefrescarPoliza(policyCode: string): void {
    if (!policyCode || this.iqBusyPolicy[policyCode]) return;
    this.iqBusyPolicy[policyCode] = true;
    this._user.refreshInsurancePolicyFromSM(policyCode).subscribe({
      next: (r: any) => {
        this.iqBusyPolicy[policyCode] = false;
        const d = r?.data;
        if (d?.avisos?.length) {
          // Doble cobro: SM la da por pagada y nosotros seguimos cobrándola.
          // No se completa nada; se avisa. Completarla sería condonar deuda.
          console.warn('[cola cuotas] avisos', d.avisos);
        }
        this.iqLoad(this.iqPage);
        this.iqLoadBreakdown();
      },
      error: (e: any) => {
        this.iqBusyPolicy[policyCode] = false;
        console.error('[cola cuotas] refresco', e);
      }
    });
  }

  /** Etiqueta y color de la celda que forman los dos ejes (ticket 02). */
  iqEstadoChip(row: any): { label: string; clase: string } {
    switch (row?.estado) {
      case 'AL_DIA':               return { label: 'Al día', clase: 'bg-green-100 text-green-800' };
      case 'AL_DIA_POR_EVIDENCIA': return { label: 'Al día (por evidencia)', clase: 'bg-green-100 text-green-800' };
      case 'SIN_CONFIRMAR':        return { label: 'Enviado, sin confirmar', clase: 'bg-amber-100 text-amber-800' };
      case 'RECHAZADO':            return { label: 'Rechazado', clase: 'bg-red-100 text-red-800' };
      case 'ESPERANDO_SM':         return { label: 'Esperando a SM', clase: 'bg-gray-100 text-gray-700' };
      case 'SIN_RECIBO':           return { label: 'Sin recibo', clase: 'bg-gray-100 text-gray-700' };
      case 'SIN_DATO':             return { label: 'Sin dato', clase: 'bg-slate-100 text-slate-700' };
      case 'NO_APLICA':            return { label: 'No se reporta por cuota', clase: 'bg-slate-100 text-slate-700' };
      default:                     return { label: 'Reportable', clase: 'bg-blue-100 text-blue-800' };
    }
  }

  /** Sólo tiene sentido reportar lo que SM tiene abierto y no reconoce. */
  iqPuedeReportar(row: any): boolean {
    return row?.estado === 'REPORTABLE' || row?.estado === 'SIN_CONFIRMAR' || row?.estado === 'RECHAZADO';
  }

  /**
   * Quién reportó la cuota y por qué vía, para la fila.
   *
   * `AUTO` es el envío que dispara el propio cobro; `MANUAL`, el de una persona
   * desde la consola. Las filas viejas no tienen `sent_origin` —se reportaron
   * antes de que se guardara— y ahí se dice lo que se sabe y nada más.
   */
  iqQuienReporto(row: any): string {
    const r = row?.sm_report || {};
    if (!r.sent_at) return '';
    if (r.sent_origin === 'AUTO') return 'reportada automáticamente al cobrar';
    const quien = r.sent_by?.name || r.sent_by?.email;
    if (r.sent_origin === 'MANUAL') return quien ? `reportada por ${quien}` : 'reportada desde la consola';
    return quien ? `reportada por ${quien}` : 'reportada';
  }

  /**
   * Por dónde entró el cobro, en cristiano. El canal lo resuelve el backend
   * desde la cuenta receptora del pago: `typeTransaction` vale `AUTO` en el
   * botón, en el débito inmediato y en el botón BDV, así que no distingue nada.
   */
  iqCanalLabel(row: any): string {
    switch (row?.canal_cobro) {
      case 'BDV-PAYMENT-BUTTON':        return 'Botón de pago BDV';
      case 'MERCANTIL-PAYMENT-BUTTON':  return 'Botón de pago Mercantil';
      case 'BANCO_PLAZA_INSTANT_DEBIT': return 'Débito inmediato Banco Plaza';
      case 'TDD':                       return 'Domiciliación';
      default:                          return 'Canal sin identificar';
    }
  }

  // ── Acordeón por cliente ───────────────────────────────────────────────────
  // Colapsado de entrada: la página trae 25 cuotas y desplegadas todas obligan a
  // desplazarse para ver a cuántos clientes se está mirando. El estado es de la
  // pantalla, no del dato, así que vive aquí y se pierde al cambiar de página, a
  // propósito: una página nueva empieza cerrada.
  iqExpandido: { [personId: string]: boolean } = {};

  iqAbierto(cli: any): boolean {
    return !!this.iqExpandido[String(cli?.person?.id)];
  }

  iqToggleCliente(cli: any): void {
    const id = String(cli?.person?.id);
    this.iqExpandido[id] = !this.iqExpandido[id];
  }

  /** Cuántas cuotas del cliente hay EN ESTA PÁGINA (el grupo no cruza páginas). */
  iqCuotasDe(cli: any): number {
    return (cli?.polizas || []).reduce((n: number, p: any) => n + (p.cuotas?.length || 0), 0);
  }

  /** La ficha del crédito del cliente, que es donde se trabaja el caso completo. */
  iqFichaUrl(cli: any): string {
    return cli?.person?.code ? `/console/person/financial-payments/${cli.person.code}` : '#';
  }

  // Cédula copiada más recientemente: sólo para dar acuse en el botón.
  iqCopiado: string | null = null;

  iqCopiar(texto: string, ev?: Event): void {
    ev?.stopPropagation();          // el clic no debe plegar la ficha del cliente
    if (!texto) return;
    const acuse = () => {
      this.iqCopiado = texto;
      setTimeout(() => { if (this.iqCopiado === texto) this.iqCopiado = null; }, 1500);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texto).then(acuse).catch(() => this.iqCopiarLegacy(texto, acuse));
    } else {
      // http:// sin contexto seguro —el caso de la consola en local— no expone
      // la API moderna, así que hace falta el camino viejo.
      this.iqCopiarLegacy(texto, acuse);
    }
  }

  private iqCopiarLegacy(texto: string, acuse: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); acuse(); } catch (e) { console.error('[cola cuotas] copiar', e); }
    document.body.removeChild(ta);
  }

  // ── Paginación ─────────────────────────────────────────────────────────────
  // El total sale del reparto de la cabecera, que ya cuenta la cola entera. Con
  // búsqueda activa devuelve null: ese conteo no conoce el filtro, y enseñar un
  // "de N" que no corresponde es peor que no enseñar ninguno.
  iqTotalPaginas(): number | null {
    if (this.iqSearch && this.iqSearch.trim()) return null;
    const total = this.iqCuentaCola(this.iqCola);
    if (total == null) return null;
    return Math.max(1, Math.ceil(total / 25));
  }

  /**
   * Saltar al final sólo tiene sentido cuando se sabe cuál es el final: con
   * búsqueda activa no hay total, y el botón se apaga en vez de adivinar.
   * La comprobación vive aquí y no en el template porque `iqTotalPaginas()`
   * puede ser null y el chequeo estricto de plantillas lo rechaza — con razón.
   */
  iqHayUltima(): boolean {
    const tp = this.iqTotalPaginas();
    return tp != null && this.iqPage < tp;
  }

  iqIrUltima(): void {
    const tp = this.iqTotalPaginas();
    if (tp != null) this.iqLoad(tp);
  }

  iqHaySiguiente(): boolean {
    const tp = this.iqTotalPaginas();
    // Sin total conocido, el criterio es el de siempre: una página llena puede
    // tener continuación.
    return tp == null ? this.iqRows.length >= 25 : this.iqPage < tp;
  }

  // Visor del pago vinculado a la cuota: el mismo componente que usa la pestaña
  // vieja y la ficha del crédito, para que el detalle del pago se lea igual en
  // los tres sitios.
  iqViewerParams: any = null;

  iqVerPago(template: TemplateRef<any>, row: any): void {
    this.iqViewerParams = { person_code: row?.person?.code, code_payment: row?.pago?.code };
    this.offcanvasService.open(template, { position: 'bottom', keyboard: false });
  }

  // ===========================================================================
  // TAB: Reporte de Pagos de Seguros (reporte manual + trazabilidad)
  // ===========================================================================
  prData: any[] = [];
  // Listado AGRUPADO por cliente → póliza (ordenado oldest-first)
  prGroups: any[] = [];
  prExpanded: { [ownerId: string]: boolean } = {};
  prStats: { pending: number; reported: number; permanently_failed: number } | null = null;
  prFilters: {
    status: string; search: string; dateFrom: string; dateTo: string;
    dateField: string; paymentMethod: string; bankCode: string;
    reportedBy: string; minAttempts: number | null; sortOrder: string;
  } = {
      status: 'pending', search: '', dateFrom: '', dateTo: '',
      // 'payment' = fecha REAL del pago (register_payment_date ?? date). Es la que la
      // lista muestra, ordena y usa para el semáforo de antigüedad: se filtra por lo que se ve.
      dateField: 'payment', paymentMethod: 'ALL', bankCode: '',
      reportedBy: '', minAttempts: null, sortOrder: 'asc'
    };
  /** Facetas devueltas por /stats para poblar los dropdowns CON contadores. */
  prFacets: { by_payment_method: any[]; by_bank: any[]; by_reporter: any[] } = {
    by_payment_method: [], by_bank: [], by_reporter: []
  };
  /**
   * Error del backend en el listado/KPIs.
   *
   * Se muestra explícitamente en la UI en vez de dejar la lista vacía: un fallo de
   * la consulta y un filtro sin coincidencias se veían idénticos, y eso hacía
   * imposible distinguir "no hay pagos" de "la consulta se rompió".
   */
  prError: string | null = null;

  /** Panel de filtros avanzados desplegable (capa 3). */
  prFiltersOpen = false;
  /** Vista operativa activa (capa 1), para resaltar el chip. */
  prActiveView: string | null = null;
  /** Canal único de recarga: debounce + switchMap para evitar carreras. */
  private prReload$ = new Subject<void>();
  private prSub: Subscription | null = null;
  // Paginación POR CLIENTE (no por pago) para que los grupos no se partan.
  prPagination: any = { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false };
  prLoading = false;
  // Modal de auditoría
  prAuditModalOpen = false;
  prAuditLoading = false;
  prAuditRows: any[] = [];
  prAuditPayment: any = null;
  // Modal de resumen pre-reporte
  prReportModalOpen = false;
  prReportPayment: any = null;
  prReporting = false;
  // Dry-run: simulación del reporte (traza real devuelta por el backend, sin ejecutar)
  prDryRunning = false;
  prDryRunResult: any = null;
  // Detalle enriquecido de la póliza/cuotas (se carga al abrir el modal)
  prDetail: any = null;
  prDetailLoading = false;
  // Visor de pago (offcanvas reutilizando app-person-payments)
  prViewerParams: any = null;

  /**
   * Objeto de filtros que se manda al backend. Lo comparten el listado y los KPIs:
   * si se enviaran filtros distintos, las tarjetas contradecirían la tabla.
   * Los campos vacíos los omite el servicio (HttpParams), así que no ensucian la query.
   */
  private prBuildFilters(page: number = this.prPagination.page): InsuranceReportFilters {
    const f = this.prFilters;
    const filters: InsuranceReportFilters = {
      status: f.status,
      search: f.search,
      dateField: f.dateField,
      paymentMethod: f.paymentMethod,
      bankCode: f.bankCode,
      sortOrder: f.sortOrder,
      page,
      limit: this.prPagination.limit
    };
    // Solo se mandan las fechas cuando el rango está COMPLETO y es válido: un rango
    // a medias produciría un filtro sorpresa mientras el usuario todavía escribe.
    if (DateUtils.isValidDateString(f.dateFrom) && DateUtils.isValidDateString(f.dateTo)) {
      filters.dateFrom = f.dateFrom;
      filters.dateTo = f.dateTo;
    }
    if (f.minAttempts != null) filters.minAttempts = f.minAttempts;
    // "Reportado por" solo aplica donde tiene sentido.
    if (f.reportedBy && (f.status === 'reported' || f.status === 'all')) filters.reportedBy = f.reportedBy;
    return filters;
  }

  /**
   * Canal ÚNICO de recarga del tab.
   *
   * Con aplicación inmediata de filtros, una respuesta lenta puede llegar después de
   * una más reciente y pisarla (el usuario ve datos que no corresponden a lo que
   * pidió). `switchMap` cancela la petición anterior en cuanto llega una nueva.
   * Lista y KPIs viajan en el MISMO forkJoin para que nunca queden desfasados.
   */
  private prInitReloadChannel(): void {
    if (this.prSub) return; // ya inicializado
    this.prSub = this.prReload$.pipe(
      debounceTime(350),
      switchMap(() => {
        this.prLoading = true;
        this.prError = null;
        const filters = this.prBuildFilters();
        // Se captura el error en lugar de descartarlo: un fallo del backend NO debe
        // verse como "no hay resultados" — eso hace imposible distinguir un filtro
        // sin coincidencias de una consulta rota.
        return forkJoin({
          list: this._user.listInsuranceReportPaymentsGrouped(filters).pipe(catchError(err => of({ __error: err }))),
          stats: this._user.getInsuranceReportStats(filters).pipe(catchError(err => of({ __error: err })))
        });
      })
    ).subscribe({
      next: ({ list, stats }: any) => {
        if (list?.__error) {
          this.prError = this.prErrorMessage(list.__error);
          this.prGroups = [];
          // Se resetea la paginación: dejarla con los valores previos producía el
          // footer contradictorio "0 de 48 cliente(s)".
          this.prPagination = { ...this.prPagination, total: 0, totalPages: 0, hasMore: false };
        } else if (list) {
          this.prGroups = list?.data || [];
          this.prPagination = list?.pagination || this.prPagination;
          // Adjuntar el cliente a cada pago (para el visor / reportar) y expandir el
          // primer cliente (el más antiguo, el siguiente a trabajar).
          this.prGroups.forEach((c: any, i: number) => {
            (c.policies || []).forEach((p: any) => (p.payments || []).forEach((pm: any) => { pm.person = c.person; }));
            this.prExpanded[c._id] = (i === 0);
          });
        } else {
          this.prGroups = [];
        }
        if (stats?.__error && !this.prError) {
          this.prError = this.prErrorMessage(stats.__error);
        }
        if (stats?.stats) {
          this.prStats = stats.stats;
          this.prFacets = {
            by_payment_method: stats.stats.by_payment_method || [],
            by_bank: stats.stats.by_bank || [],
            by_reporter: stats.stats.by_reporter || []
          };
          this.prComputeMethodCounts();
          // Las etiquetas de los chips de banco y "reportado por" usan los nombres
          // que vienen en las facetas: hay que recalcularlas cuando llegan.
          this.prRefreshChips();
        }
        this.prLoading = false;
      },
      error: (err: any) => {
        this.prGroups = [];
        this.prLoading = false;
        this.prError = this.prErrorMessage(err);
      }
    });
  }

  /**
   * Mensaje de error del backend, priorizando el texto que devuelve la API (que ya
   * explica el motivo, p. ej. un rango de fechas inválido → 400).
   */
  private prErrorMessage(err: any): string {
    const status = err?.status ? `HTTP ${err.status}` : '';
    const msg = err?.error?.error || err?.error?.message || err?.message || 'Error desconocido';
    return status ? `${status} · ${msg}` : msg;
  }

  loadPaymentReportsStats(): void {
    // Los KPIs viajan junto al listado en el mismo canal (ver prInitReloadChannel).
    this.loadPaymentReports(this.prPagination.page);
  }

  loadPaymentReports(page: number = 1): void {
    this.prInitReloadChannel();
    this.prPagination.page = page;
    // Toda mutación de filtros pasa por aquí, así que es el punto natural para
    // refrescar los chips de inmediato (sin esperar la respuesta del backend).
    this.prRefreshChips();
    this.prReload$.next();
  }

  // ===== Agrupado: helpers de UI =====
  prToggleClient(ownerId: string): void { this.prExpanded[ownerId] = !this.prExpanded[ownerId]; }
  prIsExpanded(ownerId: string): boolean { return !!this.prExpanded[ownerId]; }

  /**
   * Pago pendiente MÁS ANTIGUO del cliente.
   *
   * Antes tomaba el primero del array, o sea que dependía del orden que trajera el
   * backend. Al hacer configurable el ordenamiento eso se vuelve frágil: con orden
   * descendente apuntaría al más NUEVO y el ejecutivo reportaría en orden incorrecto,
   * sin ningún error visible. Ahora se elige explícitamente por fecha — defensa en
   * profundidad: el backend mantiene el invariante y el front no asume que se cumple.
   */
  prNextPending(client: any): any {
    let best: any = null;
    let bestTime = Number.POSITIVE_INFINITY;
    for (const p of (client?.policies || [])) {
      for (const pm of (p?.payments || [])) {
        const ir = pm?.insurance_report;
        if (ir?.reportado || ir?.permanently_failed) continue;
        const raw = pm?.eff_date || pm?.register_payment_date || pm?.date;
        const t = raw ? new Date(raw).getTime() : Number.POSITIVE_INFINITY;
        if (t < bestTime) { bestTime = t; best = pm; }
      }
    }
    return best;
  }

  // ===== Trazabilidad: quién reportó =====

  /**
   * Nombre de quien reportó el pago.
   *
   * Prioriza `reported_by` (campo raíz, el que además permite filtrar). Para los
   * pagos históricos que aún no pasaron por el backfill, cae a la última entrada
   * exitosa de history[]. Si no hay actor en ninguno, fue un reporte automático o
   * sin contexto de auditoría.
   */
  prReportedByName(pm: any): string | null {
    const ir = pm?.insurance_report;
    if (!ir?.reportado) return null;
    const direct = ir.reported_by;
    if (direct && (direct.name || direct.email)) return direct.name || direct.email;
    const hist = Array.isArray(ir.history) ? ir.history : [];
    for (let i = hist.length - 1; i >= 0; i--) {
      const h = hist[i];
      if (h?.success === true && h?.actor && (h.actor.name || h.actor.email)) {
        return h.actor.name || h.actor.email;
      }
    }
    return 'Sistema (automático)';
  }

  prIsSystemReported(pm: any): boolean {
    return this.prReportedByName(pm) === 'Sistema (automático)';
  }

  // ===========================================================================
  // Panel de auditoría transversal
  //
  // La auditoría existente es PUNTUAL (un modal por pago). Responder "¿qué pasó
  // esta semana?" obligaba a abrir pago por pago. Este panel agrega por ejecutivo.
  //
  // OJO: la colección guarda un registro por INTENTO (hasta ~11 por pago), así que
  // los conteos son de intentos, no de pagos. La UI lo dice explícitamente.
  // ===========================================================================
  prAuditPanelOpen = false;
  prAuditSummaryLoading = false;
  prAuditSummary: any = null;
  prAuditRange: { dateFrom: string; dateTo: string } = { dateFrom: '', dateTo: '' };

  prToggleAuditPanel(): void {
    this.prAuditPanelOpen = !this.prAuditPanelOpen;
    if (this.prAuditPanelOpen && !this.prAuditSummary) {
      // Arranque razonable: el mes en curso.
      this.prAuditRange = { dateFrom: DateUtils.startOfMonth(), dateTo: DateUtils.today() };
      this.prLoadAuditSummary();
    }
  }

  prLoadAuditSummary(): void {
    this.prAuditSummaryLoading = true;
    this._user.getInsuranceReportAuditSummary({
      dateFrom: this.prAuditRange.dateFrom,
      dateTo: this.prAuditRange.dateTo
    }).subscribe({
      next: (resp: any) => {
        this.prAuditSummary = resp || null;
        this.prAuditSummaryLoading = false;
      },
      error: () => {
        this.prAuditSummary = null;
        this.prAuditSummaryLoading = false;
        Swal.fire('Error', 'No se pudo cargar el resumen de auditoría', 'error');
      }
    });
  }

  prApplyAuditPreset(preset: { date_from: string; date_to: string }): void {
    this.prAuditRange = { dateFrom: preset?.date_from || '', dateTo: preset?.date_to || '' };
    this.prLoadAuditSummary();
  }

  /** Clic en un ejecutivo → lista filtrada por sus reportes en el mismo rango. */
  prDrillDownReporter(row: any): void {
    this.prResetFilters();
    this.prFilters.status = 'reported';
    this.prFilters.reportedBy = row?._id ? String(row._id) : 'SYSTEM';
    this.prFilters.dateField = 'reported';
    this.prFilters.dateFrom = this.prAuditRange.dateFrom;
    this.prFilters.dateTo = this.prAuditRange.dateTo;
    this.prActiveView = null;
    this.prAuditPanelOpen = false;
    this.prFiltersOpen = true;
    this.loadPaymentReports(1);
  }

  prExportAuditSummary(): void {
    const rows = this.prAuditSummary?.by_actor || [];
    if (!rows.length) {
      Swal.fire('Sin datos', 'No hay actividad de auditoría en este periodo.', 'info');
      return;
    }
    const sheet: any[][] = [
      ['Reporte de Auditoría — Reportes a la Aseguradora'],
      [`Periodo: ${this.prAuditRange.dateFrom} a ${this.prAuditRange.dateTo}`],
      ['NOTA: los conteos son de INTENTOS de reporte, no de pagos únicos.'],
      [],
      ['Ejecutivo', 'Email', 'Exitosos', 'Fallidos', 'Total intentos', 'Último reporte']
    ];
    rows.forEach((r: any) => {
      sheet.push([
        r.name || 'Sistema (automático)',
        r.email || '',
        r.success || 0,
        r.failure || 0,
        r.total || 0,
        r.last ? new Date(r.last).toLocaleString('es-VE') : ''
      ]);
    });
    const t = this.prAuditSummary?.totals || {};
    sheet.push([], ['TOTAL', '', t.success || 0, t.failure || 0, t.total || 0, '']);
    sheet.push(['Pagos únicos involucrados', '', '', '', t.payments || 0, '']);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet), 'Auditoría');
    XLSX.writeFile(wb, `auditoria-reportes-${this.prAuditRange.dateFrom}_${this.prAuditRange.dateTo}.xlsx`);
  }
  prIsNext(client: any, pm: any): boolean { return this.prNextPending(client) === pm; }

  prReportNext(client: any): void {
    const next = this.prNextPending(client);
    if (next) this.reportPayment(next);
    else Swal.fire('Info', 'Este cliente no tiene pagos pendientes por reportar.', 'info');
  }

  /** Foto del cliente a partir del objeto person (no de un row). */
  personPhotoG(person: any): any {
    const url = person?.photo_url;
    if (url) return this.sanitizer.bypassSecurityTrustUrl(url);
    const female = (person?.gender === 'F');
    return this.sanitizer.bypassSecurityTrustUrl(
      female ? '../assets/public/images/avatara-female.png' : '../assets/public/images/avatar.png'
    );
  }

  /** Etiqueta amigable del producto a partir del type_product (string). */
  prProductLabelG(t: string): string {
    const s = (t || '').toString().trim();
    if (!s) return 'Seguro';
    const map: { [k: string]: string } = { SEGUROS_MERCANTIL: 'Seguros Mercantil', SEGURO_MERCANTIL: 'Seguros Mercantil' };
    return map[s] || s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  prSetStatus(status: string): void {
    if (this.prFilters.status === status) return;
    this.prFilters.status = status;
    // Los filtros que no aplican al estado nuevo se limpian, para no dejar un filtro
    // invisible restringiendo resultados desde un panel que ya no se muestra.
    if (status !== 'reported' && status !== 'all') {
      this.prFilters.reportedBy = '';
      if (this.prFilters.dateField === 'reported') this.prFilters.dateField = 'payment';
    }
    this.prActiveView = null;
    this.loadPaymentReports(1);
  }

  /** Aplicación inmediata: cualquier cambio de filtro recarga (con debounce). */
  prApplyFilters(): void {
    this.prActiveView = null;
    this.loadPaymentReports(1);
  }

  /** Rango de fechas: solo recarga cuando queda completo y válido, o cuando se vacía. */
  prOnDateRangeChange(): void {
    const { dateFrom, dateTo } = this.prFilters;
    const bothEmpty = !dateFrom && !dateTo;
    const bothValid = DateUtils.isValidDateString(dateFrom) && DateUtils.isValidDateString(dateTo);
    if (bothEmpty || bothValid) this.prApplyFilters();
  }

  /** Preset de fecha reusando el catálogo ya existente en DateUtils. */
  prApplyDatePreset(preset: { date_from: string; date_to: string }): void {
    this.prFilters.dateFrom = preset?.date_from || '';
    this.prFilters.dateTo = preset?.date_to || '';
    this.prApplyFilters();
  }

  /** trackBy estable: evita que *ngFor recree los botones y se pierdan los clics. */
  prTrackByKey(_index: number, item: { key: string }): string { return item?.key; }

  /** Resalta el preset cuyo rango coincide con el filtro actual. */
  prIsActivePreset(preset: { date_from: string; date_to: string }): boolean {
    if (!preset?.date_from && !preset?.date_to) return !this.prFilters.dateFrom && !this.prFilters.dateTo;
    return this.prFilters.dateFrom === preset.date_from && this.prFilters.dateTo === preset.date_to;
  }

  /**
   * Presets de fecha como ARRAY tipado, calculado UNA SOLA VEZ.
   *
   * Importante que sea una propiedad y NO un getter: un getter devuelve un array
   * nuevo en cada ciclo de detección de cambios, con lo que *ngFor destruye y
   * recrea los botones continuamente y los clics se pierden con el elemento que
   * iba a recibirlos. (Se evita además el pipe `keyvalue`, que tipa los valores
   * como `unknown` y rompe la compilación estricta de templates.)
   */
  readonly prDatePresetList: Array<{ key: string; label: string; date_from: string; date_to: string }> =
    (() => {
      const presets: any = DateUtils.getDatePresets();
      return Object.keys(presets).map(k => ({ key: k, ...presets[k] }));
    })();

  prToggleSort(): void {
    this.prFilters.sortOrder = this.prFilters.sortOrder === 'asc' ? 'desc' : 'asc';
    this.loadPaymentReports(1);
  }

  get prSortLabel(): string {
    return this.prFilters.sortOrder === 'asc' ? 'Más antiguo primero' : 'Más reciente primero';
  }

  get prSortIcon(): string {
    return this.prFilters.sortOrder === 'asc' ? 'fa-sort-amount-up' : 'fa-sort-amount-down';
  }

  prToggleFiltersPanel(): void {
    this.prFiltersOpen = !this.prFiltersOpen;
  }

  // ===== Capa 1: vistas operativas (un clic arma el filtro completo) =====

  /**
   * "Críticos por reportar": pendientes con más de 7 días sin reportar, más viejo
   * primero. Es el arranque natural del día.
   */
  prViewCriticos(): void {
    const hasta = DateUtils.daysAgo(7); // ya devuelve 'yyyy-MM-dd'
    this.prResetFilters();
    this.prFilters.status = 'pending';
    this.prFilters.dateField = 'payment';
    this.prFilters.dateFrom = '';
    this.prFilters.dateTo = hasta;
    this.prFilters.sortOrder = 'asc';
    this.prActiveView = 'criticos';
    this.loadPaymentReports(1);
  }

  /**
   * "A punto de fallar": pendientes con 8+ intentos. Están a uno o dos intentos de
   * marcarse Fallido automáticamente y perderse.
   */
  prViewPorFallar(): void {
    this.prResetFilters();
    this.prFilters.status = 'pending';
    this.prFilters.minAttempts = 8;
    this.prFilters.sortOrder = 'asc';
    this.prActiveView = 'por_fallar';
    this.loadPaymentReports(1);
  }

  private prResetFilters(): void {
    this.prFilters = {
      status: 'pending', search: '', dateFrom: '', dateTo: '',
      dateField: 'payment', paymentMethod: 'ALL', bankCode: '',
      reportedBy: '', minAttempts: null, sortOrder: 'asc'
    };
  }

  prClearFilters(): void {
    const status = this.prFilters.status;
    this.prResetFilters();
    this.prFilters.status = status; // el estado es navegación, no filtro
    this.prActiveView = null;
    this.loadPaymentReports(1);
  }

  // ===== Chips de filtros activos (removibles uno a uno) =====

  /**
   * Filtros activos como chips. Que se puedan quitar DE UNO EN UNO es el mayor
   * arreglo al "filtré, no salió nada y no sé cuál filtro tiene la culpa".
   *
   * Se guarda en una PROPIEDAD y se recalcula explícitamente (prRefreshChips), no
   * en un getter: un getter en *ngFor devuelve un array nuevo por cada ciclo de
   * detección de cambios, Angular recrea los chips sin parar y el botón de quitar
   * deja de responder.
   */
  prActiveFilterChips: Array<{ key: string; label: string }> = [];
  prActiveFilterCount = 0;

  private prRefreshChips(): void {
    this.prActiveFilterChips = this.prComputeChips();
    this.prActiveFilterCount = this.prActiveFilterChips.length;
  }

  private prComputeChips(): Array<{ key: string; label: string }> {
    const f = this.prFilters;
    const chips: Array<{ key: string; label: string }> = [];
    if (f.search) chips.push({ key: 'search', label: `Búsqueda: "${f.search}"` });
    if (f.dateFrom || f.dateTo) {
      const campo = f.dateField === 'register' ? 'registro' : f.dateField === 'reported' ? 'reporte' : 'pago';
      chips.push({ key: 'date', label: `Fecha de ${campo}: ${f.dateFrom || '…'} → ${f.dateTo || '…'}` });
    }
    if (f.paymentMethod && f.paymentMethod !== 'ALL') {
      chips.push({ key: 'paymentMethod', label: `Método: ${getPaymentMethodLabel(f.paymentMethod)}` });
    }
    if (f.bankCode) {
      const bank = this.prFacets.by_bank.find(b => String(b._id) === String(f.bankCode));
      chips.push({ key: 'bankCode', label: `Banco: ${bank?.name || f.bankCode}` });
    }
    if (f.reportedBy) {
      const rep = this.prFacets.by_reporter.find(r => String(r._id) === String(f.reportedBy));
      const nombre = f.reportedBy === 'SYSTEM' ? 'Sistema (automático)' : (rep?.name || 'Ejecutivo');
      chips.push({ key: 'reportedBy', label: `Reportado por: ${nombre}` });
    }
    if (f.minAttempts != null) chips.push({ key: 'minAttempts', label: `Intentos ≥ ${f.minAttempts}` });
    return chips;
  }

  /** Quitar un filtro concreto sin resetear el resto. */
  prRemoveFilter(key: string): void {
    switch (key) {
      case 'search': this.prFilters.search = ''; break;
      case 'date': this.prFilters.dateFrom = ''; this.prFilters.dateTo = ''; break;
      case 'paymentMethod': this.prFilters.paymentMethod = 'ALL'; break;
      case 'bankCode': this.prFilters.bankCode = ''; break;
      case 'reportedBy': this.prFilters.reportedBy = ''; break;
      case 'minAttempts': this.prFilters.minAttempts = null; break;
    }
    this.prActiveView = null;
    this.loadPaymentReports(1);
  }

  // ===== Capa 3: qué filtros aplican a cada estado =====

  /** "Reportado por" y "fecha de reporte" solo tienen sentido sobre lo ya reportado. */
  get prShowsReporterFilters(): boolean {
    return this.prFilters.status === 'reported' || this.prFilters.status === 'all';
  }

  /** El filtro por intentos no aporta nada en la vista de reportados. */
  get prShowsAttemptsFilter(): boolean {
    return this.prFilters.status === 'pending' || this.prFilters.status === 'failed' || this.prFilters.status === 'all';
  }

  // ===== Helpers de método de pago =====

  get prPaymentMethodOptions(): string[] { return PAYMENT_METHOD_ORDER; }
  prPaymentMethodLabel(code: string): string { return getPaymentMethodLabel(code); }
  prPaymentMethodMeta(code: string): any { return getPaymentMethodMeta(code); }

  /**
   * ¿Este pago requiere reporte MANUAL? El cron solo procesa domiciliaciones; los
   * pagos 'AUTO' (botón de pagos / débito inmediato) son visibles pero el
   * automatismo no los toma: si nadie los reporta a mano, se quedan pendientes.
   */
  prRequiresManualReport(pm: any): boolean { return requiresManualReport(pm); }

  /**
   * Contadores por método, precalculados al llegar las facetas (no en el template,
   * que se reevaluaría en cada ciclo de detección de cambios).
   *
   * Se incluye el 0 explícito: "Botón de pagos BDV (0)" informa que en esta vista no
   * hay ninguno. Dejarlo sin número obligaría a probar el filtro para descubrirlo.
   */
  prMethodCounts: { [code: string]: number } = {};

  private prComputeMethodCounts(): void {
    const rows = this.prFacets.by_payment_method || [];
    const counts: { [code: string]: number } = {};
    if (!rows.length) { this.prMethodCounts = {}; return; }
    const matchers: { [code: string]: (tx: string, api: string) => boolean } = {
      DOMICILIATION: (tx) => tx === 'DOMICILIATION',
      BDV_PAYMENT_BUTTON: (_tx, api) => api === 'BDV-PAYMENT-BUTTON',
      MERCANTIL_PAYMENT_BUTTON: (_tx, api) => api === 'MERCANTIL-PAYMENT-BUTTON',
      BANCO_PLAZA_INSTANT_DEBIT: (_tx, api) => api === 'BANCO_PLAZA_INSTANT_DEBIT'
    };
    Object.keys(matchers).forEach(code => {
      counts[code] = rows
        .filter((r: any) => matchers[code](r?._id?.tx, r?._id?.api))
        .reduce((acc: number, r: any) => acc + (r.count || 0), 0);
    });
    counts['ALL'] = rows.reduce((acc: number, r: any) => acc + (r.count || 0), 0);
    this.prMethodCounts = counts;
  }

  prHasMethodCounts(): boolean {
    return Object.keys(this.prMethodCounts).length > 0;
  }

  prGoToPage(page: number): void {
    if (page < 1 || page > this.prPagination.totalPages) return;
    this.loadPaymentReports(page);
  }

  // Estado visual de una fila — mismos colores que los componentes de pago (Bootstrap text-bg-*)
  // Pendiente=amarillo, Reportado=verde, Fallido=rojo, En proceso=azul
  prRowState(row: any): { label: string; cls: string } {
    const ir = row?.insurance_report || {};
    if (ir.reportado) return { label: 'Reportado', cls: 'text-bg-success' };
    if (ir.permanently_failed) return { label: 'Fallido', cls: 'text-bg-danger' };
    if ((ir.current_intento || 0) > 0) return { label: 'En proceso', cls: 'text-bg-primary' };
    return { label: 'Pendiente', cls: 'text-bg-warning' };
  }

  // Antigüedad del pago sin reportar (días desde la fecha efectiva) con semáforo
  // para priorizar cobranza: ≤3d verde, 4–7d ámbar, >7d rojo. Solo lectura sobre
  // eff_date/date ya existentes — no cambia ningún comportamiento del reporte.
  prAgeInfoFromDate(raw: any): { days: number; text: string; dotCls: string; textCls: string } {
    const d = raw ? new Date(raw) : null;
    if (!d || isNaN(d.getTime())) return { days: -1, text: '—', dotCls: 'bg-gray-300', textCls: 'text-gray-400' };
    const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    if (days > 7) return { days, text: days + 'd', dotCls: 'bg-red-500', textCls: 'text-red-600' };
    if (days > 3) return { days, text: days + 'd', dotCls: 'bg-yellow-500', textCls: 'text-yellow-700' };
    return { days, text: days + 'd', dotCls: 'bg-green-500', textCls: 'text-green-600' };
  }

  // A nivel de fila (usa la fecha efectiva del pago).
  prAgeInfo(pm: any): { days: number; text: string; dotCls: string; textCls: string } {
    return this.prAgeInfoFromDate(pm?.eff_date || pm?.date);
  }

  // ¿Los intentos de reporte están cerca del límite? (para resaltar el x/10 en rojo,
  // avisando que el pago está a punto de marcarse como Fallido de forma automática).
  prTriesHot(pm: any): boolean {
    const ir = pm?.insurance_report || {};
    const lim = ir.limite || 10;
    return (ir.current_intento || 0) >= Math.max(1, lim - 2);
  }

  // Foto del cliente (selfie) o avatar por defecto según género
  personPhoto(row: any): any {
    const url = row?.person?.photo_url;
    if (url) return this.sanitizer.bypassSecurityTrustUrl(url);
    const female = (row?.person?.gender === 'F');
    return this.sanitizer.bypassSecurityTrustUrl(
      female ? '../assets/public/images/avatara-female.png' : '../assets/public/images/avatar.png'
    );
  }

  // Abrir el visor de pago (mismo componente usado en distribuciones)
  openPaymentViewer(template: TemplateRef<any>, row: any): void {
    this.prViewerParams = { person_code: row?.person?.code, code_payment: row?.code };
    this.offcanvasService.open(template, { position: 'bottom', keyboard: false });
  }

  // Paso 1: abrir modal con el resumen detallado de lo que se va a reportar
  reportPayment(row: any): void {
    if (row?.insurance_report?.reportado) {
      Swal.fire('Atención', 'Este pago ya fue reportado.', 'info');
      return;
    }
    // El modal compartido (app-insurance-report-confirm) carga el detalle y
    // auto-simula al abrir; aquí solo lo abrimos con el pago seleccionado.
    this.prReportPayment = row;
    this.prReportModalOpen = true;
  }

  // El modal compartido emitió (reported): refrescamos contadores y listado.
  onInsuranceReported(): void {
    this.loadPaymentReportsStats();
    this.loadPaymentReports(this.prPagination.page);
  }

  /** ¿El dry-run indica un bloqueo/error (parcial, sin recibo, etc.)? → no permitir confirmar. */
  get prReportBlocked(): boolean {
    const r = this.prDryRunResult;
    if (!r) return false;
    return r.success === false || !!r.error;
  }

  /** Filas de comparación: lo que el pago DISTRIBUYÓ vs lo que se REPORTARÁ a SM. */
  get prComparisonRows(): any[] {
    const r = this.prDryRunResult;
    if (!r || !Array.isArray(r.distribution)) return [];
    const reported = Array.isArray(r.report_summary) ? r.report_summary : [];
    return r.distribution.map((d: any) => {
      const rep = reported.find((x: any) => String(x.receipt_number) === String(d.receipt_number))
        || reported.find((x: any) => String(x.cuota) === String(d.cuota));
      const match = !!rep && String(rep.receipt_number) === String(d.receipt_number) && String(rep.cuota) === String(d.cuota);
      return {
        cuota_dist: d.cuota,
        receipt_dist: d.receipt_number,
        monto_cuota: d.monto_cuota,
        distribuido: d.distribuido,
        pending: d.pending,
        cuota_rep: rep?.cuota ?? null,
        receipt_rep: rep?.receipt_number ?? null,
        match,
        sm_proximo_cuota: d.sm_proximo_cuota,
        sm_proximo_recibo: d.sm_proximo_recibo,
        sm_difiere: d.sm_proximo_cuota != null && String(d.sm_proximo_cuota) !== String(d.cuota)
      };
    });
  }

  // Carga el detalle de póliza(s), número, tipo de producto y cuotas del pago
  loadReportDetail(paymentCode: string): void {
    this.prDetail = null;
    this.prDetailLoading = true;
    this._user.getInsuranceReportDetail(paymentCode).subscribe({
      next: (resp: any) => {
        this.prDetailLoading = false;
        this.prDetail = resp?.data || null;
      },
      error: () => {
        this.prDetailLoading = false;
        this.prDetail = null;
      }
    });
  }

  // ¿Es un pago multi-cuota?
  prIsMulti(row: any): boolean {
    const codes = row?.insurance_report?.installment_codes;
    return Array.isArray(codes) && codes.length > 1;
  }

  closeReportModal(): void {
    if (this.prReporting || this.prDryRunning) return;
    this.prReportModalOpen = false;
    this.prReportPayment = null;
    this.prDryRunResult = null;
    this.prDetail = null;
  }

  // Simulación (dry run): ejecuta TODA la cadena en el backend resolviendo póliza,
  // cuotas y el payload exacto que se enviaría a la aseguradora, SIN persistir
  // ni notificar nada. Devuelve la traza real para inspeccionarla en el modal.
  simulateReport(): void {
    const row = this.prReportPayment;
    if (!row || this.prReporting) return;
    this.prDryRunning = true;
    this.prDryRunResult = null;
    this._user.retryOrCreateInsuranceReport(row.code, true).subscribe({
      next: (resp: any) => {
        this.prDryRunning = false;
        // El backend devuelve la traza en data.trace (would_call con el payload real)
        this.prDryRunResult = resp?.data?.trace || resp?.data || resp;
      },
      error: (error: any) => {
        this.prDryRunning = false;
        this.prDryRunResult = {
          error: error?.error?.message || error?.message || 'Error en la simulación',
          details: error?.error || null
        };
      }
    });
  }

  // JSON formateado del resultado del dry-run para mostrarlo en el panel
  get prDryRunJson(): string {
    return this.prDryRunResult ? JSON.stringify(this.prDryRunResult, null, 2) : '';
  }

  copyDryRunResult(): void {
    try {
      navigator.clipboard?.writeText(this.prDryRunJson);
      Swal.fire({ icon: 'success', title: 'Traza copiada', timer: 1200, showConfirmButton: false });
    } catch {
      // noop
    }
  }

  // Paso 2: confirmar y ejecutar el reporte a la aseguradora.
  // Pide una CONFIRMACIÓN FINAL explícita (acción irreversible que notifica a SM).
  async confirmReport(): Promise<void> {
    const row = this.prReportPayment;
    if (!row) return;

    // Si la simulación detectó un bloqueo (parcial / sin recibo / discrepancia), no permitir.
    if (this.prReportBlocked) {
      Swal.fire('No permitido', 'Hay un bloqueo (pago parcial, cuota sin recibo o discrepancia). No se puede reportar.', 'warning');
      return;
    }

    // Resumen de lo que se va a reportar, para la confirmación final.
    const rows = this.prComparisonRows;
    const resumen = rows.length
      ? rows.map((r: any) => `• Cuota <b>${r.cuota_rep ?? r.cuota_dist}</b> · recibo <b>${r.receipt_rep ?? r.receipt_dist}</b> · ${(r.pending ?? 0)} USD`).join('<br>')
      : 'la(s) cuota(s) de este pago';

    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Confirmar reporte a la aseguradora?',
      html: `Esta acción <b>notifica el pago a Seguros Mercantil</b>, queda registrada a tu nombre y <b>no se puede deshacer</b>.<br><br>Se reportará:<br>${resumen}`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reportar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true
    });
    if (!confirm.isConfirmed) return;

    this.prReporting = true;
    this._user.retryOrCreateInsuranceReport(row.code).subscribe({
      next: (resp: any) => {
        this.prReporting = false;
        this.prReportModalOpen = false;
        this.prReportPayment = null;
        if (resp?.success) {
          Swal.fire('Éxito', resp?.message || 'Pago reportado exitosamente', 'success');
        } else {
          Swal.fire('Atención', resp?.message || 'No se pudo reportar el pago', 'warning');
        }
        this.loadPaymentReportsStats();
        this.loadPaymentReports(this.prPagination.page);
      },
      error: (error: any) => {
        this.prReporting = false;
        const msg = error?.error?.message || error?.message || 'Error desconocido';
        Swal.fire('Error', `No se pudo reportar: ${msg}`, 'error');
      }
    });
  }

  // Etiqueta amigable del producto / aseguradora a partir del type_product
  // (evita mostrar solo el UUID crudo de la póliza)
  prProductLabel(row: any): string {
    const t = (row?.insurance_report?.type_product || row?.typeProduct || '').toString().trim();
    if (!t) return 'Seguro';
    const map: { [k: string]: string } = {
      SEGUROS_MERCANTIL: 'Seguros Mercantil',
      SEGURO_MERCANTIL: 'Seguros Mercantil'
    };
    if (map[t]) return map[t];
    // Humaniza: "SEGURO_DE_VIDA" -> "Seguro De Vida"
    return t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  // URL exacta del endpoint que ejecuta el reporte (solo informativo, para el panel)
  get prReportEndpoint(): string {
    const code = this.prReportPayment?.code || '{paymentCode}';
    return `POST /api/admin/insurance-reports/retry-or-create/${code}`;
  }

  // Vista previa en JSON de exactamente qué se envía y qué deriva el backend.
  // Solo informativo — sirve para disipar dudas del proceso desde la UI.
  get prReportJsonPreview(): string {
    const p = this.prReportPayment;
    if (!p) return '{}';
    const ir = p.insurance_report || {};
    const preview = {
      metodo: 'POST',
      endpoint: `/api/admin/insurance-reports/retry-or-create/${p.code}`,
      request_body: {},
      nota: 'El cuerpo va vacío: solo viaja el código del pago en la URL. El backend deriva el resto desde la BD y reporta a la aseguradora.',
      datos_del_pago: {
        codigo_pago: p.code,
        poliza: p.policy_code || ir.policy_code || null,
        producto: this.prProductLabel(p),
        type_product: ir.type_product || p.typeProduct || null,
        monto: p.amount ?? ir.total_amount ?? null,
        tasa: p.rate ?? ir.change_rate ?? null,
        banco: p.bank?.name || p.bank?.code || null,
        referencia: ir.reference || p.reference || null,
        fecha_pago: p.date || null,
        tipo: this.prIsMulti(p)
          ? `Multi-cuota (${ir.installment_codes?.length || 0} cuotas)`
          : 'Cuota única',
        installment_codes: ir.installment_codes || null,
        intentos: `${ir.current_intento || 0} / ${ir.limite || 10}`
      }
    };
    return JSON.stringify(preview, null, 2);
  }

  // Copia el JSON de la vista previa al portapapeles
  copyReportPreview(): void {
    try {
      navigator.clipboard?.writeText(this.prReportJsonPreview);
      Swal.fire({ icon: 'success', title: 'JSON copiado', timer: 1200, showConfirmButton: false });
    } catch {
      // noop
    }
  }

  // Modal de auditoría (quién hizo qué)
  openAuditModal(row: any): void {
    this.prAuditPayment = row;
    this.prAuditRows = [];
    this.prAuditModalOpen = true;
    this.prAuditLoading = true;
    this._user.getInsuranceReportAudit(row.code).subscribe({
      next: (resp: any) => {
        this.prAuditRows = resp?.data || [];
        this.prAuditLoading = false;
      },
      error: () => {
        this.prAuditLoading = false;
        Swal.fire('Error', 'No se pudo cargar la auditoría', 'error');
      }
    });
  }

  closeAuditModal(): void {
    this.prAuditModalOpen = false;
    this.prAuditPayment = null;
    this.prAuditRows = [];
  }

  // Date filter methods
  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  // Dropdown methods
  togglePoliciesDropdown(): void {
    this.showPoliciesDropdown = !this.showPoliciesDropdown;
    if (this.showPoliciesDropdown) {
      this.showConfigDropdown = false;
      this.showErrorsDropdown = false;
    }
  }

  toggleConfigDropdown(): void {
    this.showConfigDropdown = !this.showConfigDropdown;
    if (this.showConfigDropdown) {
      this.showPoliciesDropdown = false;
      this.showErrorsDropdown = false;
    }
  }

  toggleErrorsDropdown(): void {
    this.showErrorsDropdown = !this.showErrorsDropdown;
    if (this.showErrorsDropdown) {
      this.showPoliciesDropdown = false;
      this.showConfigDropdown = false;
      this.showSyncDropdown = false;
    }
  }

  toggleSyncDropdown(): void {
    this.showSyncDropdown = !this.showSyncDropdown;
    if (this.showSyncDropdown) {
      this.showPoliciesDropdown = false;
      this.showConfigDropdown = false;
      this.showErrorsDropdown = false;
    }
  }

  applyDatePreset(preset: any, presetKey?: string): void {
    // Update selected preset
    if (presetKey) {
      this.selectedPreset = presetKey;
    }
    
    // Apply date filter (this triggers dashboard data reload)
    this.segurosDataService.setDateFilter({
      date_from: preset.date_from,
      date_to: preset.date_to
    });
    
    // Reload all data with new date filter
    this.reloadAllData();
    
    this.showDatePicker = false;
  }

  applyCustomDateRange(startDate: string, endDate: string): void {
    if (DateUtils.isValidDateString(startDate) && DateUtils.isValidDateString(endDate)) {
      // Clear selected preset when using custom range
      this.selectedPreset = '';
      
      this.segurosDataService.setDateFilter({
        date_from: startDate,
        date_to: endDate
      });
      
      // Reload all data with new date filter
      this.reloadAllData();
    }
    this.showDatePicker = false;
  }

  private reloadAllData(): void {
    console.log('🔄 Recargando todos los datos con nuevo filtro de fecha...');

    // Reload dashboard data (KPIs, status distribution, creation trend, recent activity)
    // Respeta la aseguradora seleccionada del dashboard rediseñado y los widget summaries.
    this.refreshDashboard();
    
    // Reload policies data - use search if active, otherwise normal load
    if (this.isSearchActive) {
      this.performSearch();
    } else {
      this.segurosDataService.loadPoliciesData({ page: 1, limit: 20 });
    }
    
    // Reload error policies data - use search if active, otherwise normal load
    if (this.isEmissionErrorsSearchActive) {
      this.performEmissionErrorsSearch();
    } else {
      this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });
    }
    
    // Reload payment error policies data - use search if active, otherwise normal load
    if (this.isPaymentErrorsSearchActive) {
      this.performPaymentErrorsSearch();
    } else {
      this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
    }
    
    // Reload verification error policies data - use search if active, otherwise normal load
    if (this.isVerificationErrorsSearchActive) {
      this.performVerificationErrorsSearch();
    } else {
      this.segurosDataService.loadVerificationErrorPoliciesData({ page: 1, limit: 20 });
    }
    
    // Reload default address policies data - use search if active, otherwise normal load
    if (this.isDefaultAddressSearchActive) {
      this.performDefaultAddressSearch();
    } else {
      this.segurosDataService.loadDefaultAddressPoliciesData({ page: 1, limit: 20 });
    }
    
    // ALWAYS reload analytics data so it's ready when user switches to that tab
    this.segurosDataService.loadAnalyticsData();
    
    // Reload annulled policies data with global date filter
    this.loadAnnulledPoliciesWithGlobalFilter();
    
    console.log('✅ Comandos de recarga enviados para todas las pestañas');
  }

  clearDateFilter(): void {
    this.selectedPreset = '';
    this.segurosDataService.clearDateFilter();
    this.reloadAllData();
    this.showDatePicker = false;
  }

  // Insurer filter methods
  toggleInsurerDropdown(): void {
    this.showInsurerDropdown = !this.showInsurerDropdown;
  }

  // Mapeo de tabs de Sincronización al code de la aseguradora que los habilita.
  // El dropdown "Sincronización" del navbar oculta opciones que no apliquen al
  // filtro general. Si entra una 3ra aseguradora con sync propio, agregar acá.
  private readonly SYNC_TAB_BY_INSURER: Record<string, 'sync_mercantil' | 'sync_caracas'> = {
    SM: 'sync_mercantil',
    SC: 'sync_caracas'
  };

  /** Devuelve true si la opción del dropdown Sincronización debe verse según el
   *  filtro general de aseguradora. Sin filtro (null) → todas visibles. */
  isSyncTabVisible(tab: 'sync_mercantil' | 'sync_caracas'): boolean {
    if (!this.selectedInsurerCode) return true;
    return this.SYNC_TAB_BY_INSURER[this.selectedInsurerCode] === tab;
  }

  /** Hay al menos una opción de sync visible con el filtro actual.
   *  Si no, el botón principal del dropdown se oculta. */
  hasAnyVisibleSyncTab(): boolean {
    return this.isSyncTabVisible('sync_mercantil') || this.isSyncTabVisible('sync_caracas');
  }

  selectInsurer(insurerCode: string | null): void {
    this.selectedInsurerCode = insurerCode;
    this.segurosDataService.setInsurerFilter(insurerCode);
    this.showInsurerDropdown = false;
    // Cache depende de visibleInsurers (que cambia al filtrar).
    this.invalidateCobranzaCaches();

    // Si el tab activo es de sincronización y ya no es visible con el nuevo
    // filtro, redirigir a dashboard para no dejar al usuario en una vista
    // vacía/irrelevante. Cerramos también el dropdown si quedó abierto.
    if ((this.activeTab === 'sync_mercantil' || this.activeTab === 'sync_caracas')
        && !this.isSyncTabVisible(this.activeTab)) {
      this.setActiveTab('dashboard');
    }
    this.showSyncDropdown = false;

    // El Map widgetSummaries puede tener entradas de aseguradoras que ya no
    // están en visibleInsurers tras el cambio de filtro. Invalidamos para
    // que el siguiente refresh cargue solo las visibles y la card de
    // "Cobranza y Comisión" del tab analytics no sume datos huérfanos.
    this.segurosDataService.invalidateAllWidgetSummaries();

    // Sincronizar dashboard: si se filtró por una aseguradora, ese es el
    // widget seleccionado y único visible. Si se limpió el filtro, dejamos
    // la selección actual o caemos a la primera de la lista.
    if (insurerCode) {
      this.selectedDashboardInsurerCode = insurerCode;
      this.segurosDataService.loadDashboardData(undefined, insurerCode);
    }
    this.refreshDashboardWidgetGrid();

    // Reload data after changing filter
    this.reloadAllData();

    // El tab "Reporte de Pagos" (SM-only) no está en reloadAllData. Si el
    // usuario sale de SC hacia SM/Todas con la tab abierta, hidratar; si entra
    // a SC, la UI ya hace toggle al empty state vía *ngIf.
    if (this.activeTab === 'payment_reports' && this.selectedInsurerCode !== 'SC') {
      this.loadPaymentReportsStats();
      this.loadPaymentReports(1);
    }
  }

  getSelectedInsurerName(): string {
    if (!this.selectedInsurerCode) {
      return 'Todas las aseguradoras';
    }
    const insurer = this.insurers.find(i => i.code === this.selectedInsurerCode);
    return insurer ? insurer.name : 'Aseguradora desconocida';
  }

  clearInsurerFilter(): void {
    this.selectedInsurerCode = null;
    this.segurosDataService.clearInsurerFilter();
    this.showInsurerDropdown = false;

    // Volver a mostrar todas las aseguradoras en el grid y refrescar resumenes.
    // No auto-seleccionamos ninguna: el panel detalle aparece sólo cuando el
    // usuario hace click en un widget.
    this.refreshDashboardWidgetGrid();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Dashboard rediseñado: widgets por aseguradora + panel de detalle
  // ─────────────────────────────────────────────────────────────────────────

  // Lista de aseguradoras visibles en el grid del dashboard. Si hay un filtro
  // global de aseguradora seteado, devolvemos sólo esa; si no, todas.
  get visibleInsurers(): any[] {
    if (this.selectedInsurerCode) {
      const match = this.insurers.find(i => i.code === this.selectedInsurerCode);
      return match ? [match] : [];
    }
    return this.insurers || [];
  }

  // Selección de un widget: actualiza el código seleccionado y dispara la
  // carga del panel de detalle scoped a esa aseguradora. Idempotente.
  selectInsurerWidget(insurerCode: string): void {
    if (!insurerCode || insurerCode === this.selectedDashboardInsurerCode) return;
    this.selectedDashboardInsurerCode = insurerCode;
    this.segurosDataService.loadDashboardData(undefined, insurerCode);
  }

  isWidgetSelected(insurerCode: string): boolean {
    return this.selectedDashboardInsurerCode === insurerCode;
  }

  getWidgetSummary(insurerCode: string): InsurerWidgetSummary | null {
    return this.widgetSummaries.get(insurerCode) || null;
  }

  // Conteo de pólizas en estado ERROR para el badge del widget.
  getWidgetErrorCount(insurerCode: string): number {
    const summary = this.getWidgetSummary(insurerCode);
    if (!summary?.statusDistribution?.distribution) return 0;
    const errorEntry = summary.statusDistribution.distribution.find(d => d.status === 'ERROR');
    return errorEntry?.count || 0;
  }

  // Total de pólizas del widget (usa el KPI totalPolicies).
  getWidgetTotal(insurerCode: string): number {
    const summary = this.getWidgetSummary(insurerCode);
    return summary?.kpis?.totalPolicies || 0;
  }

  getWidgetInProcess(insurerCode: string): number {
    return this.getWidgetSummary(insurerCode)?.kpis?.inProcessPolicies || 0;
  }

  getWidgetCompleted(insurerCode: string): number {
    return this.getWidgetSummary(insurerCode)?.kpis?.completedPolicies || 0;
  }

  // Tasa de finalización: completadas / total * 100. Calculado desde los
  // counts en vez de usar el campo completionRate del backend para evitar
  // ambigüedad de escala (0-1 vs 0-100).
  getWidgetCompletionRate(insurerCode: string): number {
    const total = this.getWidgetTotal(insurerCode);
    if (total === 0) return 0;
    return (this.getWidgetCompleted(insurerCode) / total) * 100;
  }

  // Devuelve el objeto aseguradora completo del listado (para logo/nombre en el panel).
  getInsurerByCode(code: string | null): any | null {
    if (!code) return null;
    return this.insurers.find(i => i.code === code) || null;
  }

  // Refresca los widget summaries del grid para las aseguradoras visibles.
  // Mantiene sincronizado el Map de payment errors por aseguradora (lo usa el
  // split de Pagadas en el tab Analítica Avanzada).
  refreshDashboardWidgetGrid(): void {
    const codes = this.visibleInsurers.map(i => i.code);
    if (codes.length === 0) return;
    this.segurosDataService.loadAllWidgetSummaries(codes);
  }

  // Reintento del panel detalle cuando hay error de carga.
  retryDetailPanel(): void {
    if (!this.selectedDashboardInsurerCode) return;
    this.segurosDataService.loadDashboardData(undefined, this.selectedDashboardInsurerCode);
  }

  // trackBy para el *ngFor del grid de widgets.
  trackByInsurerCode(_index: number, insurer: any): string {
    return insurer?.code || '';
  }

  // Data refresh methods
  refreshAllData(): void {
    this.reloadAllData();
  }

  refreshDashboard(): void {
    this.refreshDashboardWidgetGrid();
    if (this.selectedDashboardInsurerCode) {
      this.segurosDataService.loadDashboardData(undefined, this.selectedDashboardInsurerCode);
    }
  }

  refreshAnalytics(): void {
    this.segurosDataService.loadAnalyticsData();
  }

  // Policy management methods
  refreshPolicy(policyCode: string): void {
    this.segurosDataService.refreshPolicy(policyCode).subscribe(success => {
      if (success) {
        // Reload error policies if we're on that tab
        if (this.activeTab === 'emission_errors') {
          this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });
        }
        // Show success message (you can implement a toast service here)
        console.log('Póliza actualizada exitosamente');
      } else {
        // Show error message
        console.error('Error al actualizar la póliza');
      }
    });
  }

  // Utility methods
  formatDate(dateString: string): string {
    return DateUtils.formatSpanishDate(dateString);
  }

  formatDateTime(dateString: string): string {
    return DateUtils.formatSpanishDateTime(dateString);
  }

  // Formatear fecha de pago (puede venir como objeto $date o string)
  formatPaymentDate(paymentDate: any): string {
    if (!paymentDate) return 'N/A';
    
    // Si viene en formato { $date: "..." }
    if (paymentDate.$date) {
      return DateUtils.formatSpanishDateTime(paymentDate.$date);
    }
    
    // Si viene como string directo
    if (typeof paymentDate === 'string') {
      return DateUtils.formatSpanishDateTime(paymentDate);
    }
    
    return 'N/A';
  }

  // Formatear solo la fecha (dd/mm/yyyy)
  formatPaymentDateShort(paymentDate: any): string {
    if (!paymentDate) return 'N/A';
    
    let dateString: string;
    
    // Si viene en formato { $date: "..." }
    if (paymentDate.$date) {
      dateString = paymentDate.$date;
    } else if (typeof paymentDate === 'string') {
      dateString = paymentDate;
    } else {
      return 'N/A';
    }
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }

  // Formatear solo la hora (hh:mm:ss)
  formatPaymentTimeShort(paymentDate: any): string {
    if (!paymentDate) return 'N/A';
    
    let dateString: string;
    
    // Si viene en formato { $date: "..." }
    if (paymentDate.$date) {
      dateString = paymentDate.$date;
    } else if (typeof paymentDate === 'string') {
      dateString = paymentDate;
    } else {
      return 'N/A';
    }
    
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
  }

  // Formatear moneda (USD)
  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  // Formatear moneda en Bolívares (sin símbolo)
  formatBsCurrency(amount: number): string {
    if (amount === null || amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  getCurrentDateRangeText(): string {
    
    // If no dates are set, show "Todo el tiempo"
    if (!this.currentDateFilter.date_from && !this.currentDateFilter.date_to) {
      return 'Todo el tiempo';
    }
    
    // If we have a selected preset, show the preset label
    if (this.selectedPreset) {
      const preset = (this.datePresets as any)[this.selectedPreset];
      if (preset) {
        return preset.label;
      }
    }
    
    // Otherwise, show the date range
    if (this.currentDateFilter.date_from && this.currentDateFilter.date_to) {
      const start = this.formatDate(this.currentDateFilter.date_from);
      const end = this.formatDate(this.currentDateFilter.date_to);
      return `${start} - ${end}`;
    }
    
    if (this.currentDateFilter.date_from) {
      return `Desde ${this.formatDate(this.currentDateFilter.date_from)}`;
    }
    
    if (this.currentDateFilter.date_to) {
      return `Hasta ${this.formatDate(this.currentDateFilter.date_to)}`;
    }
    
    return 'Rango personalizado';
  }

  // Get status badge class
  getStatusBadgeClass(status: string): string {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    switch (status) {
      case 'ACTIVE':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'PENDING':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'ERROR':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'COMPLETED':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'LICENSE_UPLOADED':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'VERIFIED':
        return `${baseClasses} bg-green-100 text-green-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  }

  // Get status text in Spanish
  getStatusText(status: string): string {
    // Retornar el status tal cual viene de la BD sin traducir
    return status;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers del funnel — derivan del metadata file policy-status-metadata.ts
  // ─────────────────────────────────────────────────────────────────────────

  // Etiqueta amigable en español del estado.
  getStatusLabel(code: string): string {
    return getPolicyStatusMetadata(code).label;
  }

  // Acción del usuario / "Hasta:" que se muestra en cada card.
  getStatusUserAction(code: string): string {
    return getPolicyStatusMetadata(code).userAction;
  }

  // Distribución por estado del panel detalle, ordenada cronológicamente
  // por funnelStep para que la card narrativa lea como un embudo.
  getSortedStatusDistribution(): StatusDistribution[] {
    const dist = this.currentDashboardData?.statusDistribution?.distribution || [];
    return sortByFunnelStep(dist);
  }

  // =========================================================================
  // Breakdown del card "Distribución por Estado"
  // =========================================================================
  // El endpoint refactorizado devuelve sub-dimensiones (by_plan_kind,
  // by_notification, by_frequency) para los buckets vendidos. Acá viven los
  // helpers para renderizar el acordeón y persistir su estado.

  private readonly BREAKDOWN_LS_KEY = 'insurance_breakdown_expanded_v1';
  /** Estados con su acordeón actualmente expandido. Se hidrata desde localStorage en ngOnInit. */
  expandedBreakdownStatuses: Set<string> = new Set<string>();

  /** Restaura el set de buckets expandidos desde localStorage. Llamado desde ngOnInit. */
  private restoreBreakdownExpansion(): void {
    try {
      const raw = localStorage.getItem(this.BREAKDOWN_LS_KEY);
      if (!raw) return;
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        this.expandedBreakdownStatuses = new Set(arr.filter(x => typeof x === 'string'));
      }
    } catch (e) {
      // Ignorar JSON corrupto en LS.
    }
  }

  private persistBreakdownExpansion(): void {
    try {
      localStorage.setItem(this.BREAKDOWN_LS_KEY, JSON.stringify([...this.expandedBreakdownStatuses]));
    } catch (e) {
      // QuotaExceeded u otros — silencioso.
    }
  }

  hasBreakdown(item: StatusDistribution): boolean {
    const b = item?.breakdown;
    if (!b) return false;
    return Object.values(b).some(arr => Array.isArray(arr) && arr.length > 0);
  }

  isBreakdownExpanded(status: string): boolean {
    return this.expandedBreakdownStatuses.has(status);
  }

  toggleBreakdown(status: string): void {
    if (this.expandedBreakdownStatuses.has(status)) {
      this.expandedBreakdownStatuses.delete(status);
    } else {
      this.expandedBreakdownStatuses.add(status);
    }
    this.persistBreakdownExpansion();
  }

  /** Devuelve las dimensiones del breakdown en un orden consistente (no usar
   *  Object.keys directo porque el orden no está garantizado en JS estrictamente). */
  getBreakdownDimensions(item: StatusDistribution): BreakdownDimension[] {
    const order: BreakdownDimension[] = [
      'by_plan_kind', 'by_notification', 'by_frequency',
      // Plan 35: las dimensiones de TuDoc sólo llegan en el summary de VE.
      ...(TUDOC_UI_ENABLED
        ? (['tudoc_by_duration', 'tudoc_by_validity', 'tudoc_by_renewals'] as BreakdownDimension[])
        : [])
    ];
    return order.filter(dim => {
      const arr = item?.breakdown?.[dim];
      return Array.isArray(arr) && arr.length > 0;
    });
  }

  /** Plan 22: cantidad de policies TuDoc que vencen en los proximos 7 dias.
   *  Alimenta el banner arriba del widget Distribucion por Estado en el panel VE. */
  getTudocExpiringSoonCount(): number {
    // Plan 35: el banner del dashboard condiciona a `> 0`, así que devolver 0
    // con el flag apagado lo oculta sin tocar el template.
    if (!this.tudocUiEnabled) return 0;
    if (this.selectedDashboardInsurerCode !== TUDOC_INSURER_CODE) return 0;
    const summary = this.widgetSummaries.get(TUDOC_INSURER_CODE);
    return (summary?.statusDistribution as any)?.tudocExpiringCount || 0;
  }

  /** Helper para el template — evita bracket access dinámico en Angular 13. */
  getBreakdownItems(item: StatusDistribution, dim: BreakdownDimension): any[] {
    return item?.breakdown?.[dim] || [];
  }

  getBreakdownDimensionTitle(dim: BreakdownDimension): string {
    return getBreakdownDimensionTitle(dim);
  }

  getBreakdownLabel(dim: BreakdownDimension, key: string, fallback?: string): string {
    return getBreakdownLabel(dim, key, fallback);
  }

  /** Estilos por tone semántico del key del sub-bucket. Tres familias:
   *  success (verde suave), warning (ámbar), danger (rojo), neutral (gris). */
  getBreakdownItemBgClass(key: string): string {
    switch (getBreakdownKeyTone(key)) {
      case 'success': return 'bg-emerald-50';
      case 'warning': return 'bg-amber-50';
      case 'danger':  return 'bg-red-50';
      default:        return 'bg-white';
    }
  }
  getBreakdownItemTextClass(key: string): string {
    switch (getBreakdownKeyTone(key)) {
      case 'success': return 'text-emerald-700';
      case 'warning': return 'text-amber-700';
      case 'danger':  return 'text-red-700';
      default:        return 'text-gray-700';
    }
  }
  getBreakdownItemIconClass(key: string): string {
    switch (getBreakdownKeyTone(key)) {
      case 'success': return 'fa-check-circle text-emerald-500';
      case 'warning': return 'fa-exclamation-triangle text-amber-500';
      case 'danger':  return 'fa-wrench text-red-500';
      default:        return 'fa-circle text-gray-400';
    }
  }

  /** Conteo de pólizas con error de notificación dentro del bucket. Se muestra
   *  como badge ámbar en el header del bucket sin necesidad de expandir. */
  getNotificationErrorCount(item: StatusDistribution): number {
    const arr = item?.breakdown?.by_notification || [];
    return (arr.find(b => b.key === 'notification_error')?.count) || 0;
  }

  /** Conteo de pólizas que cumplen el criterio fully_paid_stuck dentro del
   *  bucket. Aparece sólo en PAYMENT_RECORDED y dispara badge rojo. */
  getFullyPaidStuckCount(item: StatusDistribution): number {
    const arr = item?.breakdown?.by_plan_kind || [];
    return (arr.find(b => b.key === 'fully_paid_stuck')?.count) || 0;
  }

  // Border-left coloreado de la card narrativa de cada estado. Refleja la
  // misma paleta semántica que getStatusBarClass para mantener consistencia.
  getStatusBorderClass(status: string): string {
    const map: { [key: string]: string } = {
      ACTIVE: 'border-green-500',
      PENDING: 'border-yellow-500',
      ERROR: 'border-red-500',
      COMPLETED: 'border-blue-500',
      LICENSE_UPLOADED: 'border-purple-500',
      VERIFIED: 'border-green-500',
      CREATED: 'border-gray-400',
      PLAN_SELECTED: 'border-indigo-400',
      DATA_CLIENT_CONFIRMED: 'border-cyan-400',
      // Refactor Cotiza→Paga→Emite (Plan 2/3 SC/RCV): estados nuevos.
      INVOICE_CREATED: 'border-amber-400',
      ISSUED: 'border-blue-400',
      PAYMENT_RECORDED: 'border-emerald-400',
      ISSUED_PAID: 'border-teal-500',
      ANNULLED: 'border-red-700'
    };
    return map[status] || 'border-gray-300';
  }

  // Get status bar color class for progress bar
  getStatusBarClass(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-500';
      case 'PENDING':
        return 'bg-yellow-500';
      case 'ERROR':
        return 'bg-red-500';
      case 'COMPLETED':
        return 'bg-blue-500';
      case 'LICENSE_UPLOADED':
        return 'bg-purple-500';
      case 'VERIFIED':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  }

  // Helper methods for Creation Trend
  getTotalCreations(): number {
    if (!this.currentDashboardData?.creationTrend?.data) return 0;
    return this.currentDashboardData.creationTrend.data.reduce((total, item) => total + item.count, 0);
  }

  // Config del gráfico de líneas que consume <app-line-chart>. Se reconstruye
  // cada vez que dashboardData$ emite (suscripción en initializeSubscriptions).
  // Valor null cuando no hay datos válidos o el backend devolvió `tooManyRecords`.
  trendChartConfig: any = null;

  /** Construye el config de Chart.js para el gráfico de tendencia. Si ya hay
   *  un chart vivo (config !== null), se hace toggle null → new config en
   *  ticks distintos para forzar destruir el canvas viejo y montar uno nuevo
   *  (sin esto, Chart.js v4 deja el chart anterior y la prop nueva se ignora). */
  buildTrendChartConfig(): void {
    const trend = this.currentDashboardData?.creationTrend;
    if (!trend?.data || trend.data.length === 0 || trend.tooManyRecords) {
      this.trendChartConfig = null;
      return;
    }
    const labels = trend.data.map(d => this.formatDateForTrend(d.date));
    const values = trend.data.map(d => d.count);
    const PRIMARY = ChartConfig.COLORS.primary;
    const newConfig = {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Pólizas creadas',
          data: values,
          borderColor: PRIMARY,
          backgroundColor: PRIMARY + '20', // 12.5% alpha — área suave bajo la línea
          fill: true,
          tension: 0.4,
          pointBackgroundColor: PRIMARY,
          pointBorderColor: '#fff',
          pointBorderWidth: 1
        }]
      },
      options: ChartConfig.getTrendLineConfig()
    };
    if (this.trendChartConfig === null) {
      this.trendChartConfig = newConfig;
    } else {
      this.trendChartConfig = null;
      setTimeout(() => { this.trendChartConfig = newConfig; }, 0);
    }
  }

  formatDateForTrend(dateString: string): string {
    // Detectar si es formato mes (YYYY-MM) o día (YYYY-MM-DD)
    if (/^\d{4}-\d{2}$/.test(dateString)) {
      const date = new Date(dateString + '-01T00:00:00');
      return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }
    const date = DateUtils.fromDateString(dateString);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  }

  formatDateShort(dateString: string): string {
    // Detectar si es formato mes (YYYY-MM) o día (YYYY-MM-DD)
    if (/^\d{4}-\d{2}$/.test(dateString)) {
      const date = new Date(dateString + '-01T00:00:00');
      return date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
    }
    const date = DateUtils.fromDateString(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  }

  // Helper methods for Analytics
  getTotalCount(data: any[]): number {
    if (!data) return 0;
    return data.reduce((total, item) => total + item.count, 0);
  }

  getPercentage(count: number, data: any[]): number {
    const total = this.getTotalCount(data);
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Analítica Avanzada — bloque "Cobranza y Comisión"
  // Deriva de widgetSummaries (Map<insurerCode, summary>) que ya alimenta
  // el grid del dashboard. Sin nuevas llamadas al backend.
  // ─────────────────────────────────────────────────────────────────────────

  // ───────────────────────────────────────────────────────────────────────
  // Cache memoizado de getters costosos. Sin esto, cada CD recrea CobranzaStats
  // y arrays nuevos → Angular detecta cambios → re-render en cascada de los
  // sub-componentes (sales-collection-summary, cards demográficas, etc.) →
  // el navegador puede congelarse al entrar al tab analytics.
  // Las suscripciones a widgetSummaries$, dashboardData$ y los métodos que
  // mutan el filtro (selectInsurer, selectInsurerWidget) invalidan el cache.
  // ───────────────────────────────────────────────────────────────────────
  private _cachedGlobalCobranza: CobranzaStats | null = null;
  private _cachedGlobalHierarchy: CobranzaHierarchy | null = null;
  private _cachedSelectedInsurerCobranza: { code: string | null; value: CobranzaStats | null } = { code: null, value: null };
  private _cachedSelectedInsurerHierarchy: { code: string | null; value: CobranzaHierarchy | null } = { code: null, value: null };
  private _cachedByInsurer: InsurerCobranzaRow[] | null = null;
  private _cachedPaymentErrorsActiveTotal: number | null = null;

  /** Llamar cada vez que cambie widgetSummaries, currentDashboardData o el
   *  filtro de aseguradora. Las suscripciones del componente lo hacen. */
  private invalidateCobranzaCaches(): void {
    this._cachedGlobalCobranza = null;
    this._cachedGlobalHierarchy = null;
    this._cachedSelectedInsurerCobranza = { code: null, value: null };
    this._cachedSelectedInsurerHierarchy = { code: null, value: null };
    this._cachedByInsurer = null;
    this._cachedPaymentErrorsActiveTotal = null;
  }

  getGlobalCobranza(): CobranzaStats {
    if (this._cachedGlobalCobranza) return this._cachedGlobalCobranza;
    // Whitelist por visibleInsurers — defensa en profundidad para que la card
    // del tab analytics no sume entradas huérfanas del Map cuando el filtro
    // de aseguradora cambia.
    const visibleCodes = this.visibleInsurers.map(i => i.code);
    this._cachedGlobalCobranza = deriveGlobalCobranza(this.widgetSummaries, undefined, visibleCodes);
    return this._cachedGlobalCobranza;
  }

  /** Stats de cobranza scoped a la aseguradora seleccionada en el dashboard.
   *  Se alimenta del mismo `widgetSummaries` que el grid — sin nuevas llamadas
   *  al backend. Devuelve null si no hay aseguradora seleccionada. */
  getSelectedInsurerCobranza(): CobranzaStats | null {
    const code = this.selectedDashboardInsurerCode;
    if (!code) return null;
    if (this._cachedSelectedInsurerCobranza.code === code
        && this._cachedSelectedInsurerCobranza.value !== null) {
      return this._cachedSelectedInsurerCobranza.value;
    }
    const summary = this.widgetSummaries.get(code) || null;
    const value = deriveCobranzaFromSummary(summary);
    this._cachedSelectedInsurerCobranza = { code, value };
    return value;
  }

  /** Jerarquía Vendí · Frecuencia · Pago saneado a partir del distribution
   *  enriquecido del dashboard. Usada por la card "Cobranza y Comisión"
   *  del tab analytics y por la card scoped del panel detalle. Memoizada. */
  getGlobalCobranzaHierarchy(): CobranzaHierarchy | null {
    if (this._cachedGlobalHierarchy) return this._cachedGlobalHierarchy;
    const dist = this.currentDashboardData?.statusDistribution?.distribution;
    if (!dist || dist.length === 0) return null;
    this._cachedGlobalHierarchy = deriveCobranzaHierarchy(dist);
    return this._cachedGlobalHierarchy;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // CHIPS DE STATUS PARA LA SECCIÓN DEMOGRÁFICA (Plan C analytics)
  // ═════════════════════════════════════════════════════════════════════════

  /** Definición de los chips multi-select que filtran la sección demográfica.
   *  Cada chip mapea a un set de policy_status + opcional notification_status. */
  readonly demoStatusChips: Array<{ key: 'plan_visto' | 'emitidas' | 'pagadas'; label: string }> = [
    { key: 'plan_visto', label: 'Plan visto' },
    { key: 'emitidas',   label: 'Emitidas' },
    { key: 'pagadas',    label: 'Pagadas' }
  ];
  /** Set de chips actualmente activos. */
  demoStatusFilter = new Set<'plan_visto' | 'emitidas' | 'pagadas'>();
  private readonly DEMO_STATUS_FILTER_LS_KEY = 'insurance_demo_status_filter_v1';

  /** Chips de frecuencia para filtrar la demografía por plan ANUAL/MENSUAL.
   *  Vacío = todas las frecuencias. */
  readonly demoFrequencyChips: Array<{ key: 'ANUAL' | 'MENSUAL'; label: string }> = [
    { key: 'ANUAL',   label: 'Anual' },
    { key: 'MENSUAL', label: 'Mensual' }
  ];
  demoFrequencyFilter = new Set<'ANUAL' | 'MENSUAL'>();
  private readonly DEMO_FREQ_FILTER_LS_KEY = 'insurance_demo_freq_filter_v1';

  /** Hidrata el filtro desde localStorage en ngOnInit. Llamado desde
   *  initializeSubscriptions / ngOnInit. */
  private restoreDemoStatusFilter(): void {
    try {
      const raw = localStorage.getItem(this.DEMO_STATUS_FILTER_LS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          this.demoStatusFilter = new Set(arr.filter((x: any) => ['plan_visto', 'emitidas', 'pagadas'].includes(x)));
        }
      }
    } catch { /* ignore */ }
  }

  private persistDemoStatusFilter(): void {
    try {
      localStorage.setItem(this.DEMO_STATUS_FILTER_LS_KEY, JSON.stringify([...this.demoStatusFilter]));
    } catch { /* ignore */ }
  }

  private restoreDemoFrequencyFilter(): void {
    try {
      const raw = localStorage.getItem(this.DEMO_FREQ_FILTER_LS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          this.demoFrequencyFilter = new Set(arr.filter((x: any) => ['ANUAL', 'MENSUAL'].includes(x)));
        }
      }
    } catch { /* ignore */ }
  }

  private persistDemoFrequencyFilter(): void {
    try {
      localStorage.setItem(this.DEMO_FREQ_FILTER_LS_KEY, JSON.stringify([...this.demoFrequencyFilter]));
    } catch { /* ignore */ }
  }

  isDemoFrequencyActive(key: 'ANUAL' | 'MENSUAL'): boolean {
    return this.demoFrequencyFilter.has(key);
  }

  toggleDemoFrequencyChip(key: 'ANUAL' | 'MENSUAL'): void {
    if (this.demoFrequencyFilter.has(key)) this.demoFrequencyFilter.delete(key);
    else this.demoFrequencyFilter.add(key);
    this.persistDemoFrequencyFilter();
    this.reloadAnalyticsWithDemoFilter();
  }

  clearDemoFrequencyFilter(): void {
    this.demoFrequencyFilter.clear();
    this.persistDemoFrequencyFilter();
    this.reloadAnalyticsWithDemoFilter();
  }

  isDemoStatusActive(key: 'plan_visto' | 'emitidas' | 'pagadas'): boolean {
    return this.demoStatusFilter.has(key);
  }

  toggleDemoStatusChip(key: 'plan_visto' | 'emitidas' | 'pagadas'): void {
    if (this.demoStatusFilter.has(key)) this.demoStatusFilter.delete(key);
    else this.demoStatusFilter.add(key);
    this.persistDemoStatusFilter();
    this.reloadAnalyticsWithDemoFilter();
  }

  clearDemoStatusFilter(): void {
    this.demoStatusFilter.clear();
    this.persistDemoStatusFilter();
    this.reloadAnalyticsWithDemoFilter();
  }

  /** Conteo del chip mostrado en paréntesis. Se calcula del distribution del
   *  dashboard ya cargado (sin requests extra). Si todavía no hay data,
   *  devuelve 0. */
  getDemoStatusChipCount(key: 'plan_visto' | 'emitidas' | 'pagadas'): number {
    const dist = this.currentDashboardData?.statusDistribution?.distribution || [];
    const findCount = (status: string) => dist.find(d => d.status === status)?.count || 0;
    switch (key) {
      case 'plan_visto':
        return findCount('PLAN_SELECTED');
      case 'emitidas':
        return findCount('ISSUED') + findCount('ISSUED_PAID') + findCount('PAYMENT_RECORDED') + findCount('COMPLETED');
      case 'pagadas': {
        // Pagadas saneadas: PR + ISSUED_PAID + COMP con notif OK. Se lee del breakdown.
        // ISSUED_PAID (Plan 3 SC/RCV) emite con pago registrado, cuenta como saneada.
        const pr = dist.find(d => d.status === 'PAYMENT_RECORDED');
        const ip = dist.find(d => d.status === 'ISSUED_PAID');
        const comp = dist.find(d => d.status === 'COMPLETED');
        const prOk = pr?.breakdown?.by_notification?.find(b => b.key === 'notified_ok')?.count || 0;
        const ipOk = ip?.breakdown?.by_notification?.find(b => b.key === 'notified_ok')?.count || 0;
        const compOk = comp?.breakdown?.by_notification?.find(b => b.key === 'notified_ok')?.count || 0;
        return prOk + ipOk + compOk;
      }
    }
  }

  /** Mapea los chips activos a los parámetros policy_status + notification_status
   *  que entiende el endpoint /analytics/aggregate. Retorna {} si no hay filtro. */
  private getDemoStatusFilterParams(): { policy_status?: string; notification_status?: 'notified_ok' | 'notification_error' } {
    if (this.demoStatusFilter.size === 0) return {};
    const statuses = new Set<string>();
    let notifStatus: 'notified_ok' | 'notification_error' | undefined;
    if (this.demoStatusFilter.has('plan_visto')) {
      statuses.add('PLAN_SELECTED');
    }
    if (this.demoStatusFilter.has('emitidas')) {
      statuses.add('ISSUED');
      statuses.add('PAYMENT_RECORDED');
      // ISSUED_PAID (Plan 3 SC/RCV): emite con pago, cuenta como Emitida.
      statuses.add('ISSUED_PAID');
      statuses.add('COMPLETED');
    }
    if (this.demoStatusFilter.has('pagadas')) {
      statuses.add('PAYMENT_RECORDED');
      statuses.add('ISSUED_PAID');
      statuses.add('COMPLETED');
      // Si SOLO pagadas está activo, restringir a notif OK. Si emitidas también
      // está activo, no aplicamos notif para no excluir ISSUED del set.
      if (!this.demoStatusFilter.has('emitidas')) {
        notifStatus = 'notified_ok';
      }
    }
    return {
      policy_status: [...statuses].join(','),
      ...(notifStatus && { notification_status: notifStatus })
    };
  }

  /** Recarga analytics aplicando ambos sets de chips (status + frecuencia). */
  reloadAnalyticsWithDemoFilter(): void {
    const statusParams = this.getDemoStatusFilterParams();
    const frequencyParam = this.demoFrequencyFilter.size > 0
      ? { frequency: [...this.demoFrequencyFilter].join(',') }
      : {};
    this.segurosDataService.loadAnalyticsData(undefined, { ...statusParams, ...frequencyParam });
  }

  /** Total agregado del filtro activo (para el banner informativo). */
  getDemoStatusFilterTotal(): number {
    if (this.demoStatusFilter.size === 0) {
      return this.currentDashboardData?.statusDistribution?.totalPolicies || 0;
    }
    let total = 0;
    for (const key of this.demoStatusFilter) {
      total += this.getDemoStatusChipCount(key);
    }
    // Las pagadas son subset de emitidas → no contar 2 veces.
    if (this.demoStatusFilter.has('emitidas') && this.demoStatusFilter.has('pagadas')) {
      total -= this.getDemoStatusChipCount('pagadas');
    }
    return total;
  }

  /** Para el panel detalle del dashboard la jerarquía sale del mismo
   *  currentDashboardData (que se carga scoped a la aseguradora seleccionada
   *  cuando hay filtro). Si no hay distribución, retorna null. */
  getSelectedInsurerCobranzaHierarchy(): CobranzaHierarchy | null {
    if (!this.selectedDashboardInsurerCode) return null;
    return this.getGlobalCobranzaHierarchy();
  }

  /** Total de pólizas activas con error de notificación pendientes de resolver.
   *  Lo entrega el backend en `statusDistribution.paymentErrorsActiveTotal`
   *  IGNORANDO el filtro de fecha pero respetando insurer_code. Memoizado. */
  getPaymentErrorsActiveTotal(): number {
    if (this._cachedPaymentErrorsActiveTotal !== null) return this._cachedPaymentErrorsActiveTotal;
    this._cachedPaymentErrorsActiveTotal = this.currentDashboardData?.statusDistribution?.paymentErrorsActiveTotal || 0;
    return this._cachedPaymentErrorsActiveTotal;
  }

  getCobranzaByInsurer(): InsurerCobranzaRow[] {
    if (this._cachedByInsurer) return this._cachedByInsurer;
    // visibleInsurers para que la lista respete el filtro general — si el
    // usuario filtró a Mercantil, no quiere ver Caracas en la comparativa.
    this._cachedByInsurer = deriveCobranzaByInsurer(this.widgetSummaries, this.visibleInsurers || []);
    return this._cachedByInsurer;
  }

  // Resuelve el nombre desde la lista global `insurers` (la que carga al
  // inicio del componente). NO confundir con `getInsurerName` existente,
  // que resuelve desde `availableInsurers` (lista del config de productos).
  getInsurerNameByCode(code: string): string {
    return this.insurers?.find(i => i.code === code)?.name || code;
  }

  // Clase Tailwind del badge de % cobranza según el tier.
  getCobranzaTierClass(rate: number): string {
    const tier = getCobranzaTier(rate);
    switch (tier) {
      case 'high':   return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low':    return 'bg-red-100 text-red-800';
      default:       return 'bg-gray-100 text-gray-500';
    }
  }

  // Toggle de la sección colapsable de las 6 cards demográficas.
  toggleDemoSection(): void {
    this.isDemoSectionCollapsed = !this.isDemoSectionCollapsed;
    try {
      localStorage.setItem(
        this.DEMO_SECTION_LS_KEY,
        this.isDemoSectionCollapsed ? '0' : '1'
      );
    } catch {
      // localStorage no disponible — silenciamos.
    }
  }

  // Banner "comisión en $ pendiente" — dismissible y persistido.
  dismissCommissionBanner(): void {
    this.showCommissionInfoBanner = false;
    try {
      localStorage.setItem(this.COMMISSION_BANNER_LS_KEY, '1');
    } catch {
      // silenciamos.
    }
  }

  // Wrapper para usar Number.isNaN desde el template (Angular no expone
  // isNaN global de forma confiable en templates).
  isNaNValue(v: number): boolean {
    return Number.isNaN(v);
  }

  // Formatear profesión - mostrar "No tiene Profesión" cuando esté vacío
  formatProfession(profession: string): string {
    if (!profession || profession.trim() === '' || profession === 'null' || profession === 'undefined') {
      return 'No tiene Profesión';
    }
    return profession;
  }

  // Obtener iniciales de la aseguradora
  getInsurerInitials(insurerName: string): string {
    if (!insurerName || insurerName === 'N/A') {
      return 'N/A';
    }
    
    // Dividir el nombre por espacios y tomar las iniciales
    const words = insurerName.trim().split(/\s+/);
    
    // Si solo hay una palabra, tomar las primeras 3 letras
    if (words.length === 1) {
      return insurerName.substring(0, 3).toUpperCase();
    }
    
    // Si hay múltiples palabras, tomar la primera letra de cada palabra (máximo 4)
    return words
      .slice(0, 4)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  // Traducir campos faltantes de inglés a español
  translateMissingFields(fields: string[]): string {
    if (!fields || fields.length === 0) return '';
    
    const translations: { [key: string]: string } = {
      'country': 'país',
      'state': 'estado',
      'city': 'ciudad',
      'address': 'dirección'
    };
    
    return fields.map(field => translations[field] || field).join(', ');
  }

  // Traducir estado de póliza a español
  translatePolicyStatus(status: string): string {
    const translations: { [key: string]: string } = {
      'CREATED': 'Creada',
      'PLAN_SELECTED': 'Plan Seleccionado',
      'DATA_CLIENT_CONFIRMED': 'Datos de Cliente Confirmados',
      'LICENSE_UPLOADED': 'Licencia Cargada',
      'VERIFIED': 'Verificada',
      'INVOICE_CREATED': 'Orden Creada',
      'ISSUED': 'Emitida (sin pagar)',
      'ISSUED_PAID': 'Emitida y Pagada',
      'PAYMENT_RECORDED': 'Pago Registrado',
      'COMPLETED': 'Completada',
      'ANNULLED': 'Anulada',
      'RENEWED': 'Renovada'
    };
    
    return translations[status] || status;
  }

  // Obtener array de pagos de la póliza (busca en diferentes ubicaciones)
  getPayments(policy: any): any[] {
    if (!policy) return [];
    
    // Buscar en params.payments
    if (policy.params?.payments && Array.isArray(policy.params.payments)) {
      return policy.params.payments;
    }
    
    // Buscar directamente en payments
    if (policy.payments && Array.isArray(policy.payments)) {
      return policy.payments;
    }
    
    return [];
  }

  // Search methods
  performSearch(): void {
    if (this.searchMode === 'general') {
      this.performGeneralSearch();
    } else {
      this.performAdvancedSearch();
    }
  }

  performGeneralSearch(): void {
    if (!this.searchQuery.trim()) {
      this.clearSearch();
      return;
    }

    this.isSearchActive = true;
    this.resetExpandedPolicyGroups();
    this.segurosDataService.searchPoliciesData({
      query: this.searchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  performAdvancedSearch(): void {
    const hasAdvancedParams = 
      this.advancedSearchParams.policy_code.trim() ||
      this.advancedSearchParams.identity.trim() ||
      this.advancedSearchParams.policy_status.trim();

    if (!hasAdvancedParams) {
      this.clearSearch();
      return;
    }

    this.isSearchActive = true;
    const searchParams: any = { page: 1, limit: 20 };

    if (this.advancedSearchParams.policy_code.trim()) {
      searchParams.policy_code = this.advancedSearchParams.policy_code.trim();
    }
    if (this.advancedSearchParams.identity.trim()) {
      searchParams.identity = this.advancedSearchParams.identity.trim();
    }
    if (this.advancedSearchParams.policy_status.trim()) {
      searchParams.policy_status = this.advancedSearchParams.policy_status.trim();
    }

    this.resetExpandedPolicyGroups();
    this.segurosDataService.searchPoliciesData(searchParams);
  }

  clearSearch(): void {
    this.isSearchActive = false;
    this.searchQuery = '';
    this.advancedSearchParams = {
      policy_code: '',
      identity: '',
      policy_status: ''
    };
    this.resetExpandedPolicyGroups();
    this.segurosDataService.loadPoliciesData({ page: 1, limit: 20 });
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
    this.searchMode = this.showAdvancedSearch ? 'advanced' : 'general';
    if (!this.showAdvancedSearch) {
      this.advancedSearchParams = {
        policy_code: '',
        identity: '',
        policy_status: ''
      };
    }
  }

  onSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.performSearch();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Agrupación de pólizas duplicadas (mismo asegurado + producto + frecuencia)
  // ─────────────────────────────────────────────────────────────────────────

  // Lista de grupos derivada del page actual. El template itera acá en vez
  // de currentPoliciesData.data directamente. Recalcula en cada change
  // detection — para listas chicas (page size = 20) el costo es trivial.
  get policyGroups(): PolicyGroup[] {
    const list = this.currentPoliciesData?.data || [];
    return groupPoliciesByInsuredProduct(list);
  }

  isPolicyGroupExpanded(key: string): boolean {
    return this.expandedPolicyGroups.has(key);
  }

  togglePolicyGroup(key: string): void {
    if (this.expandedPolicyGroups.has(key)) {
      this.expandedPolicyGroups.delete(key);
    } else {
      this.expandedPolicyGroups.add(key);
    }
  }

  dismissGroupingCaveat(): void {
    this.showGroupingCaveat = false;
    try {
      localStorage.setItem(this.GROUPING_CAVEAT_LS_KEY, '1');
    } catch {
      // localStorage no disponible (modo privado, cuotas, etc.) — silenciamos.
    }
  }

  // Toggle global del modo "mostrar intentos previos abandonados".
  // Cuando se enciende, los grupos isAllAbandoned vuelven al comportamiento
  // normal de acordeón. Cuando se apaga, solo se ve el primary con badge.
  toggleShowAbandonedAttempts(): void {
    this.showAbandonedAttempts = !this.showAbandonedAttempts;
    try {
      localStorage.setItem(this.SHOW_ABANDONED_LS_KEY, this.showAbandonedAttempts ? '1' : '0');
    } catch {
      // localStorage no disponible — la decisión sigue activa solo en sesión.
    }
    // Al cambiar el modo, resetear los expand individuales para evitar UI
    // inconsistente (un grupo abandonado expandido por badge no debe quedar
    // marcado como expandido cuando ahora se ve siempre).
    this.expandedPolicyGroups.clear();
  }

  // Decide si renderizar el badge "+N intentos previos" en vez del acordeón
  // normal para un grupo. Se muestra solo cuando todos los intentos son
  // abandonados, el toggle global está apagado, y el ejecutivo no lo
  // expandió individualmente por click en el badge.
  shouldShowAbandonedBadge(group: PolicyGroup): boolean {
    return (
      group.isAllAbandoned &&
      group.hiddenAttemptsCount > 0 &&
      !this.showAbandonedAttempts &&
      !this.expandedPolicyGroups.has(group.key)
    );
  }

  // Conteo global de intentos abandonados ocultos en la página actual.
  // La UI lo muestra en el header de la tabla para que el ejecutivo sepa
  // que hay filas no visibles por el filtro.
  get hiddenAbandonedAttemptsCount(): number {
    if (this.showAbandonedAttempts) return 0;
    return this.policyGroups.reduce((acc, g) => acc + (g.isAllAbandoned ? g.hiddenAttemptsCount : 0), 0);
  }

  trackByGroupKey(_index: number, group: PolicyGroup): string {
    return group.key;
  }

  // Helper privado: limpiar estado expandido cuando se recarga el listado.
  private resetExpandedPolicyGroups(): void {
    this.expandedPolicyGroups.clear();
  }

  // Emission Errors Search methods
  performEmissionErrorsSearch(): void {
    if (this.emissionErrorsSearchMode === 'general') {
      this.performEmissionErrorsGeneralSearch();
    } else {
      this.performEmissionErrorsAdvancedSearch();
    }
  }

  performEmissionErrorsGeneralSearch(): void {
    if (!this.emissionErrorsSearchQuery.trim()) {
      this.clearEmissionErrorsSearch();
      return;
    }

    this.isEmissionErrorsSearchActive = true;
    this.segurosDataService.loadErrorPoliciesData({
      query: this.emissionErrorsSearchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  performEmissionErrorsAdvancedSearch(): void {
    const hasAdvancedParams = 
      this.emissionErrorsAdvancedSearchParams.policy_code.trim() ||
      this.emissionErrorsAdvancedSearchParams.identity.trim() ||
      this.emissionErrorsAdvancedSearchParams.policy_status.trim();

    if (!hasAdvancedParams) {
      this.clearEmissionErrorsSearch();
      return;
    }

    this.isEmissionErrorsSearchActive = true;
    const searchParams: any = { page: 1, limit: 20 };

    if (this.emissionErrorsAdvancedSearchParams.policy_code.trim()) {
      searchParams.policy_code = this.emissionErrorsAdvancedSearchParams.policy_code.trim();
    }
    if (this.emissionErrorsAdvancedSearchParams.identity.trim()) {
      searchParams.identity = this.emissionErrorsAdvancedSearchParams.identity.trim();
    }
    if (this.emissionErrorsAdvancedSearchParams.policy_status.trim()) {
      searchParams.policy_status = this.emissionErrorsAdvancedSearchParams.policy_status.trim();
    }

    this.segurosDataService.loadErrorPoliciesData(searchParams);
  }

  clearEmissionErrorsSearch(): void {
    this.isEmissionErrorsSearchActive = false;
    this.emissionErrorsSearchQuery = '';
    this.emissionErrorsAdvancedSearchParams = {
      policy_code: '',
      identity: '',
      policy_status: ''
    };
    this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });
  }

  toggleEmissionErrorsAdvancedSearch(): void {
    this.showEmissionErrorsAdvancedSearch = !this.showEmissionErrorsAdvancedSearch;
    this.emissionErrorsSearchMode = this.showEmissionErrorsAdvancedSearch ? 'advanced' : 'general';
    if (!this.showEmissionErrorsAdvancedSearch) {
      this.emissionErrorsAdvancedSearchParams = {
        policy_code: '',
        identity: '',
        policy_status: ''
      };
    }
  }

  onEmissionErrorsSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.performEmissionErrorsSearch();
    }
  }

  // Payment Errors Search methods
  performPaymentErrorsSearch(): void {
    if (this.paymentErrorsSearchMode === 'general') {
      this.performPaymentErrorsGeneralSearch();
    } else {
      this.performPaymentErrorsAdvancedSearch();
    }
  }

  performPaymentErrorsGeneralSearch(): void {
    if (!this.paymentErrorsSearchQuery.trim()) {
      this.clearPaymentErrorsSearch();
      return;
    }

    this.isPaymentErrorsSearchActive = true;
    this.segurosDataService.loadPaymentErrorPoliciesData({
      query: this.paymentErrorsSearchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  performPaymentErrorsAdvancedSearch(): void {
    const hasAdvancedParams = 
      this.paymentErrorsAdvancedSearchParams.policy_code.trim() ||
      this.paymentErrorsAdvancedSearchParams.identity.trim() ||
      this.paymentErrorsAdvancedSearchParams.policy_status.trim();

    if (!hasAdvancedParams) {
      this.clearPaymentErrorsSearch();
      return;
    }

    this.isPaymentErrorsSearchActive = true;
    const searchParams: any = { page: 1, limit: 20 };

    if (this.paymentErrorsAdvancedSearchParams.policy_code.trim()) {
      searchParams.policy_code = this.paymentErrorsAdvancedSearchParams.policy_code.trim();
    }
    if (this.paymentErrorsAdvancedSearchParams.identity.trim()) {
      searchParams.identity = this.paymentErrorsAdvancedSearchParams.identity.trim();
    }
    if (this.paymentErrorsAdvancedSearchParams.policy_status.trim()) {
      searchParams.policy_status = this.paymentErrorsAdvancedSearchParams.policy_status.trim();
    }

    this.segurosDataService.loadPaymentErrorPoliciesData(searchParams);
  }

  clearPaymentErrorsSearch(): void {
    this.isPaymentErrorsSearchActive = false;
    this.paymentErrorsSearchQuery = '';
    this.paymentErrorsAdvancedSearchParams = {
      policy_code: '',
      identity: '',
      policy_status: ''
    };
    this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
  }

  togglePaymentErrorsAdvancedSearch(): void {
    this.showPaymentErrorsAdvancedSearch = !this.showPaymentErrorsAdvancedSearch;
    this.paymentErrorsSearchMode = this.showPaymentErrorsAdvancedSearch ? 'advanced' : 'general';
    if (!this.showPaymentErrorsAdvancedSearch) {
      this.paymentErrorsAdvancedSearchParams = {
        policy_code: '',
        identity: '',
        policy_status: ''
      };
    }
  }

  onPaymentErrorsSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.performPaymentErrorsSearch();
    }
  }

  // Pagination methods
  loadPoliciesPage(page: number): void {
    this.resetExpandedPolicyGroups();
    if (this.isSearchActive) {
      if (this.searchMode === 'general') {
        this.segurosDataService.searchPoliciesData({
          query: this.searchQuery.trim(),
          page: page,
          limit: 20
        });
      } else {
        const searchParams: any = { page: page, limit: 20 };
        if (this.advancedSearchParams.policy_code.trim()) {
          searchParams.policy_code = this.advancedSearchParams.policy_code.trim();
        }
        if (this.advancedSearchParams.identity.trim()) {
          searchParams.identity = this.advancedSearchParams.identity.trim();
        }
        if (this.advancedSearchParams.policy_status.trim()) {
          searchParams.policy_status = this.advancedSearchParams.policy_status.trim();
        }
        this.segurosDataService.searchPoliciesData(searchParams);
      }
    } else {
      this.segurosDataService.loadPoliciesData({ page: page, limit: 20 });
    }
  }

  loadErrorPoliciesPage(page: number): void {
    console.log('🔄 CLICK DETECTADO - Navegando a página:', page);
    if (this.isEmissionErrorsSearchActive) {
      if (this.emissionErrorsSearchMode === 'general') {
        this.segurosDataService.loadErrorPoliciesData({
          query: this.emissionErrorsSearchQuery.trim(),
          page: page,
          limit: 20
        });
      } else {
        const searchParams: any = { page: page, limit: 20 };
        if (this.emissionErrorsAdvancedSearchParams.policy_code.trim()) {
          searchParams.policy_code = this.emissionErrorsAdvancedSearchParams.policy_code.trim();
        }
        if (this.emissionErrorsAdvancedSearchParams.identity.trim()) {
          searchParams.identity = this.emissionErrorsAdvancedSearchParams.identity.trim();
        }
        if (this.emissionErrorsAdvancedSearchParams.policy_status.trim()) {
          searchParams.policy_status = this.emissionErrorsAdvancedSearchParams.policy_status.trim();
        }
        this.segurosDataService.loadErrorPoliciesData(searchParams);
      }
    } else {
      this.segurosDataService.loadErrorPoliciesData({ page: page, limit: 20 });
    }
  }

  loadPaymentErrorsPage(page: number): void {
    console.log('🔄 CLICK DETECTADO - Navegando a página de errores de pagos:', page);
    if (this.isPaymentErrorsSearchActive) {
      if (this.paymentErrorsSearchMode === 'general') {
        this.segurosDataService.loadPaymentErrorPoliciesData({
          query: this.paymentErrorsSearchQuery.trim(),
          page: page,
          limit: 20
        });
      } else {
        const searchParams: any = { page: page, limit: 20 };
        if (this.paymentErrorsAdvancedSearchParams.policy_code.trim()) {
          searchParams.policy_code = this.paymentErrorsAdvancedSearchParams.policy_code.trim();
        }
        if (this.paymentErrorsAdvancedSearchParams.identity.trim()) {
          searchParams.identity = this.paymentErrorsAdvancedSearchParams.identity.trim();
        }
        if (this.paymentErrorsAdvancedSearchParams.policy_status.trim()) {
          searchParams.policy_status = this.paymentErrorsAdvancedSearchParams.policy_status.trim();
        }
        this.segurosDataService.loadPaymentErrorPoliciesData(searchParams);
      }
    } else {
      this.segurosDataService.loadPaymentErrorPoliciesData({ page: page, limit: 20 });
    }
  }


  // Policy detail helper methods
  isRCVProduct(policyDetail: any): boolean {
    if (!policyDetail) return false;
    const productName = policyDetail.params?.insurance_product?.name || policyDetail.insurance_product?.name || '';
    return productName.toUpperCase().includes('R.C.V') || productName.toUpperCase().includes('RCV');
  }

  getPaymentErrorMessage(policy: any): string {
    const insurerCode = policy.params?.insurance?.code || policy.insurance?.code;
    const payments = policy.params?.payments || [];
    
    // Buscar el ÚLTIMO pago con error (el más reciente)
    let paymentWithError = null;
    for (let i = payments.length - 1; i >= 0; i--) {
      if (payments[i].error) {
        paymentWithError = payments[i];
        break;
      }
    }
    
    if (paymentWithError?.error) {
      const error = paymentWithError.error;
      
      // Seguros Caracas usa cdError/deError
      if (insurerCode === 'SC' && error.deError) {
        return error.deError;
      }
      
      // Seguros Mercantil usa message/code
      if (error.message) {
        return error.message;
      }
      
      if (error.code) {
        return error.code;
      }
      
      return 'Error desconocido al notificar pago';
    }
    
    return 'Error al notificar pago a la aseguradora';
  }

  togglePaymentError(index: number): void {
    this.expandedPaymentErrors[index] = !this.expandedPaymentErrors[index];
  }

  // Error resolution methods
  openResolveErrorModal(policy: any, errorType: 'emission' | 'payment'): void {
    console.log('🔵 openResolveErrorModal called', { policy, errorType });
    // Usar el code de la póliza como ID ya que el backend busca por code
    const policyCode = policy.params?.code || policy.code || '';
    this.resolvingPolicyId = policyCode;
    this.resolvingPolicyCode = policyCode;
    this.resolveErrorType = errorType;
    this.resolutionComment = '';
    this.showResolveErrorModal = true;
    console.log('🔵 showResolveErrorModal set to:', this.showResolveErrorModal);
  }

  closeResolveErrorModal(): void {
    this.showResolveErrorModal = false;
    this.resolvingPolicyId = null;
    this.resolvingPolicyCode = null;
    this.resolveErrorType = null;
    this.resolutionComment = '';
    this.isResolvingError = false;
  }

  submitResolveError(): void {
    if (!this.resolvingPolicyId || !this.resolveErrorType) {
      alert('Error: No se encontró información de la póliza');
      return;
    }

    if (!this.resolutionComment.trim()) {
      alert('Por favor ingrese un comentario explicando cómo se resolvió el error');
      return;
    }

    this.isResolvingError = true;

    const resolveObservable = this.resolveErrorType === 'emission'
      ? this.segurosDataService.resolveEmissionError(
          this.resolvingPolicyId,
          this.resolutionComment.trim()
        )
      : this.segurosDataService.resolvePaymentError(
          this.resolvingPolicyId,
          this.resolutionComment.trim()
        );

    resolveObservable.subscribe({
      next: (response: any) => {
        console.log('✅ Error marcado como resuelto:', response);
        alert(`✅ Error ${this.resolveErrorType === 'emission' ? 'de emisión' : 'de pago'} marcado como resuelto exitosamente`);
        
        // Recargar los datos de errores correspondientes
        if (this.resolveErrorType === 'emission') {
          this.segurosDataService.loadErrorPoliciesData({ page: 1, limit: 20 });
        } else {
          this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
        }
        
        // Recargar el dashboard (respetando la aseguradora seleccionada)
        this.refreshDashboard();

        this.closeResolveErrorModal();
      },
      error: (error: any) => {
        console.error('❌ Error al marcar como resuelto:', error);
        const errorMessage = error?.error?.message || error?.message || 'Error desconocido';
        alert(`❌ Error al marcar como resuelto: ${errorMessage}`);
        this.isResolvingError = false;
      }
    });
  }

  // Deep-link al detalle del pago en /console/person/financial-payments.
  // El backend de payment-errors enriquece el payload con
  // params.invoice_code y params.person_code para construir la URL sin
  // llamadas extras. La ruta destino soporta auto-entry a View 2 (cuotas)
  // cuando recibe el queryParam `invoice`.
  canOpenFinancialPaymentDetail(policy: any): boolean {
    return !!(policy?.params?.person_code && policy?.params?.invoice_code);
  }

  openFinancialPaymentDetail(policy: any): void {
    if (!this.canOpenFinancialPaymentDetail(policy)) return;
    const personCode = policy.params.person_code;
    const invoiceCode = policy.params.invoice_code;
    const url = `/console/person/financial-payments/${personCode}?invoice=${encodeURIComponent(invoiceCode)}`;
    window.open(url, '_blank');
  }

  retryPaymentNotification(policy: any): void {
    const policyCode = policy.params?.code || policy.code;
    const policyNumber = policy.params?.number || policy.policy_number || policyCode;
    
    if (!policyCode) {
      alert('Error: No se encontró el código de la póliza');
      return;
    }

    if (!confirm(`¿Desea reintentar la notificación de pago para la póliza ${policyNumber}?`)) {
      return;
    }

    policy._retrying = true;

    this.segurosDataService.retryPaymentNotification(policyCode).subscribe({
      next: (response: any) => {
        console.log('✅ Reintento de notificación exitoso:', response);
        alert('✅ Notificación de pago reenviada exitosamente');
        policy._retrying = false;

        // Recargar datos de errores de pago y dashboard (scoped a la aseguradora seleccionada)
        this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
        this.refreshDashboard();
      },
      error: (error: any) => {
        console.error('❌ Error al reintentar notificación:', error);
        
        // Log completo del error para debugging
        console.log('Error completo:', JSON.stringify(error, null, 2));
        
        // Extraer el mensaje de error detallado del backend
        let errorMessage = 'Error desconocido';
        let detailedError = '';
        
        // Extraer mensaje principal - buscar en error.error primero
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.error?.msg) {
          errorMessage = error.error.msg;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        // Extraer error detallado de la aseguradora - buscar en error.error.details
        if (error?.error?.details?.deError) {
          detailedError = error.error.details.deError;
        }
        
        // Si hay detalle, mostrarlo; si no, solo el mensaje principal
        const fullErrorMessage = detailedError 
          ? `${errorMessage}\n\n${detailedError}`
          : errorMessage;
        
        alert(`❌ ${fullErrorMessage}`);
        policy._retrying = false;
        
        // Recargar datos de errores de pago y dashboard para refrescar la tabla
        this.segurosDataService.loadPaymentErrorPoliciesData({ page: 1, limit: 20 });
        this.refreshDashboard();
      }
    });
  }

  hasProposal(policyDetail: any): boolean {
    if (!policyDetail) return false;
    return !!(policyDetail.params?.proposal || policyDetail.proposal);
  }

  getProposalData(policyDetail: any): any {
    if (!policyDetail) return null;
    return policyDetail.params?.proposal || policyDetail.proposal || null;
  }

  getVehicleUpdateDate(policyDetail: any): string {
    if (!policyDetail) return '';
    
    // Look for vehicle update date in various possible locations
    const vehicleUpdateDate = policyDetail.params?.vehicleUpdateInfoDate || 
                             policyDetail.params?.vehicle_update_info_date ||
                             policyDetail.vehicleUpdateInfoDate ||
                             policyDetail.vehicle_update_info_date ||
                             policyDetail.params?.vehicle?.update_date ||
                             policyDetail.vehicle?.update_date;
    
    return vehicleUpdateDate || '';
  }

  hasInvoice(policyDetail: any): boolean {
    if (!policyDetail) return false;
    return !!(policyDetail.params?.invoice || policyDetail.invoice);
  }

  getInvoiceData(policyDetail: any): any {
    if (!policyDetail) return null;
    return policyDetail.params?.invoice || policyDetail.invoice || null;
  }

  // Helper function to get invoice status badge class
  getInvoiceStatusClass(status: string): string {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'completado':
      case 'completed':
      case 'paid':
      case 'pagado':
      case 'pago completado':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pendiente':
      case 'pending':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'borrador':
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  }

  getInvoiceFields(policyDetail: any): {label: string, value: string, isStatus?: boolean}[] {
    if (!policyDetail) return [];
    
    const invoiceData = this.getInvoiceData(policyDetail);
    if (!invoiceData) return [];

    const invoiceFields: {label: string, value: string, isStatus?: boolean}[] = [];
    
    // Campos específicos en el orden solicitado
    const requiredFields = [
      { key: 'identifier', label: 'Identificador' },
      { key: 'status', label: 'Estado', isStatus: true },
      { key: 'date', label: 'Fecha' },
      { key: 'total_amount', label: 'Monto Total', fallbacks: ['amount'] },
      { key: 'minimumPaymentDue', label: 'Pago Mínimo Requerido' },
      { key: 'dueBalance', label: 'Saldo Pendiente' },
      { key: 'rate', label: 'Tasa' }
    ];

    // Agregar campos en el orden específico
    requiredFields.forEach(field => {
      let value = invoiceData[field.key];
      
      // Intentar con campos alternativos si el principal no existe
      if ((value === null || value === undefined || value === '') && field.fallbacks) {
        for (const fallback of field.fallbacks) {
          value = invoiceData[fallback];
          if (value !== null && value !== undefined && value !== '') {
            break;
          }
        }
      }
      
      if (value !== null && value !== undefined && value !== '') {
        const formattedValue = this.formatFieldValue(value);
        
        if (formattedValue && formattedValue !== 'N/A') {
          invoiceFields.push({
            label: field.label,
            value: formattedValue,
            isStatus: field.isStatus || false
          });
        }
      } else {
        // Mostrar N/A para campos requeridos que no tienen valor
        invoiceFields.push({
          label: field.label,
          value: 'N/A',
          isStatus: field.isStatus || false
        });
      }
    });

    return invoiceFields;
  }

  // Helper function to get a specific vehicle field value
  getVehicleFieldValue(policyDetail: any, fieldName: string): string | null {
    if (!policyDetail) return null;
    
    const vehicleData = policyDetail.params?.vehicle || policyDetail.vehicle;
    if (!vehicleData) return null;

    // Map of field names to possible variations in the data
    const fieldVariations: {[key: string]: string[]} = {
      'year': ['year', 'Year'],
      'brand': ['brand', 'Brand'],
      'model': ['model', 'Model'],
      'plate': ['plate', 'Plate Number', 'plate_number', 'plateNumber'],
      'serial': ['serial', 'Serial'],
      'version': ['version', 'Version', 'version_name', 'versionName'],
      'color': ['color', 'Color', 'color_name', 'colorName'],
      'usage': ['usage', 'Use', 'use', 'usage_type', 'usageType']
    };

    const possibleNames = fieldVariations[fieldName] || [fieldName];
    
    for (const name of possibleNames) {
      const value = vehicleData[name];
      if (value !== null && value !== undefined && value !== '') {
        return this.formatFieldValue(value);
      }
    }
    
    return null;
  }

  // Helper function to get OCR value for a specific vehicle field
  getVehicleOcrValue(policyDetail: any, fieldName: string): string | null {
    if (!policyDetail) return null;
    
    const vehicleData = policyDetail.params?.vehicle || policyDetail.vehicle;
    if (!vehicleData) return null;

    // Map of field names to their specific OCR field variations
    const ocrFieldVariations: {[key: string]: string[]} = {
      'plate': ['ocr_plate', 'OCR_plate', 'ocrPlate', 'OCRPlate', 'ocr_plate_number', 'OCR_plate_number'],
      'year': ['ocr_year', 'OCR_year', 'ocrYear', 'OCRYear'],
      'brand': ['ocr_brand', 'OCR_brand', 'ocrBrand', 'OCRBrand'],
      'model': ['ocr_model', 'OCR_model', 'ocrModel', 'OCRModel'],
      'serial': ['ocr_serial', 'OCR_serial', 'ocrSerial', 'OCRSerial'],
      'usage': ['ocr_usage', 'OCR_usage', 'ocrUsage', 'OCRUsage', 'ocr_use', 'OCR_use'],
      'version': ['ocr_version', 'OCR_version', 'ocrVersion', 'OCRVersion'],
      'color': ['ocr_color', 'OCR_color', 'ocrColor', 'OCRColor']
    };

    // Get specific variations for this field, or use generic pattern
    const specificVariations = ocrFieldVariations[fieldName];
    const genericVariations = [
      `ocr_${fieldName}`,
      `OCR_${fieldName}`,
      `ocr${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`,
      `OCR${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`
    ];

    const allVariations = specificVariations ? [...specificVariations, ...genericVariations] : genericVariations;

    for (const ocrKey of allVariations) {
      const ocrValue = vehicleData[ocrKey];
      if (ocrValue && ocrValue !== '' && ocrValue !== null && ocrValue !== undefined) {
        return this.formatFieldValue(ocrValue);
      }
    }
    
    return null;
  }

  // Helper function to get other vehicle fields (not in the main layout)
  getOtherVehicleFields(policyDetail: any): {label: string, value: string}[] {
    if (!policyDetail) return [];
    
    const vehicleData = policyDetail.params?.vehicle || policyDetail.vehicle;
    if (!vehicleData) return [];

    const otherFields: {label: string, value: string}[] = [];
    
    // Fields that are already shown in the main layout
    const mainFields = [
      'year', 'brand', 'model', 'plate', 'serial', 'version', 'color', 'usage', 'update_date',
      // Variaciones de nombres que pueden aparecer en los datos
      'Plate Number', 'plate_number', 'plateNumber',
      'Use', 'use', 'usage_type', 'usageType',
      'Version', 'version_name', 'versionName',
      'Color', 'color_name', 'colorName'
    ];
    
    // Map of field names to their Spanish labels
    const fieldLabels: {[key: string]: string} = {
      'vin': 'VIN',
      'type': 'Tipo',
      'fuel_type': 'Tipo de Combustible',
      'transmission': 'Transmisión',
      'capacity': 'Capacidad',
      'weight': 'Peso',
      'commercial_value': 'Valor Comercial',
      'engine': 'Motor',
      'doors': 'Puertas',
      'seats': 'Asientos',
      'cylinder_capacity': 'Cilindraje',
      'fuel': 'Combustible',
      'submodel': 'Submodelo',
      'body_type': 'Tipo de Carrocería'
    };

    // Iterate through all properties in the vehicle object
    Object.keys(vehicleData).forEach(key => {
      const value = vehicleData[key];
      
      // Only show fields that are not in main layout and have actual values
      if (!mainFields.includes(key) && 
          !key.startsWith('ocr_') && 
          value !== null && 
          value !== undefined && 
          value !== '') {
        
        const label = fieldLabels[key] || this.formatFieldLabel(key);
        const formattedValue = this.formatFieldValue(value);
        
        if (formattedValue && formattedValue !== 'N/A') {
          otherFields.push({
            label: label,
            value: formattedValue
          });
        }
      }
    });

    return otherFields;
  }

  getVehicleFields(policyDetail: any): {label: string, value: string, type?: string, ocrValue?: string}[] {
    if (!policyDetail) return [];
    
    const vehicleData = policyDetail.params?.vehicle || policyDetail.vehicle;
    if (!vehicleData) return [];

    const vehicleFields: {label: string, value: string, type?: string, ocrValue?: string}[] = [];
    
    // Orden específico: año, marca, modelo, versión
    const priorityOrder = ['year', 'brand', 'model', 'version'];
    
    // Map of field names to their Spanish labels
    const fieldLabels: {[key: string]: string} = {
      'brand': 'Marca',
      'model': 'Modelo', 
      'year': 'Año',
      'version': 'Versión',
      'plate': 'Placa',
      'vin': 'VIN',
      'serial': 'Serial',
      'type': 'Tipo',
      'color': 'Color',
      'fuel_type': 'Tipo de Combustible',
      'transmission': 'Transmisión',
      'capacity': 'Capacidad',
      'weight': 'Peso',
      'commercial_value': 'Valor Comercial',
      'usage': 'Uso',
      'engine': 'Motor',
      'doors': 'Puertas',
      'seats': 'Asientos',
      'cylinder_capacity': 'Cilindraje',
      'fuel': 'Combustible',
      'submodel': 'Submodelo',
      'body_type': 'Tipo de Carrocería'
    };

    // Función para obtener el valor OCR de un campo
    const getOcrValue = (fieldName: string): string | null => {
      const ocrKey = `ocr_${fieldName}`;
      const ocrValue = vehicleData[ocrKey];
      return (ocrValue && ocrValue !== '' && ocrValue !== null && ocrValue !== undefined) ? 
        this.formatFieldValue(ocrValue) : null;
    };

    // Función para agregar un campo
    const addField = (key: string, isPriority: boolean = false) => {
      const value = vehicleData[key];
      
      if (value !== null && value !== undefined && value !== '' && 
          key !== 'update_date' && !key.startsWith('ocr_')) {
        
        const label = fieldLabels[key] || this.formatFieldLabel(key);
        const formattedValue = this.formatFieldValue(value);
        const ocrValue = getOcrValue(key);
        
        if (formattedValue && formattedValue !== 'N/A') {
          vehicleFields.push({
            label: label,
            value: formattedValue,
            type: isPriority ? 'priority' : 'other',
            ocrValue: ocrValue || undefined
          });
        }
      }
    };

    // Primero agregar campos prioritarios en orden específico
    priorityOrder.forEach(key => {
      addField(key, true);
    });

    // Luego agregar el resto de campos
    Object.keys(vehicleData).forEach(key => {
      if (!priorityOrder.includes(key)) {
        addField(key, false);
      }
    });

    return vehicleFields;
  }

  // Helper function to get insured full name
  getInsuredFullName(policyDetail: any): string {
    if (!policyDetail) return 'N/A';
    
    const insuredData = policyDetail.params?.insured || policyDetail.insured;
    if (!insuredData) return 'N/A';

    const name = insuredData.name || '';
    const apellidos = insuredData.apellidos || '';
    
    if (name && apellidos) {
      return `${name} ${apellidos}`.trim();
    } else if (name) {
      return name;
    } else if (apellidos) {
      return apellidos;
    }
    
    return 'N/A';
  }

  // Helper function to get insured field value
  getInsuredFieldValue(policyDetail: any, fieldName: string): string {
    if (!policyDetail) return 'N/A';
    
    const insuredData = policyDetail.params?.insured || policyDetail.insured;
    if (!insuredData) return 'N/A';

    const value = insuredData[fieldName];
    return (value !== null && value !== undefined && value !== '') ? 
      this.formatFieldValue(value) : 'N/A';
  }

  // Helper function to get insured birth date formatted
  getInsuredBirthDate(policyDetail: any): string {
    if (!policyDetail) return 'N/A';
    
    const insuredData = policyDetail.params?.insured || policyDetail.insured;
    if (!insuredData) return 'N/A';

    const birthDate = insuredData.birth_date;
    if (!birthDate) return 'N/A';

    try {
      // Handle YYYY-MM-DD format directly
      if (typeof birthDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
        const [year, month, day] = birthDate.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Fallback to regular date parsing
      return this.formatDate(birthDate);
    } catch (error) {
      console.error('Error formatting birth date:', error);
      return birthDate.toString();
    }
  }

  getAdditionalFields(policyDetail: any): {label: string, value: string}[] {
    if (!policyDetail) return [];
    
    const additionalFields: {label: string, value: string}[] = [];
    const processedKeys = new Set([
      '_id', 'policy_code', 'policy_status', 'date', 'created_at', 'params',
      'insured', 'insurance', 'insurance_product', 'insurer', 'vehicle', 
      'plan', 'integration_data', 'update_date', 'proposal', 'invoice',
      // Policy holder is same as insured (duplicate data)
      'policy_holder',
      // Vehicle-related fields now in vehicle section
      'vehicleUpdateInfoDate', 'vehicle_update_info_date',
      // Policy fields already shown in main policy section
      'number', 'receipt_number', 'issue_date', 'code',
      // Additional fields to exclude
      'status', 'V', 'insurance_product_id'
    ]);

    // Only include specific fields that we want to show
    const allowedFields = [
      'checks_date', 'checksDate', 'Checks Date',
      'check_declaration', 'checkDeclaration', 'Check Declaration', 
      'check_terms_and_conditions', 'checkTermsAndConditions', 'Check Terms And Conditions'
    ];

    // Check for allowed fields at root level
    Object.keys(policyDetail).forEach(key => {
      if (!processedKeys.has(key) && 
          allowedFields.includes(key) &&
          policyDetail[key] !== null && 
          policyDetail[key] !== undefined) {
        const value = this.formatFieldValue(policyDetail[key]);
        if (value && value !== 'N/A') {
          additionalFields.push({
            label: this.formatFieldLabel(key),
            value: value
          });
        }
      }
    });

    // Check for allowed fields in params
    if (policyDetail.params) {
      Object.keys(policyDetail.params).forEach(key => {
        if (!processedKeys.has(key) && 
            allowedFields.includes(key) &&
            policyDetail.params[key] !== null && 
            policyDetail.params[key] !== undefined) {
          const value = this.formatFieldValue(policyDetail.params[key]);
          if (value && value !== 'N/A') {
            additionalFields.push({
              label: this.formatFieldLabel(`params.${key}`),
              value: value
            });
          }
        }
      });
    }

    return additionalFields;
  }

  private formatFieldLabel(key: string): string {
    const labelMap: {[key: string]: string} = {
      'mt_pendiente': 'MT Pendiente',
      'fe_vencimiento_cuota': 'Fecha Vencimiento Cuota',
      'nu_cuota': 'Número de Cuota',
      'emission_status': 'Estado de Emisión',
      'policy_number': 'Número de Póliza',
      'receipt_number': 'Número de Recibo',
      'issue_date': 'Fecha de Emisión',
      'created_date': 'Fecha de Creación',
      'updated_date': 'Fecha de Actualización',
      'params.user_id': 'ID de Usuario',
      'params.agent_id': 'ID de Agente',
      'params.branch_id': 'ID de Sucursal'
    };

    if (labelMap[key]) {
      return labelMap[key];
    }

    // Convert snake_case to Title Case
    return key.replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase())
              .replace('Params.', '');
  }

  private formatFieldValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }

    if (typeof value === 'object') {
      if (value instanceof Date) {
        return this.formatDateTime(value.toISOString());
      }
      // For complex objects, show as JSON
      return `<pre class="text-xs">${JSON.stringify(value, null, 2)}</pre>`;
    }

    // Check if it looks like a date string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return this.formatDateTime(value);
    }

    return String(value);
  }

  // Export methods
  exportPoliciesData(): void {
    const totalItems = this.currentPoliciesData?.pagination?.totalItems || 0;
    if (totalItems === 0) {
      alert('No hay datos de pólizas para exportar');
      return;
    }

    // Obtener los filtros actuales
    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    // Descargar CSV directamente del backend (streaming)
    this.isExportingPolicies = true;
    this.segurosApiService.exportPoliciesCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingPolicies = false;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `polizas_${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error: any) => {
          this.isExportingPolicies = false;
          alert('Error al exportar pólizas');
          console.error('Error exportando pólizas:', error);
        }
      });
  }

  exportErrorsData(): void {
    if (!this.currentErrorPoliciesData?.pagination?.totalItems) {
      alert('No hay datos de errores para exportar');
      return;
    }

    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    this.isExportingErrors = true;
    this.segurosApiService.exportEmissionErrorsCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingErrors = false;
          this.downloadBlobAsFile(blob, 'errores_emision');
        },
        error: (error: any) => {
          this.isExportingErrors = false;
          alert('Error al exportar errores de emisión');
          console.error('Error exportando errores:', error);
        }
      });
  }

  exportPaymentErrorsData(): void {
    if (!this.currentPaymentErrorsData?.pagination?.totalItems) {
      alert('No hay datos de errores de pagos para exportar');
      return;
    }

    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    this.isExportingPaymentErrors = true;
    this.segurosApiService.exportPaymentErrorsCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingPaymentErrors = false;
          this.downloadBlobAsFile(blob, 'errores_pagos');
        },
        error: (error: any) => {
          this.isExportingPaymentErrors = false;
          alert('Error al exportar errores de pagos');
          console.error('Error exportando errores de pagos:', error);
        }
      });
  }

  // Verification Errors methods
  performVerificationErrorsSearch(): void {
    if (!this.verificationErrorsSearchQuery.trim()) {
      this.clearVerificationErrorsSearch();
      return;
    }

    this.isVerificationErrorsSearchActive = true;
    this.segurosDataService.loadVerificationErrorPoliciesData({
      query: this.verificationErrorsSearchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  clearVerificationErrorsSearch(): void {
    this.isVerificationErrorsSearchActive = false;
    this.verificationErrorsSearchQuery = '';
    this.segurosDataService.loadVerificationErrorPoliciesData({ page: 1, limit: 20 });
  }

  loadVerificationErrorsPage(page: number): void {
    if (this.isVerificationErrorsSearchActive) {
      this.segurosDataService.loadVerificationErrorPoliciesData({
        query: this.verificationErrorsSearchQuery.trim(),
        page: page,
        limit: 20
      });
    } else {
      this.segurosDataService.loadVerificationErrorPoliciesData({ page: page, limit: 20 });
    }
  }

  exportVerificationErrorsData(): void {
    if (!this.currentVerificationErrorsData?.pagination?.totalItems) {
      alert('No hay datos de errores de verificación para exportar');
      return;
    }

    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    this.isExportingVerificationErrors = true;
    this.segurosApiService.exportVerificationErrorsCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingVerificationErrors = false;
          this.downloadBlobAsFile(blob, 'errores_verificacion');
        },
        error: (error: any) => {
          this.isExportingVerificationErrors = false;
          alert('Error al exportar errores de verificación');
          console.error('Error exportando errores de verificación:', error);
        }
      });
  }

  // Vehicle Use Errors methods
  performVehicleUseErrorsSearch(): void {
    if (!this.vehicleUseErrorsSearchQuery.trim()) {
      this.clearVehicleUseErrorsSearch();
      return;
    }

    this.isVehicleUseErrorsSearchActive = true;
    this.segurosDataService.loadVehicleUseErrorPoliciesData({
      query: this.vehicleUseErrorsSearchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  clearVehicleUseErrorsSearch(): void {
    this.isVehicleUseErrorsSearchActive = false;
    this.vehicleUseErrorsSearchQuery = '';
    this.segurosDataService.loadVehicleUseErrorPoliciesData({ page: 1, limit: 20 });
  }

  loadVehicleUseErrorsPage(page: number): void {
    if (this.isVehicleUseErrorsSearchActive) {
      this.segurosDataService.loadVehicleUseErrorPoliciesData({
        query: this.vehicleUseErrorsSearchQuery.trim(),
        page: page,
        limit: 20
      });
    } else {
      this.segurosDataService.loadVehicleUseErrorPoliciesData({ page: page, limit: 20 });
    }
  }

  exportVehicleUseErrorsData(): void {
    if (!this.currentVehicleUseErrorsData?.pagination?.totalItems) {
      alert('No hay datos de errores de uso para exportar');
      return;
    }

    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    this.isExportingVehicleUseErrors = true;
    this.segurosApiService.exportVehicleUseErrorsCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingVehicleUseErrors = false;
          this.downloadBlobAsFile(blob, 'errores_uso_vehiculo');
        },
        error: (error: any) => {
          this.isExportingVehicleUseErrors = false;
          alert('Error al exportar errores de uso de vehículo');
          console.error('Error exportando errores de uso:', error);
        }
      });
  }

  // Default Address methods
  performDefaultAddressSearch(): void {
    if (!this.defaultAddressSearchQuery.trim()) {
      this.clearDefaultAddressSearch();
      return;
    }

    this.isDefaultAddressSearchActive = true;
    this.segurosDataService.loadDefaultAddressPoliciesData({
      query: this.defaultAddressSearchQuery.trim(),
      page: 1,
      limit: 20
    });
  }

  clearDefaultAddressSearch(): void {
    this.isDefaultAddressSearchActive = false;
    this.defaultAddressSearchQuery = '';
    this.segurosDataService.loadDefaultAddressPoliciesData({ page: 1, limit: 20 });
  }

  loadDefaultAddressPage(page: number): void {
    if (this.isDefaultAddressSearchActive) {
      this.segurosDataService.loadDefaultAddressPoliciesData({
        query: this.defaultAddressSearchQuery.trim(),
        page: page,
        limit: 20
      });
    } else {
      this.segurosDataService.loadDefaultAddressPoliciesData({ page: page, limit: 20 });
    }
  }

  exportDefaultAddressData(): void {
    if (!this.currentDefaultAddressData?.pagination?.totalItems) {
      alert('No hay datos de pólizas con dirección por defecto para exportar');
      return;
    }

    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    const insurerCode = this.segurosDataService.getCurrentInsurerFilter();
    const params: any = {};
    if (currentFilter?.date_from) params.date_from = currentFilter.date_from;
    if (currentFilter?.date_to) params.date_to = currentFilter.date_to;
    if (insurerCode) params.insurer_code = insurerCode;

    this.isExportingDefaultAddress = true;
    this.segurosApiService.exportDefaultAddressCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingDefaultAddress = false;
          this.downloadBlobAsFile(blob, 'polizas_direccion_defecto');
        },
        error: (error: any) => {
          this.isExportingDefaultAddress = false;
          alert('Error al exportar pólizas con dirección por defecto');
          console.error('Error exportando dirección por defecto:', error);
        }
      });
  }

  private downloadBlobAsFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private downloadExcel(data: any[], filename: string): void {
    // Convert data to CSV format
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ==========================================
  // INSURERS CONFIGURATION METHODS
  // ==========================================

  loadInsurersConfig(): void {
    this.isLoadingInsurersConfig = true;
    
    const params = {
      page: this.insurersConfigPage,
      limit: this.insurersConfigLimit,
      sortBy: 'name',
      sortOrder: 'asc',
      search: this.insurersConfigSearch || undefined,
      status: this.insurersConfigStatusFilter
    };

    this.segurosDataService['segurosApi'].getInsurersConsole(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Insurers config loaded:', response);
          this.insurersConfigData = response;
          this.isLoadingInsurersConfig = false;
        },
        error: (error: any) => {
          console.error('❌ Error loading insurers config:', error);
          this.isLoadingInsurersConfig = false;
          alert('Error al cargar las aseguradoras');
        }
      });
  }

  onInsurersConfigPageChange(page: number): void {
    this.insurersConfigPage = page;
    this.loadInsurersConfig();
  }

  onInsurersConfigSearch(): void {
    this.insurersConfigPage = 1;
    this.loadInsurersConfig();
  }

  onInsurersConfigStatusFilterChange(status: boolean | null): void {
    this.insurersConfigStatusFilter = status;
    this.insurersConfigPage = 1;
    this.loadInsurersConfig();
  }

  clearInsurersConfigSearch(): void {
    this.insurersConfigSearch = '';
    this.insurersConfigPage = 1;
    this.loadInsurersConfig();
  }

  toggleInsurerStatus(insurer: any): void {
    if (confirm(`¿Está seguro que desea ${insurer.status ? 'desactivar' : 'activar'} la aseguradora "${insurer.name}"?`)) {
      this.segurosDataService['segurosApi'].toggleInsurerStatus(insurer._id, !insurer.status)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            console.log('✅ Insurer status toggled:', response);
            this.loadInsurersConfig();
            this.loadInsurers(); // Refresh dropdown
          },
          error: (error: any) => {
            console.error('❌ Error toggling insurer status:', error);
            alert('Error al cambiar el estado de la aseguradora');
          }
        });
    }
  }

  openEditInsurerModal(insurer: any): void {
    this.selectedInsurerForEdit = insurer;
    this.editInsurerForm = {
      name: insurer.name || '',
      description: insurer.description || '',
      logo: insurer.logo || '',
      params: insurer.params ? { ...insurer.params } : {}
    };
    this.paramsJsonString = JSON.stringify(this.editInsurerForm.params, null, 2);
    this.showParamsJson = false;
    this.showInsurerEditModal = true;
  }

  closeEditInsurerModal(): void {
    this.showInsurerEditModal = false;
    this.selectedInsurerForEdit = null;
    this.editInsurerForm = {
      name: '',
      description: '',
      logo: '',
      params: {}
    };
    this.paramsJsonString = '';
    this.showParamsJson = false;
  }

  toggleParamsJsonView(): void {
    if (!this.showParamsJson) {
      this.paramsJsonString = JSON.stringify(this.editInsurerForm.params, null, 2);
    } else {
      try {
        this.editInsurerForm.params = JSON.parse(this.paramsJsonString);
      } catch (error) {
        alert('JSON inválido. Por favor corrija el formato.');
        return;
      }
    }
    this.showParamsJson = !this.showParamsJson;
  }

  updateParamValue(key: string, value: any): void {
    this.editInsurerForm.params[key] = value;
  }

  addNewParam(): void {
    const key = prompt('Ingrese el nombre del nuevo parámetro:');
    if (key && key.trim()) {
      const value = prompt('Ingrese el valor del parámetro:');
      this.editInsurerForm.params[key.trim()] = value || '';
    }
  }

  removeParam(key: string): void {
    if (confirm(`¿Está seguro que desea eliminar el parámetro "${key}"?`)) {
      delete this.editInsurerForm.params[key];
    }
  }

  saveInsurerChanges(): void {
    if (!this.selectedInsurerForEdit) return;

    if (this.showParamsJson) {
      try {
        this.editInsurerForm.params = JSON.parse(this.paramsJsonString);
      } catch (error) {
        alert('JSON inválido. Por favor corrija el formato antes de guardar.');
        return;
      }
    }

    if (!this.editInsurerForm.name || !this.editInsurerForm.name.trim()) {
      alert('El nombre de la aseguradora es obligatorio');
      return;
    }

    this.isUpdatingInsurer = true;

    const updateData = {
      name: this.editInsurerForm.name.trim(),
      description: this.editInsurerForm.description?.trim() || '',
      logo: this.editInsurerForm.logo?.trim() || '',
      params: this.editInsurerForm.params
    };

    this.segurosDataService['segurosApi'].updateInsurer(this.selectedInsurerForEdit._id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Insurer updated:', response);
          this.isUpdatingInsurer = false;
          this.closeEditInsurerModal();
          this.loadInsurersConfig();
          this.loadInsurers();
          alert('Aseguradora actualizada exitosamente');
        },
        error: (error: any) => {
          console.error('❌ Error updating insurer:', error);
          this.isUpdatingInsurer = false;
          alert(error.error?.message || 'Error al actualizar la aseguradora');
        }
      });
  }

  getParamKeys(params: any): string[] {
    if (!params) return [];
    
    // Define el orden específico de los campos
    const fieldOrder = [
      'urlTyC',
      'commission',
      'administrativeAreaId',
      'subadministrativeAreaId',
      'localityId',
      'postalCode',
      'address1',
      'cdProductor',
      'cdSucursal'
    ];
    
    const keys = Object.keys(params);
    
    // Separar campos conocidos de desconocidos
    const orderedKeys = fieldOrder.filter(key => keys.includes(key));
    const otherKeys = keys.filter(key => !fieldOrder.includes(key));
    
    // Retornar campos ordenados + otros campos al final
    return [...orderedKeys, ...otherKeys];
  }

  getParamLabel(key: string): string {
    const labelMap: { [key: string]: string } = {
      'urlTyC': 'URL de los Términos y Condiciones (urlTyC)',
      'commission': 'Comisión MT (commission)',
      'administrativeAreaId': 'Estado por defecto (administrativeAreaId)',
      'subadministrativeAreaId': 'Municipio por defecto (subadministrativeAreaId)',
      'localityId': 'Ciudad por defecto (localityId)',
      'postalCode': 'Código Postal por defecto (postalCode)',
      'address1': 'Dirección por defecto (address1)',
      'cdProductor': 'Código del Productor (cdProductor)',
      'cdSucursal': 'Código de Sucursal (cdSucursal)'
    };
    
    return labelMap[key] || key;
  }

  getParamType(value: any): string {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'checkbox';
    return 'text';
  }

  // ==================== PRODUCTS CONFIGURATION METHODS ====================

  loadProductsConfig(): void {
    this.isLoadingProductsConfig = true;
    
    const params: any = {
      page: this.productsConfigPage,
      limit: this.productsConfigLimit,
      sortBy: 'name',
      sortOrder: 'asc'
    };

    if (this.productsConfigSearch) {
      params.search = this.productsConfigSearch;
    }

    if (this.productsConfigStatusFilter !== null) {
      params.status = this.productsConfigStatusFilter;
    }

    this.segurosDataService['segurosApi'].getProducts(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Products config loaded:', response);
          this.productsConfigData = response;
          this.isLoadingProductsConfig = false;
        },
        error: (error: any) => {
          console.error('❌ Error loading products config:', error);
          this.isLoadingProductsConfig = false;
        }
      });
  }

  onProductsConfigPageChange(page: number): void {
    this.productsConfigPage = page;
    this.loadProductsConfig();
  }

  onProductsConfigSearchChange(): void {
    this.productsConfigPage = 1;
    this.loadProductsConfig();
  }

  onProductsConfigStatusFilterChange(status: boolean | null): void {
    this.productsConfigStatusFilter = status;
    this.productsConfigPage = 1;
    this.loadProductsConfig();
  }

  openEditProductModal(product: any): void {
    this.selectedProductForEdit = product;
    this.editProductForm = {
      name: product.name || '',
      description: product.description || '',
      icon: product.icon || '',
      mapping: product.mapping || []
    };
    
    this.ensureMappingStructure();
    
    // Store original mapping claves to identify existing vs new mappings
    this.originalMappingClaves = this.editProductForm.mapping
      .map((m: any) => m.clave)
      .filter((c: string) => c);
    
    this.mappingJsonString = JSON.stringify(this.editProductForm.mapping, null, 2);
    this.mappingVisualMode = true;
    
    // Load available insurers for the select dropdowns
    this.loadAvailableInsurers();
    
    this.showProductEditModal = true;
  }

  loadAvailableInsurers(): void {
    const params = {
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    };

    this.segurosDataService['segurosApi'].getInsurersConsole(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Available insurers loaded:', response);
          this.availableInsurers = response.data || [];
          console.log('✅ Available insurers count:', this.availableInsurers.length);
        },
        error: (error: any) => {
          console.error('❌ Error loading available insurers:', error);
          this.availableInsurers = [];
        }
      });
  }

  closeEditProductModal(): void {
    this.showProductEditModal = false;
    this.selectedProductForEdit = null;
    this.editProductForm = {
      name: '',
      description: '',
      icon: '',
      mapping: []
    };
    this.showMappingJson = false;
    this.mappingJsonString = '';
    this.mappingVisualMode = true;
    this.originalMappingClaves = [];
  }

  isNewMapping(mapping: any): boolean {
    return !this.originalMappingClaves.includes(mapping.clave);
  }

  toggleProductStatus(product: any): void {
    const newStatus = !product.status;
    const action = newStatus ? 'activar' : 'desactivar';
    
    if (!confirm(`¿Está seguro que desea ${action} el producto "${product.name}"?`)) {
      return;
    }

    this.segurosDataService['segurosApi'].toggleProductStatus(product._id, newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Product status toggled:', response);
          this.loadProductsConfig();
          alert(`Producto ${action === 'activar' ? 'activado' : 'desactivado'} exitosamente`);
        },
        error: (error: any) => {
          console.error('❌ Error toggling product status:', error);
          alert(error.error?.message || `Error al ${action} el producto`);
        }
      });
  }

  toggleMappingJsonView(): void {
    this.showMappingJson = !this.showMappingJson;
    
    if (this.showMappingJson) {
      try {
        const parsed = JSON.parse(this.mappingJsonString);
        this.editProductForm.mapping = parsed;
      } catch (e) {
        console.error('Invalid JSON for mapping');
      }
    } else {
      this.mappingJsonString = JSON.stringify(this.editProductForm.mapping, null, 2);
    }
  }

  updateMappingFromJson(): void {
    try {
      const parsed = JSON.parse(this.mappingJsonString);
      this.editProductForm.mapping = parsed;
      console.log('✅ Mapping updated from JSON');
    } catch (e) {
      alert('JSON inválido para mapping. Por favor revise la sintaxis.');
      console.error('Invalid JSON for mapping:', e);
    }
  }

  copyMappingJson(): void {
    navigator.clipboard.writeText(this.mappingJsonString).then(() => {
      this.showCopySuccessMessage();
    });
  }

  saveProductChanges(): void {
    if (!this.selectedProductForEdit) return;

    if (this.showMappingJson) {
      try {
        this.editProductForm.mapping = JSON.parse(this.mappingJsonString);
      } catch (e) {
        alert('JSON inválido para mapping. Por favor revise la sintaxis antes de guardar.');
        return;
      }
    }

    this.isUpdatingProduct = true;

    const updateData: any = {
      name: this.editProductForm.name?.trim() || '',
      description: this.editProductForm.description?.trim() || '',
      icon: this.editProductForm.icon?.trim() || '',
      mapping: this.editProductForm.mapping
    };

    this.segurosDataService['segurosApi'].updateProduct(this.selectedProductForEdit._id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Product updated:', response);
          this.isUpdatingProduct = false;
          this.closeEditProductModal();
          this.loadProductsConfig();
          alert('Producto actualizado exitosamente');
        },
        error: (error: any) => {
          console.error('❌ Error updating product:', error);
          this.isUpdatingProduct = false;
          alert(error.error?.message || 'Error al actualizar el producto');
        }
      });
  }

  // Mapping Visual Methods
  ensureMappingStructure(): void {
    if (!Array.isArray(this.editProductForm.mapping)) {
      this.editProductForm.mapping = [];
    }
    
    // Ensure each mapping has the required structure
    this.editProductForm.mapping = this.editProductForm.mapping.map((m: any) => ({
      clave: m.clave || '',
      value: m.value || '',
      params: m.params || {},
      frequencies: m.frequencies || [
        { code: 'ANUAL', label: 'Anual', active: false },
        { code: 'MENSUAL', label: 'Mensual', active: false }
      ]
    }));
  }

  addInsurerToMapping(): void {
    this.editProductForm.mapping.push({
      clave: '',
      value: '',
      params: {},
      frequencies: [
        { code: 'ANUAL', label: 'Anual', active: false },
        { code: 'MENSUAL', label: 'Mensual', active: false }
      ]
    });
    this.updateMappingJson();
  }

  removeInsurerFromMapping(index: number): void {
    if (confirm('¿Está seguro que desea eliminar esta aseguradora del mapping?')) {
      this.editProductForm.mapping.splice(index, 1);
      this.updateMappingJson();
    }
  }

  updateMappingJson(): void {
    this.mappingJsonString = JSON.stringify(this.editProductForm.mapping, null, 2);
  }

  toggleMappingMode(): void {
    if (this.mappingVisualMode) {
      // Switching to JSON mode, update JSON string
      this.updateMappingJson();
    } else {
      // Switching to visual mode, parse JSON
      try {
        this.editProductForm.mapping = JSON.parse(this.mappingJsonString);
        this.ensureMappingStructure();
      } catch (e) {
        alert('Error al parsear el JSON. Por favor verifica la sintaxis.');
        return;
      }
    }
    this.mappingVisualMode = !this.mappingVisualMode;
  }

  addParamToMapping(mappingIndex: number, paramKey: string = '', paramValue: string = ''): void {
    if (!this.editProductForm.mapping[mappingIndex].params) {
      this.editProductForm.mapping[mappingIndex].params = {};
    }
    
    const key = paramKey || prompt('Ingrese el nombre del parámetro:');
    if (key) {
      const value = paramValue || prompt('Ingrese el valor del parámetro:');
      this.editProductForm.mapping[mappingIndex].params[key] = value || '';
      this.updateMappingJson();
    }
  }

  removeParamFromMapping(mappingIndex: number, paramKey: string): void {
    if (confirm(`¿Está seguro que desea eliminar el parámetro "${paramKey}"?`)) {
      delete this.editProductForm.mapping[mappingIndex].params[paramKey];
      this.updateMappingJson();
    }
  }

  onMappingInsurerChange(index: number): void {
    this.updateMappingJson();
  }

  onMappingValueChange(index: number): void {
    this.updateMappingJson();
  }

  onFrequencyToggle(mappingIndex: number, frequencyIndex: number): void {
    this.editProductForm.mapping[mappingIndex].frequencies[frequencyIndex].active = 
      !this.editProductForm.mapping[mappingIndex].frequencies[frequencyIndex].active;
    this.updateMappingJson();
  }

  getInsurerName(code: string): string {
    const insurer = this.availableInsurers.find(i => i.code === code);
    return insurer ? insurer.name : code;
  }

  getAvailableInsurersForSelect(): any[] {
    // Get all insurer codes already in the mapping
    const usedCodes = this.editProductForm.mapping
      .map((m: any) => m.clave)
      .filter((c: string) => c);
    
    // Return only insurers not already in the mapping
    return this.availableInsurers.filter(insurer => !usedCodes.includes(insurer.code));
  }

  // Annulled Policies Methods
  initializeAnnulledPoliciesDateRange(): void {
    // Use global date filter instead of initializing with current month
    this.loadAnnulledPoliciesWithGlobalFilter();
  }

  loadAnnulledPoliciesWithGlobalFilter(): void {
    // Get the current global date filter from the service
    const currentFilter = this.segurosDataService.getCurrentDateFilter();
    
    if (currentFilter && currentFilter.date_from && currentFilter.date_to) {
      // Si hay filtro de fecha, usarlo
      this.annulledPoliciesDateRange.startDate = currentFilter.date_from;
      this.annulledPoliciesDateRange.endDate = currentFilter.date_to;
    } else {
      // Si NO hay filtro de fecha (Todo el tiempo), enviar undefined para mostrar todas
      this.annulledPoliciesDateRange.startDate = undefined;
      this.annulledPoliciesDateRange.endDate = undefined;
    }
    
    this.annulledPoliciesPage = 1; // Reset to first page
    this.loadAnnulledPolicies();
  }

  loadAnnulledPolicies(): void {
    console.log('🔄 loadAnnulledPolicies called');
    console.log('Date range:', this.annulledPoliciesDateRange);
    
    console.log('⏳ Setting loading to true');
    this.isLoadingAnnulledPolicies = true;
    
    // Preparar params - solo incluir fechas si están definidas
    const params: any = {
      page: this.annulledPoliciesPage,
      limit: this.annulledPoliciesLimit
    };
    
    // Solo agregar fechas si existen (no son undefined)
    if (this.annulledPoliciesDateRange.startDate && this.annulledPoliciesDateRange.endDate) {
      params.startDate = this.annulledPoliciesDateRange.startDate;
      params.endDate = this.annulledPoliciesDateRange.endDate;
    }
    
    console.log('🌐 Calling API with params:', params);
    
    this.segurosApiService.getAnnulledPolicies(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('✅ Annulled policies response:', response);
          this.currentAnnulledPoliciesData = response.data || {
            policies: [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              itemsPerPage: this.annulledPoliciesLimit
            }
          };
          this.isLoadingAnnulledPolicies = false;
          console.log('✅ Final data set:', this.currentAnnulledPoliciesData);
          console.log('✅ Loading set to false');
        },
        error: (error: any) => {
          console.error('❌ Error loading annulled policies:', error);
          this.isLoadingAnnulledPolicies = false;
          // Initialize with empty structure on error
          this.currentAnnulledPoliciesData = {
            policies: [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              itemsPerPage: this.annulledPoliciesLimit
            }
          };
          console.log('❌ Error data set:', this.currentAnnulledPoliciesData);
          alert('Error al cargar pólizas anuladas: ' + (error.error?.message || error.message));
        }
      });
  }

  loadAnnulledPoliciesPage(page: number): void {
    this.annulledPoliciesPage = page;
    this.loadAnnulledPolicies();
  }

  applyAnnulledPoliciesDateRange(startDate: string, endDate: string): void {
    if (!startDate || !endDate) {
      alert('Por favor seleccione un rango de fechas válido');
      return;
    }

    this.annulledPoliciesDateRange.startDate = startDate;
    this.annulledPoliciesDateRange.endDate = endDate;
    this.annulledPoliciesPage = 1; // Reset to first page
    this.loadAnnulledPolicies();
  }

  exportAnnulledPoliciesData(): void {
    const totalItems = this.currentAnnulledPoliciesData?.pagination?.totalItems || 0;
    if (totalItems === 0) {
      alert('No hay datos de pólizas anuladas para exportar');
      return;
    }

    const params: any = {};
    if (this.annulledPoliciesDateRange.startDate) params.startDate = this.annulledPoliciesDateRange.startDate;
    if (this.annulledPoliciesDateRange.endDate) params.endDate = this.annulledPoliciesDateRange.endDate;

    this.isExportingAnnulledPolicies = true;
    this.segurosApiService.exportAnnulledPoliciesCsv(params).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isExportingAnnulledPolicies = false;
          this.downloadBlobAsFile(blob, 'polizas_anuladas');
        },
        error: (error: any) => {
          this.isExportingAnnulledPolicies = false;
          alert('Error al exportar pólizas anuladas');
          console.error('Error exportando pólizas anuladas:', error);
        }
      });
  }

  getCurrencySymbol(cdMoneda: string): string {
    if (cdMoneda === '01') return 'Bs';
    if (cdMoneda === '02') return '$';
    return cdMoneda;
  }

  getBankName(bankCode: string): string {
    if (!bankCode) return bankCode;
    const bank = this.banks.find(b => b.bankCode === bankCode);
    return bank ? bank.name : bankCode;
  }

  getOperationType(tpOperacion: string): string {
    if (tpOperacion === 'D') return 'Depósito';
    if (tpOperacion === 'T') return 'Transferencia';
    return tpOperacion;
  }

  get canEditDeposits(): boolean {
    const st = this.scRelationData?.header?.stRelacion;
    return st === 'INCOMPLETA' || st === 'PENDIENTE';
  }

  getTotalDeposits(): number {
    if (!this.scRelationData?.deposits || this.scRelationData.deposits.length === 0) {
      return 0;
    }
    return this.scRelationData.deposits.reduce((sum: number, dep: any) => sum + (dep.mtDeposito || 0), 0);
  }

  isTotalMatching(): boolean {
    // Comparamos depósitos vs dinero recibido en CT ese día (Tabla A).
    // Regla del cliente 2026-07-17: la relación SC se pela por fecha de pago,
    // por lo que Σ Tabla A === lo que se debe depositar a SC ese día.
    const totalDeposits = this.getTotalDeposits();
    const totalPaymentsCT = this.scPaymentsData?.total_ves || 0;
    const difference = Math.abs(totalDeposits - totalPaymentsCT);
    return difference < 0.01;
  }

  openEditDepositModal(deposit: any): void {
    // Formatear fecha usando UTC para evitar offset de timezone
    let formattedDate = '';
    if (deposit.feDeposito) {
      const fecha = new Date(deposit.feDeposito);
      const year = fecha.getUTCFullYear();
      const month = String(fecha.getUTCMonth() + 1).padStart(2, '0');
      const day = String(fecha.getUTCDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    }
    
    this.editingDeposit = {
      _id: deposit._id,
      cdBanco: deposit.cdBanco,
      nuDeposito: deposit.nuDeposito,
      feDeposito: formattedDate,
      mtDeposito: deposit.mtDeposito,
      cdMoneda: deposit.cdMoneda,
      tpOperacion: deposit.tpOperacion
    };
    this.showEditDepositModal = true;
  }

  closeEditDepositModal(): void {
    this.showEditDepositModal = false;
  }

  updateDeposit(): void {
    if (!this.editingDeposit.cdBanco || !this.editingDeposit.nuDeposito || !this.editingDeposit.mtDeposito) {
      alert('Debe completar todos los campos obligatorios');
      return;
    }

    this.isUpdatingDeposit = true;

    const depositData = {
      cdBanco: this.editingDeposit.cdBanco,
      nuDeposito: this.editingDeposit.nuDeposito,
      feDeposito: this.editingDeposit.feDeposito,
      mtDeposito: this.editingDeposit.mtDeposito,
      cdMoneda: this.editingDeposit.cdMoneda,
      tpOperacion: this.editingDeposit.tpOperacion
    };

    this.segurosApiService.updateSCDeposit(this.editingDeposit._id, depositData).subscribe({
      next: (response: any) => {
        console.log('✅ Depósito actualizado:', response);
        alert('Depósito actualizado exitosamente');
        this.closeEditDepositModal();
        this.loadSCRelation();
        this.isUpdatingDeposit = false;
      },
      error: (error: any) => {
        console.error('❌ Error al actualizar depósito:', error);
        alert(error.error?.msg || 'Error al actualizar el depósito');
        this.isUpdatingDeposit = false;
      }
    });
  }

  deleteDeposit(deposit: any): void {
    if (!confirm(`¿Está seguro que desea eliminar este depósito?\n\nBanco: ${this.getBankName(deposit.cdBanco)}\nNúmero: ${deposit.nuDeposito}\nMonto: ${deposit.mtDeposito} ${this.getCurrencySymbol(deposit.cdMoneda)}`)) {
      return;
    }

    this.segurosApiService.deleteSCDeposit(deposit._id).subscribe({
      next: (response: any) => {
        console.log('✅ Depósito eliminado:', response);
        alert('Depósito eliminado exitosamente');
        this.loadSCRelation();
      },
      error: (error: any) => {
        console.error('❌ Error al eliminar depósito:', error);
        alert(error.error?.msg || 'Error al eliminar el depósito');
      }
    });
  }

  reportDepositsToSC(): void {
    if (!this.scRelationData?.header?.nuRelacion) {
      alert('No hay relación cargada');
      return;
    }

    if (!this.scRelationData?.deposits || this.scRelationData.deposits.length === 0) {
      alert('No hay depósitos para reportar');
      return;
    }

    // TEMPORALMENTE COMENTADO PARA PRUEBAS - Validar que el total de depósitos coincida con el total en Bs de la relación
    const totalDeposits = this.getTotalDeposits();
    // const totalRelation = this.scRelationData.header?.total_mtPago_ves || 0;
    // const difference = Math.abs(totalDeposits - totalRelation);

    // if (difference > 0.01) {
    //   alert(`⚠️ El total de depósitos (${totalDeposits.toFixed(2)} Bs) no coincide con el total de la relación (${totalRelation.toFixed(2)} Bs).\n\nDiferencia: ${difference.toFixed(2)} Bs\n\nPor favor, verifique los montos antes de reportar.`);
    //   return;
    // }

    if (confirm(`¿Está seguro que desea reportar ${this.scRelationData.deposits.length} depósito(s) a Seguros Caracas?\n\nTotal: ${totalDeposits.toFixed(2)} Bs`)) {
      this.isReportingDeposits = true;

      this.segurosApiService.reportSCDeposits(this.scRelationData.header.nuRelacion).subscribe({
        next: (response) => {
          console.log('✅ Depósitos reportados:', response);
          this.reportResult = {
            success: true,
            message: response.message,
            data: response.data
          };
          this.showReportResultModal = true;
          this.loadSCRelation();
          this.isReportingDeposits = false;
        },
        error: (error) => {
          console.error('❌ Error al reportar depósitos:', error);
          this.reportResult = {
            success: false,
            message: error.error?.msg || 'Error al reportar depósitos',
            error: error.error
          };
          this.showReportResultModal = true;
          this.isReportingDeposits = false;
        }
      });
    }
  }

  closeReportResultModal(): void {
    this.showReportResultModal = false;
    this.reportResult = null;
    
    // Solo refrescar si el modal se abrió después de un reporte (no cuando solo se consultan detalles)
    if (this.shouldRefreshOnModalClose) {
      this.loadSCRelation();
    }
    
    // Resetear bandera
    this.shouldRefreshOnModalClose = true;
  }

  showReportError(): void {
    if (this.scRelationData?.header?.deposits_sc_response) {
      const errorResponse = this.scRelationData.header.deposits_sc_response;
      // Construir mensaje completo con todos los detalles disponibles
      let errorMessage = errorResponse.msg || errorResponse.message || errorResponse.detail || 'Error al reportar depósitos';
      
      this.reportResult = {
        success: false,
        message: errorMessage,
        error: {
          sc_response: errorResponse
        }
      };
      
      // No refrescar al cerrar este modal porque solo estamos viendo detalles
      this.shouldRefreshOnModalClose = false;
      this.showReportResultModal = true;
    }
  }

  getProductName(productCode: string): string {
    if (!productCode) return 'N/A';
    
    const productMap: { [key: string]: string } = {
      'V': 'Vida',
      'A': 'Auto',
      'H': 'Hogar',
      'S': 'Salud',
      'AP': 'Accidentes Personales',
      'RC': 'Responsabilidad Civil'
    };

    return productMap[productCode] || productCode;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // SUB-TAB CONTACTOS (drill-down de la card "Distribución por Estado")
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Vista que lista personas en un policy_status para que el ejecutivo las
  // contacte (llamar/WhatsApp/email) y retome la venta o gestione cobranza.
  // Se llega desde:
  //   - Click en un bucket de "Distribución por Estado" del dashboard.
  //   - Click en un sub-bucket del breakdown (notification_error, fully_paid_stuck).
  //   - Click en una mini-card de "Vendí · Cobré · Falta cobrar".

  /** Helper de Math.min expuesto al template para la paginación. */
  mathMin(a: number, b: number): number { return Math.min(a, b); }

  /** Estado activo del sub-tab. Lo setea el handler que lo abre. */
  contactsActiveStatus: string | string[] | null = null;
  /** Title del bucket activo (para el header del sub-tab). */
  contactsActiveTitle: string = '';
  /** Filtros adicionales que vienen del breakdown del bucket. */
  contactsFilters: {
    plan_type?: string;
    frequency?: string;
    notification_status?: 'notified_ok' | 'notification_error';
    min_days_inactive?: number | null;
    has_phone?: boolean;
    has_email?: boolean;
    search?: string;
  } = {};
  contactsData: { pagination: any; data: any[] } | null = null;
  contactsLoading = false;
  contactsError: string | null = null;
  contactsPage = 1;
  contactsLimit = 20;

  /** Configuración de pre-poblado al abrir el sub-tab desde un click en card. */
  openContactsForStatus(status: string | string[], opts?: {
    title?: string;
    plan_type?: string;
    frequency?: string;
    notification_status?: 'notified_ok' | 'notification_error';
  }): void {
    this.contactsActiveStatus = status;
    this.contactsActiveTitle = opts?.title || (Array.isArray(status) ? status.join(' / ') : status);
    this.contactsFilters = {
      plan_type: opts?.plan_type,
      frequency: opts?.frequency,
      notification_status: opts?.notification_status,
      min_days_inactive: this.computeDefaultMinDaysInactive(
        status,
        this.selectedInsurerCode || this.selectedDashboardInsurerCode || null
      ),
      has_phone: false,
      has_email: false,
      search: ''
    };
    this.contactsPage = 1;
    this.setActiveTab('contacts');
    this.loadContacts();
  }

  /** Aseguradoras historicas de alto volumen: en CREATED tienen cientos de miles
   *  de registros, por eso el drill-down aplica un default de 30d sin actividad.
   *  Las demas (VE/TuDoc, futuras de bajo volumen) no lo necesitan — sus policies
   *  son recientes y el filtro las excluiria completamente. */
  private readonly HIGH_VOLUME_INSURERS_FOR_CREATED_DEFAULT = ['SM', 'SC'];

  /** Para CREATED forzamos default últimos 30 días sin actividad solo cuando el
   *  scope es SM/SC (alto volumen — 324k+ registros). Sin scope explicito
   *  (vista global) mantenemos el default 30d como fallback conservador. */
  private computeDefaultMinDaysInactive(
    status: string | string[],
    insurerCode?: string | null
  ): number | null {
    const arr = Array.isArray(status) ? status : [status];
    if (!arr.includes('CREATED')) return null;
    if (!insurerCode) return 30;
    return this.HIGH_VOLUME_INSURERS_FOR_CREATED_DEFAULT.includes(insurerCode) ? 30 : null;
  }

  loadContacts(): void {
    if (!this.contactsActiveStatus) return;
    this.contactsLoading = true;
    this.contactsError = null;
    const dateFilter = this.segurosDataService.getCurrentDateFilter();
    const insurer = this.selectedInsurerCode || this.selectedDashboardInsurerCode || undefined;
    const params = {
      policy_status: this.contactsActiveStatus,
      page: this.contactsPage,
      limit: this.contactsLimit,
      date_from: dateFilter?.date_from,
      date_to: dateFilter?.date_to,
      insurer_code: insurer,
      ...this.contactsFilters,
      // omitir vacíos
      ...(this.contactsFilters.search?.trim() ? { search: this.contactsFilters.search.trim() } : {})
    };
    this.segurosApiService.getPoliciesContacts(params).subscribe({
      next: (resp: any) => {
        this.contactsData = resp;
        this.contactsLoading = false;
      },
      error: (err: any) => {
        console.error('❌ Contacts load error:', err);
        this.contactsError = err?.error?.message || 'Error al cargar contactos.';
        this.contactsLoading = false;
      }
    });
  }

  exportContactsCsv(): void {
    if (!this.contactsActiveStatus) return;
    const dateFilter = this.segurosDataService.getCurrentDateFilter();
    const insurer = this.selectedInsurerCode || this.selectedDashboardInsurerCode || undefined;
    const url = this.segurosApiService.getPoliciesContactsExportUrl({
      policy_status: this.contactsActiveStatus,
      date_from: dateFilter?.date_from,
      date_to: dateFilter?.date_to,
      insurer_code: insurer,
      ...this.contactsFilters,
      ...(this.contactsFilters.search?.trim() ? { search: this.contactsFilters.search.trim() } : {})
    });
    window.open(url, '_blank');
  }

  contactsGoToPage(p: number): void {
    if (p < 1 || (this.contactsData && p > this.contactsData.pagination.totalPages)) return;
    this.contactsPage = p;
    this.loadContacts();
  }

  /** Plantillas de WhatsApp por estado. El ejecutivo puede editarlas en el
   *  navegador antes de enviar (WhatsApp Web/App pone el texto en el campo
   *  pero el usuario presiona enviar). */
  private readonly WHATSAPP_TEMPLATES: Record<string, (name: string) => string> = {
    CREATED: (n) => `Hola ${n}, vi que iniciaste la cotización de tu seguro y quedó pendiente. ¿Te ayudo a continuar?`,
    PLAN_SELECTED: (n) => `Hola ${n}, ya elegiste tu plan de seguro. Faltan pocos pasos — ¿retomamos juntos?`,
    DATA_CLIENT_CONFIRMED: (n) => `Hola ${n}, ya confirmaste tus datos. Solo falta cargar el carnet de circulación para emitir tu póliza.`,
    LICENSE_UPLOADED: (n) => `Hola ${n}, ya recibimos tu carnet. Estás muy cerca de tener tu seguro activo — ¿continuamos?`,
    VERIFIED: (n) => `Hola ${n}, verificamos los datos de tu vehículo. ¿Confirmamos la emisión de la póliza?`,
    // Refactor Cotiza→Paga→Emite (Plan 2 SC/RCV): la orden ya está creada, solo falta el pago.
    INVOICE_CREATED: (n) => `Hola ${n}, ya generamos tu orden de compra. Con el pago activamos tu póliza — ¿te ayudo a completarlo?`,
    ISSUED: (n) => `Hola ${n}, tu seguro está emitido 🎉 Para activarlo solo falta tu pago inicial. ¿Cómo te ayudo?`,
    PAYMENT_RECORDED: (n) => `Hola ${n}, gracias por tu pago inicial. Tu próxima cuota está cerca — ¿la coordinamos?`,
    // Refactor Cotiza→Paga→Emite (Plan 3 SC/RCV): emitida tras el pago; para CONTADO es el paso previo a COMPLETED.
    ISSUED_PAID: (n) => `Hola ${n}, tu póliza ya está emitida y con el pago registrado 🎉 En breve queda activa.`,
    COMPLETED: (n) => `Hola ${n}, ya tienes tu seguro activo y al día. Si necesitas algo, escríbenos por aquí.`,
    ANNULLED: (n) => `Hola ${n}, vimos que tu póliza quedó anulada. ¿Te ayudamos a reactivarla o cotizar de nuevo?`
  };

  /** Genera URL de WhatsApp con mensaje pre-redactado contextual al estado. */
  buildWhatsAppLink(insured: any, policyStatus: string): string {
    const phone = (insured?.phone || '').replace(/\D/g, '');
    const fname = (insured?.name || insured?.full_name || '').split(' ')[0] || 'cliente';
    const tplFn = this.WHATSAPP_TEMPLATES[policyStatus] || ((n: string) => `Hola ${n}, te contacto del equipo de seguros de CrediTotal.`);
    return `https://wa.me/${phone}?text=${encodeURIComponent(tplFn(fname))}`;
  }

  /** Asunto/cuerpo de email simple por estado. */
  buildMailtoLink(insured: any, policyStatus: string): string {
    if (!insured?.email) return '';
    const fname = (insured?.name || insured?.full_name || '').split(' ')[0] || 'cliente';
    const subject = encodeURIComponent('CrediTotal Seguros — seguimiento');
    const body = encodeURIComponent((this.WHATSAPP_TEMPLATES[policyStatus] || ((n: string) => `Hola ${n},`))(fname));
    return `mailto:${insured.email}?subject=${subject}&body=${body}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Trazabilidad TuDoc — sección exclusiva del modal cuando insurer.code=VE
  // ─────────────────────────────────────────────────────────────────────────

  /** True cuando la póliza es del aliado Venemergencia/TuDoc.
   *  La sección "Trazabilidad TuDoc" del modal se muestra solo en este caso. */
  isTudocPolicy(policy: any): boolean {
    // Plan 35: con TuDoc apagado la sección del modal no se dibuja nunca.
    if (!this.tudocUiEnabled) return false;
    const code = policy?.insurance?.code || policy?.insurer_code;
    return code === TUDOC_INSURER_CODE;
  }

  /** Devuelve el flow_history ordenado cronológicamente ascendente (evento más
   *  antiguo primero). Defensivo ante datos ausentes/malformados. */
  getTudocFlowHistory(policy: any): Array<{ step: string; timestamp: any; actor: any; metadata: any; meta: TudocFlowStepMetadata }> {
    const raw = Array.isArray(policy?.flow_history) ? policy.flow_history : [];
    return [...raw]
      .filter(e => e && e.step)
      .sort((a, b) => {
        const ta = new Date(a.timestamp || 0).getTime();
        const tb = new Date(b.timestamp || 0).getTime();
        return ta - tb;
      })
      .map(e => ({
        step: e.step,
        timestamp: e.timestamp,
        actor: e.actor || {},
        metadata: e.metadata || {},
        meta: getTudocFlowStepMetadata(e.step)
      }));
  }

  /** Clase Bootstrap para el badge del tono del step. */
  getTudocStepToneClass(tone: string): string {
    switch (tone) {
      case 'success': return 'bg-success text-white';
      case 'warning': return 'bg-warning text-dark';
      case 'danger':  return 'bg-danger text-white';
      case 'info':
      default:        return 'bg-info text-white';
    }
  }

  /** Etiqueta corta del actor para el timeline. */
  getTudocActorLabel(actor: any): string {
    const type = actor?.type || 'BACKEND';
    switch (type) {
      case 'CLIENT':  return actor?.channel ? `Cliente · ${actor.channel}` : 'Cliente';
      case 'HOOK':    return 'Hook post-pago';
      case 'CRON':    return 'Cron';
      case 'ADMIN':   return actor?.user_code ? `Admin · ${actor.user_code}` : 'Admin';
      case 'BACKEND':
      default:        return 'Backend';
    }
  }
}